import React, { startTransition, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  AlertIcon,
  ExpandIcon,
  FavoriteIcon,
  JourneyPlannerIcon,
  LocationIcon,
  MapAreaIcon,
  MapIconButton,
  MapToolsIcon,
  RefreshIcon,
  ResetViewIcon,
  RoutePathIcon,
  SearchIcon,
  StatBlock,
} from './components/ui';
import {
  ADMIN_TOKEN_STORAGE_KEY,
  ALL_ROUTE_PATHS_GLOW_LAYER_ID,
  ALL_ROUTE_PATHS_LINE_LAYER_ID,
  ALL_ROUTE_PATHS_SOURCE_ID,
  APP_RELEASE_ID,
  API_BASE_URL,
  BROWSER_CACHE_DB_NAME,
  BROWSER_CACHE_STORE_NAME,
  BROWSER_CACHE_VERSION,
  DEFAULT_CENTER,
  DEFAULT_MAP_STYLE_OPTIONS,
  DEFAULT_PUBLIC_APP_CONFIG,
  DEFAULT_ROUTE_COLOR,
  DEFAULT_ROUTE_COLORS,
  DEFAULT_ZOOM,
  EMPTY_FEATURE_COLLECTION,
  HOST_PUBLIC_APP_CONFIGS,
  JOURNEY_SOURCE_ID,
  JOURNEY_TRANSIT_LAYER_ID,
  JOURNEY_WALK_LAYER_ID,
  MAPBOX_TOKEN,
  MAP_STYLE,
  POPUP_OFFSET,
  REFRESH_INTERVAL_MS,
  ROUTE_CATEGORY_OPTIONS,
  ROUTE_CONTEXT_CACHE_KEY_PREFIX,
  ROUTE_CONTEXT_GLOW_LAYER_ID,
  ROUTE_CONTEXT_LINE_LAYER_ID,
  ROUTE_CONTEXT_SOURCE_ID,
  ROUTE_PATHS_CACHE_KEY,
  STATIC_GTFS_CACHE_TTL_MS,
  TPL_STOPS_DOT_LAYER_ID,
  TPL_STOPS_HALO_LAYER_ID,
  TPL_STOPS_HIT_LAYER_ID,
  TPL_STOPS_ICON_LAYER_ID,
  TPL_STOPS_SOURCE_ID,
  TPL_STOPS_TRAIN_ICON_LAYER_ID,
  TRANSIT_CATALOG_CACHE_KEY,
} from './config/publicAppConfig';
import { getViewportHeight, isAppleMobileBrowser, isSafariBrowser } from './utils/browser';
import { inferMovement, measureDistanceMeters, toKmPerHour } from './utils/geo';
import { detectBrowserLanguage, getLocale, normalizeLanguage, translate } from './i18n';
import {
  formatArrivalCountdown,
  formatDelay,
  formatDistanceMeters,
  formatDurationMinutes,
  formatDurationSeconds,
  formatPredictedTime,
  formatTime,
  formatTimestamp,
  getPassageArrivalTime,
  setTimeFormatLanguage,
} from './utils/timeFormat';
import './App.css';

mapboxgl.accessToken = MAPBOX_TOKEN;

const JOURNEY_TRANSIT_CASING_LAYER_ID = 'journey-transit-casing-layer';
const JOURNEY_STOPS_HALO_LAYER_ID = 'journey-stops-halo-layer';
const JOURNEY_STOPS_DOT_LAYER_ID = 'journey-stops-dot-layer';
const JOURNEY_STOPS_LABEL_LAYER_ID = 'journey-stops-label-layer';
const JOURNEY_FOCUS_LAYER_ID = 'journey-focus-layer';
const JOURNEY_REFRESH_INTERVAL_MS = 60000;
const JOURNEY_PATH_REQUEST_TIMEOUT_MS = 18000;
const JOURNEY_NAVIGATION_REALTIME_INTERVAL_MS = 20000;
const JOURNEY_NAVIGATION_STOP_RADIUS_METERS = 120;
const JOURNEY_NAVIGATION_NEAR_STOP_RADIUS_METERS = 180;
const JOURNEY_LIVE_MATCH_WINDOW_SECONDS = 12 * 60;
const JOURNEY_TIME_DEPART_NOW = 'departNow';
const JOURNEY_TIME_DEPART_AT = 'departAt';
const JOURNEY_TIME_ARRIVE_BY = 'arriveBy';
const JOURNEY_TIME_LAST = 'last';
const JOURNEY_LOADING_STEPS = [
  'journeyLoadingNearbyStops',
  'journeyLoadingTrips',
  'journeyLoadingTransfers',
];

let browserCacheDbPromise = null;
const walkingDirectionsCache = new Map();
let activeBrowserCachePrefix = 'bus-tracker';
let activeRouteColorConfig = DEFAULT_PUBLIC_APP_CONFIG.routeColors;
let activeConfiguredRouteColorKeys = new Set(Object.keys(DEFAULT_PUBLIC_APP_CONFIG.routeColors.routes || {}));
let activeRouteCategoryConfig = DEFAULT_PUBLIC_APP_CONFIG.routeCategories;
let activeCacheConfig = DEFAULT_PUBLIC_APP_CONFIG.cache;
let activeLanguage = getInitialLanguage();
setTimeFormatLanguage(activeLanguage);

const TPL_STOPS_INTERACTIVE_LAYER_IDS = [
  TPL_STOPS_TRAIN_ICON_LAYER_ID,
  TPL_STOPS_ICON_LAYER_ID,
  TPL_STOPS_HIT_LAYER_ID,
  TPL_STOPS_DOT_LAYER_ID,
];
const TPL_STOP_BUS_ICON_ID = 'tpl-stop-bus-icon';
const TPL_STOP_TRAIN_ICON_ID = 'tpl-stop-train-icon';
const SERVICE_AREA_DEFAULT_ID = '__all__';
const SERVICE_AREA_STORAGE_KEY_PREFIX = 'tpl-tracker-service-area';
const MAP_STYLE_STORAGE_KEY_PREFIX = 'tpl-tracker-map-style';
const LANGUAGE_STORAGE_KEY = 'tpl-tracker-language';
const JOURNEY_RECENT_STORAGE_KEY = 'tpl-tracker-recent-journeys';
const JOURNEY_RECENT_LIMIT = 4;
const FAVORITE_JOURNEYS_STORAGE_KEY = 'tpl-tracker-favorite-journeys';
const FAVORITE_JOURNEYS_LIMIT = 8;
const FAVORITE_ROUTES_STORAGE_KEY = 'tpl-tracker-favorite-routes';
const FAVORITE_STOPS_STORAGE_KEY = 'tpl-tracker-favorite-stops';
const FAVORITE_STOPS_LIMIT = 12;
const FAVORITE_PLACES_STORAGE_KEY = 'tpl-tracker-favorite-places';
const FAVORITE_PLACES_LIMIT = 8;
const MAP_HINT_AUTO_HIDE_MS = 10000;
const MAP_POPOVER_AUTO_HIDE_MS = 10000;
const USER_LOCATION_FINALIZE_CAMERA_MS = 950;
const USER_LOCATION_RESTORE_BOUNDS_MS = 1500;
const USER_LOCATION_CAMERA_LOCK_MS = 1900;
const STOP_PAGE_SCHEDULE_LIMIT = 240;
const STOP_PAGE_REALTIME_LIMIT = 80;
const ENABLE_NIGHT_ROUTE_CONVERSION = false;
const STOP_PASSAGE_FUZZY_DEDUPLICATION_SECONDS = 600;
const MAP_RETURN_REFIT_DELAY_MS = 80;

function t(key, values) {
  return translate(activeLanguage, key, values);
}

function setActiveLanguage(language) {
  activeLanguage = language;
  setTimeFormatLanguage(language);
}

function readStoredLanguage() {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY)) || '';
  } catch {
    return '';
  }
}

function writeStoredLanguage(language) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Ignore storage failures: language selection still works for the current session.
  }
}

function getInitialLanguage() {
  return readStoredLanguage() || detectBrowserLanguage();
}

function sortByRouteAndLabel(firstBus, secondBus) {
  const routeComparison = firstBus.routeId.localeCompare(secondBus.routeId, undefined, {
    numeric: true,
  });

  if (routeComparison !== 0) {
    return routeComparison;
  }

  return firstBus.label.localeCompare(secondBus.label, undefined, { numeric: true });
}

function sortRouteLabels(routeLabels = []) {
  return [...new Set(routeLabels.map((routeLabel) => String(routeLabel || '').trim()).filter(Boolean))].sort(
    (firstRouteLabel, secondRouteLabel) => {
      const firstNumber = Number.parseInt(firstRouteLabel.match(/\d+/)?.[0] || '', 10);
      const secondNumber = Number.parseInt(secondRouteLabel.match(/\d+/)?.[0] || '', 10);
      const hasFirstNumber = Number.isFinite(firstNumber);
      const hasSecondNumber = Number.isFinite(secondNumber);

      if (hasFirstNumber && hasSecondNumber && firstNumber !== secondNumber) {
        return firstNumber - secondNumber;
      }

      if (hasFirstNumber !== hasSecondNumber) {
        return hasFirstNumber ? -1 : 1;
      }

      return firstRouteLabel.localeCompare(secondRouteLabel, undefined, { numeric: true });
    }
  );
}

function getNightRouteBaseId(routeIdentifier) {
  if (!ENABLE_NIGHT_ROUTE_CONVERSION) {
    return '';
  }

  const routeName = String(routeIdentifier || '').trim().toUpperCase();
  const configuredPattern = activeRouteColorConfig?.nightRoute?.sourcePattern;
  const configuredBasePattern = activeRouteColorConfig?.nightRoute?.basePattern || '$1';

  if (configuredPattern) {
    try {
      const configuredRegex = new RegExp(configuredPattern);

      if (configuredRegex.test(routeName)) {
        return routeName.replace(configuredRegex, configuredBasePattern);
      }
    } catch {
      // Ignore invalid external regex and keep the built-in TEP-compatible fallback.
    }
  }

  if (/^\d+N$/.test(routeName)) {
    const baseRouteId = String(Number(routeName.slice(0, -1)));
    return baseRouteId === '0' ? '' : baseRouteId;
  }

  if (/^1\d{2}$/.test(routeName)) {
    const baseRouteId = String(Number(routeName.slice(1)));
    return baseRouteId === '0' ? '' : baseRouteId;
  }

  return '';
}

function formatRouteShortName(routeIdentifier) {
  const rawRouteName = String(routeIdentifier || '').trim();

  if (!ENABLE_NIGHT_ROUTE_CONVERSION) {
    return rawRouteName;
  }

  const configuredPattern = activeRouteColorConfig?.nightRoute?.sourcePattern;
  const configuredDisplayPattern = activeRouteColorConfig?.nightRoute?.displayPattern || '$1N';

  if (configuredPattern) {
    try {
      const configuredRegex = new RegExp(configuredPattern, 'i');

      if (configuredRegex.test(rawRouteName)) {
        return rawRouteName.replace(configuredRegex, configuredDisplayPattern);
      }
    } catch {
      // Ignore invalid external regex and keep the built-in TEP-compatible fallback.
    }
  }

  const nightRouteBaseId = getNightRouteBaseId(rawRouteName);
  return nightRouteBaseId ? `${nightRouteBaseId}N` : rawRouteName;
}

function formatRouteLongName(route, fallbackRouteId = '') {
  const rawRouteId = typeof route === 'object' ? route?.id || fallbackRouteId : route || fallbackRouteId;
  const rawShortName = typeof route === 'object' ? route?.shortName || rawRouteId : rawRouteId;
  const rawLongName = typeof route === 'object' ? route?.longName || '' : '';
  const nightRouteBaseId = getNightRouteBaseId(rawShortName) || getNightRouteBaseId(rawRouteId);

  if (nightRouteBaseId) {
    return t('nightRoute', { route: nightRouteBaseId });
  }

  return rawLongName || `${formatRouteShortName(rawShortName)}`;
}

function getRouteColor(routeId, routeShortName = '') {
  const routeColors = activeRouteColorConfig?.routes || DEFAULT_ROUTE_COLORS;
  const candidates = getRouteLookupCandidates(routeId, routeShortName);

  const configuredRouteKey = candidates.find(
    (routeKey) => activeConfiguredRouteColorKeys.has(routeKey) && routeColors[routeKey]
  );
  const fallbackRouteKey = candidates.find((routeKey) => routeColors[routeKey]);
  return routeColors[configuredRouteKey || fallbackRouteKey] ?? activeRouteColorConfig?.default ?? DEFAULT_ROUTE_COLOR;
}

function getRouteLookupCandidates(routeId, routeShortName = '') {
  return [
    routeShortName,
    formatRouteShortName(routeShortName || routeId),
    getNightRouteBaseId(routeShortName || routeId),
    routeId,
  ]
    .flatMap((value) => {
      const routeKey = String(value || '').trim();
      if (!routeKey) {
        return [];
      }

      const withoutAgencySuffix = routeKey.replace(/([A-Z0-9 +/]+?)(?:U|E)$/i, '$1').trim();
      return [routeKey, withoutAgencySuffix, formatRouteShortName(withoutAgencySuffix)];
    })
    .filter(Boolean);
}

function normalizeRouteColor(value) {
  const sanitizedValue = String(value || '').replace('#', '').trim();
  return /^[0-9a-fA-F]{6}$/.test(sanitizedValue) ? `#${sanitizedValue}` : '';
}

function registerGtfsRouteColor(routeId, routeShortName, routeColor) {
  if (!activeRouteColorConfig?.useGtfsColors) {
    return;
  }

  const color = normalizeRouteColor(routeColor);

  if (!color) {
    return;
  }

  const routeKeys = [routeId, routeShortName, formatRouteShortName(routeShortName || routeId)]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  const nextRoutes = { ...(activeRouteColorConfig.routes || {}) };
  let hasNewColor = false;

  routeKeys.forEach((routeKey) => {
    if (!nextRoutes[routeKey]) {
      nextRoutes[routeKey] = color;
      hasNewColor = true;
    }
  });

  if (hasNewColor) {
    activeRouteColorConfig = {
      ...activeRouteColorConfig,
      routes: nextRoutes,
    };
  }
}

function getRouteCategory(routeIdentifier, routeShortName = '') {
  const configuredCategories = activeRouteCategoryConfig?.routes || {};
  const configuredRouteCategory = getRouteLookupCandidates(routeIdentifier, routeShortName).find(
    (routeKey) => configuredCategories[routeKey]
  );

  if (configuredRouteCategory) {
    return configuredCategories[configuredRouteCategory];
  }

  const routeLabel = formatRouteShortName(routeShortName || routeIdentifier);
  const nightRouteBaseId = getNightRouteBaseId(routeLabel || routeShortName || routeIdentifier);

  if (nightRouteBaseId) {
    return 'night';
  }

  const numericRouteLabel = Number.parseInt(String(routeLabel || routeIdentifier).match(/\d+/)?.[0] || '', 10);

  if (Number.isFinite(numericRouteLabel) && numericRouteLabel >= 1000) {
    return 'interurban';
  }

  if (Number.isFinite(numericRouteLabel)) {
    return 'urban';
  }

  return (activeRouteColorConfig?.routes || DEFAULT_ROUTE_COLORS)[routeLabel] ? 'urban' : 'interurban';
}

function getRouteBadgeStyle(routeIdentifier) {
  const routeColor = getRouteColor(routeIdentifier);
  return {
    '--route-color': routeColor,
    '--route-text-color': getReadableTextColor(routeColor),
  };
}

function buildRouteOptions(catalogRoutes, buses) {
  const routesById = new Map(
    catalogRoutes
      .filter((route) => route?.id)
      .map((route) => [String(route.id), route])
  );

  buses.forEach((bus) => {
    if (!bus.routeId || routesById.has(bus.routeId)) {
      return;
    }

    routesById.set(bus.routeId, {
      id: bus.routeId,
      shortName: bus.routeShortName || bus.routeId,
      longName: bus.routeLongName || '',
    });
  });

  const liveCountsByRouteId = buses.reduce((counts, bus) => {
    if (bus.routeId) {
      counts.set(bus.routeId, (counts.get(bus.routeId) || 0) + 1);
    }

    return counts;
  }, new Map());

  const groupedRoutes = new Map();

  routesById.forEach((route) => {
    const routeId = String(route.id || '').trim();
    const shortName = formatRouteShortName(route.shortName || routeId);
    const groupKey = shortName || routeId;

    if (!routeId || !groupKey) {
      return;
    }

    const currentGroup = groupedRoutes.get(groupKey) || {
      id: routeId,
      key: groupKey,
      routeIds: [],
      shortName,
      longName: formatRouteLongName(route, routeId),
      category: getRouteCategory(routeId, shortName),
      liveCount: 0,
    };

    if (!currentGroup.routeIds.includes(routeId)) {
      currentGroup.routeIds.push(routeId);
    }

    currentGroup.liveCount += liveCountsByRouteId.get(routeId) || 0;

    if (!currentGroup.longName && route.longName) {
      currentGroup.longName = route.longName;
    }

    if (currentGroup.category === 'interurban') {
      const nextCategory = getRouteCategory(routeId, shortName);
      currentGroup.category = nextCategory;
    }

    groupedRoutes.set(groupKey, currentGroup);
  });

  return [...groupedRoutes.values()]
    .map((routeGroup) => ({
      ...routeGroup,
      routeIds: routeGroup.routeIds.sort((firstRouteId, secondRouteId) =>
        firstRouteId.localeCompare(secondRouteId, 'it', { numeric: true })
      ),
    }))
    .sort((firstRoute, secondRoute) =>
      firstRoute.shortName.localeCompare(secondRoute.shortName, 'it', { numeric: true })
    );
}

function getRouteOptionByRouteId(routeOptions, routeId) {
  if (!routeId) {
    return null;
  }

  return (
    routeOptions.find((routeOption) => routeOption.id === routeId || routeOption.routeIds.includes(routeId)) ??
    null
  );
}

function getRouteOptionByRouteLabel(routeOptions, routeLabel) {
  const normalizedRouteLabel = formatRouteShortName(routeLabel);

  if (!normalizedRouteLabel) {
    return null;
  }

  return (
    routeOptions.find(
      (routeOption) =>
        routeOption.shortName === normalizedRouteLabel ||
        routeOption.id === normalizedRouteLabel ||
        routeOption.routeIds.includes(normalizedRouteLabel)
    ) ?? null
  );
}

function applyRouteBadgeStyle(element, routeIdentifier) {
  const routeColor = getRouteColor(routeIdentifier);
  element.style.setProperty('--route-color', routeColor);
  element.style.setProperty('--route-text-color', getReadableTextColor(routeColor));
}

function getReadableTextColor(backgroundColor) {
  const sanitizedHex = backgroundColor.replace('#', '');
  const red = Number.parseInt(sanitizedHex.slice(0, 2), 16);
  const green = Number.parseInt(sanitizedHex.slice(2, 4), 16);
  const blue = Number.parseInt(sanitizedHex.slice(4, 6), 16);
  const luminance = (red * 299 + green * 587 + blue * 114) / 1000;
  return luminance > 150 ? '#18212b' : '#fffefb';
}

function ensureMapOverlayLayers(mapInstance) {
  ensureStopBusIcon(mapInstance);
  ensureStopTrainIcon(mapInstance);

  if (!mapInstance.getSource(ALL_ROUTE_PATHS_SOURCE_ID)) {
    mapInstance.addSource(ALL_ROUTE_PATHS_SOURCE_ID, {
      type: 'geojson',
      data: EMPTY_FEATURE_COLLECTION,
    });
  }

  if (!mapInstance.getLayer(ALL_ROUTE_PATHS_GLOW_LAYER_ID)) {
    mapInstance.addLayer({
      id: ALL_ROUTE_PATHS_GLOW_LAYER_ID,
      type: 'line',
      source: ALL_ROUTE_PATHS_SOURCE_ID,
      paint: {
        'line-color': ['coalesce', ['get', 'color'], '#0d2bba'],
        'line-width': ['match', ['get', 'routeCategory'], 'night', 8, 'urban', 7, 5],
        'line-opacity': ['match', ['get', 'routeCategory'], 'night', 0.32, 'urban', 0.28, 0.18],
      },
    });
  }

  if (!mapInstance.getLayer(ALL_ROUTE_PATHS_LINE_LAYER_ID)) {
    mapInstance.addLayer({
      id: ALL_ROUTE_PATHS_LINE_LAYER_ID,
      type: 'line',
      source: ALL_ROUTE_PATHS_SOURCE_ID,
      paint: {
        'line-color': ['coalesce', ['get', 'color'], '#0d2bba'],
        'line-width': ['match', ['get', 'routeCategory'], 'night', 4.4, 'urban', 3.8, 2.5],
        'line-opacity': ['match', ['get', 'routeCategory'], 'night', 0.86, 'urban', 0.78, 0.5],
      },
    });
  }

  if (!mapInstance.getSource(ROUTE_CONTEXT_SOURCE_ID)) {
    mapInstance.addSource(ROUTE_CONTEXT_SOURCE_ID, {
      type: 'geojson',
      data: EMPTY_FEATURE_COLLECTION,
    });
  }

  if (!mapInstance.getLayer(ROUTE_CONTEXT_GLOW_LAYER_ID)) {
    mapInstance.addLayer({
      id: ROUTE_CONTEXT_GLOW_LAYER_ID,
      type: 'line',
      source: ROUTE_CONTEXT_SOURCE_ID,
      paint: {
        'line-color': 'rgba(24, 33, 43, 0.18)',
        'line-width': 12,
        'line-blur': 1.2,
        'line-opacity': 0.95,
      },
    });
  }

  if (!mapInstance.getLayer(ROUTE_CONTEXT_LINE_LAYER_ID)) {
    mapInstance.addLayer({
      id: ROUTE_CONTEXT_LINE_LAYER_ID,
      type: 'line',
      source: ROUTE_CONTEXT_SOURCE_ID,
      paint: {
        'line-color': '#0d2bba',
        'line-width': 5,
        'line-opacity': 0.92,
      },
    });
  }

  if (!mapInstance.getSource(TPL_STOPS_SOURCE_ID)) {
    mapInstance.addSource(TPL_STOPS_SOURCE_ID, {
      type: 'geojson',
      data: EMPTY_FEATURE_COLLECTION,
    });
  }

  if (!mapInstance.getLayer(TPL_STOPS_HIT_LAYER_ID)) {
    mapInstance.addLayer({
      id: TPL_STOPS_HIT_LAYER_ID,
      type: 'circle',
      source: TPL_STOPS_SOURCE_ID,
      minzoom: 13.25,
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          9,
          14,
          20,
          17,
          28,
        ],
        'circle-color': '#ffffff',
        'circle-opacity': 0.01,
      },
    });
  }

  if (!mapInstance.getLayer(TPL_STOPS_HALO_LAYER_ID)) {
    mapInstance.addLayer({
      id: TPL_STOPS_HALO_LAYER_ID,
      type: 'circle',
      source: TPL_STOPS_SOURCE_ID,
      minzoom: 13.25,
      paint: {
        'circle-color': '#ffffff',
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          ['case', ['==', ['get', 'isSelected'], true], 5.8, ['==', ['get', 'isRouteStop'], true], 5.2, 4.2],
          15,
          ['case', ['==', ['get', 'isSelected'], true], 12.4, ['==', ['get', 'isRouteStop'], true], 11.2, 9.8],
          17,
          ['case', ['==', ['get', 'isSelected'], true], 15, ['==', ['get', 'isRouteStop'], true], 13.4, 11.6],
        ],
        'circle-opacity': ['case', ['==', ['get', 'isDimmed'], true], 0.42, 0.9],
        'circle-stroke-color': 'rgba(15, 23, 42, 0.14)',
        'circle-stroke-width': 1,
      },
    });
  }

  if (!mapInstance.getLayer(TPL_STOPS_DOT_LAYER_ID)) {
    mapInstance.addLayer({
      id: TPL_STOPS_DOT_LAYER_ID,
      type: 'circle',
      source: TPL_STOPS_SOURCE_ID,
      minzoom: 13.25,
      paint: {
        'circle-color': ['case', ['==', ['get', 'isRouteStop'], true], '#0d2bba', '#ffffff'],
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          ['case', ['==', ['get', 'isSelected'], true], 4.2, ['==', ['get', 'isRouteStop'], true], 3.7, 2.7],
          15,
          ['case', ['==', ['get', 'isSelected'], true], 10.2, ['==', ['get', 'isRouteStop'], true], 9.2, 8],
          17,
          ['case', ['==', ['get', 'isSelected'], true], 12.8, ['==', ['get', 'isRouteStop'], true], 11.4, 9.8],
        ],
        'circle-opacity': ['case', ['==', ['get', 'isDimmed'], true], 0.2, 0.92],
        'circle-stroke-color': ['case', ['==', ['get', 'isSelected'], true], '#18212b', '#0d2bba'],
        'circle-stroke-width': ['case', ['==', ['get', 'isSelected'], true], 1.8, 1.25],
      },
    });
  }

  if (!mapInstance.getLayer(TPL_STOPS_ICON_LAYER_ID)) {
    mapInstance.addLayer({
      id: TPL_STOPS_ICON_LAYER_ID,
      type: 'symbol',
      source: TPL_STOPS_SOURCE_ID,
      minzoom: 13.75,
      filter: ['!=', ['get', 'hasTrainRoutes'], true],
      layout: {
        'icon-image': TPL_STOP_BUS_ICON_ID,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          ['case', ['==', ['get', 'isSelected'], true], 0.4, ['==', ['get', 'isRouteStop'], true], 0.34, 0.3],
          15,
          ['case', ['==', ['get', 'isSelected'], true], 0.57, ['==', ['get', 'isRouteStop'], true], 0.5, 0.44],
          17,
          ['case', ['==', ['get', 'isSelected'], true], 0.7, ['==', ['get', 'isRouteStop'], true], 0.62, 0.55],
        ],
      },
      paint: {
        'icon-opacity': ['case', ['==', ['get', 'isDimmed'], true], 0.42, 1],
      },
    });
  }

  if (!mapInstance.getLayer(TPL_STOPS_TRAIN_ICON_LAYER_ID)) {
    mapInstance.addLayer({
      id: TPL_STOPS_TRAIN_ICON_LAYER_ID,
      type: 'symbol',
      source: TPL_STOPS_SOURCE_ID,
      minzoom: 13.75,
      filter: ['==', ['get', 'hasTrainRoutes'], true],
      layout: {
        'icon-image': TPL_STOP_TRAIN_ICON_ID,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          ['case', ['==', ['get', 'isSelected'], true], 0.44, ['==', ['get', 'isRouteStop'], true], 0.38, 0.34],
          15,
          ['case', ['==', ['get', 'isSelected'], true], 0.62, ['==', ['get', 'isRouteStop'], true], 0.54, 0.49],
          17,
          ['case', ['==', ['get', 'isSelected'], true], 0.76, ['==', ['get', 'isRouteStop'], true], 0.67, 0.6],
        ],
      },
      paint: {
        'icon-opacity': ['case', ['==', ['get', 'isDimmed'], true], 0.48, 1],
      },
    });
  }

  if (!mapInstance.getSource(JOURNEY_SOURCE_ID)) {
    mapInstance.addSource(JOURNEY_SOURCE_ID, {
      type: 'geojson',
      data: EMPTY_FEATURE_COLLECTION,
    });
  }

  if (!mapInstance.getLayer(JOURNEY_FOCUS_LAYER_ID)) {
    mapInstance.addLayer({
      id: JOURNEY_FOCUS_LAYER_ID,
      type: 'fill',
      source: JOURNEY_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        'fill-color': '#f8fafc',
        'fill-opacity': 0.48,
      },
    });
  }

  if (!mapInstance.getLayer(JOURNEY_WALK_LAYER_ID)) {
    mapInstance.addLayer({
      id: JOURNEY_WALK_LAYER_ID,
      type: 'line',
      source: JOURNEY_SOURCE_ID,
      filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', 'mode'], 'WALK']],
      paint: {
        'line-color': ['coalesce', ['get', 'color'], '#18212b'],
        'line-width': 5.6,
        'line-opacity': 0.9,
        'line-dasharray': [1.4, 1.8],
      },
    });
  }

  if (!mapInstance.getLayer(JOURNEY_TRANSIT_CASING_LAYER_ID)) {
    mapInstance.addLayer({
      id: JOURNEY_TRANSIT_CASING_LAYER_ID,
      type: 'line',
      source: JOURNEY_SOURCE_ID,
      filter: ['all', ['==', ['geometry-type'], 'LineString'], ['!=', ['get', 'mode'], 'WALK']],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#ffffff',
        'line-width': 13,
        'line-opacity': 0.98,
      },
    });
  }

  if (!mapInstance.getLayer(JOURNEY_TRANSIT_LAYER_ID)) {
    mapInstance.addLayer({
      id: JOURNEY_TRANSIT_LAYER_ID,
      type: 'line',
      source: JOURNEY_SOURCE_ID,
      filter: ['all', ['==', ['geometry-type'], 'LineString'], ['!=', ['get', 'mode'], 'WALK']],
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': ['coalesce', ['get', 'color'], '#0d2bba'],
        'line-width': 8,
        'line-opacity': 1,
      },
    });
  }

  if (!mapInstance.getLayer(JOURNEY_STOPS_HALO_LAYER_ID)) {
    mapInstance.addLayer({
      id: JOURNEY_STOPS_HALO_LAYER_ID,
      type: 'circle',
      source: JOURNEY_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-color': '#ffffff',
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          9,
          ['case', ['==', ['get', 'isEndpoint'], true], 6, 4.6],
          13,
          ['case', ['==', ['get', 'isEndpoint'], true], 7.6, 6],
          16,
          ['case', ['==', ['get', 'isEndpoint'], true], 10, 8.4],
        ],
        'circle-stroke-color': 'rgba(15, 23, 42, 0.18)',
        'circle-stroke-width': 1,
      },
    });
  }

  if (!mapInstance.getLayer(JOURNEY_STOPS_DOT_LAYER_ID)) {
    mapInstance.addLayer({
      id: JOURNEY_STOPS_DOT_LAYER_ID,
      type: 'circle',
      source: JOURNEY_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-color': ['coalesce', ['get', 'color'], '#0d2bba'],
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          9,
          ['case', ['==', ['get', 'isEndpoint'], true], 4.1, 2.8],
          13,
          ['case', ['==', ['get', 'isEndpoint'], true], 5.4, 4],
          16,
          ['case', ['==', ['get', 'isEndpoint'], true], 7.2, 5.8],
        ],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.2,
      },
    });
  }

  if (!mapInstance.getLayer(JOURNEY_STOPS_LABEL_LAYER_ID)) {
    mapInstance.addLayer({
      id: JOURNEY_STOPS_LABEL_LAYER_ID,
      type: 'symbol',
      source: JOURNEY_SOURCE_ID,
      minzoom: 14,
      filter: ['all', ['==', ['geometry-type'], 'Point'], ['!', ['get', 'isEndpoint']]],
      layout: {
        'text-field': ['to-string', ['get', 'stopNumber']],
        'text-size': 10,
        'text-offset': [0, -1.35],
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#18212b',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.5,
      },
    });
  }
}

function ensureStopBusIcon(mapInstance) {
  if (!mapInstance?.addImage || mapInstance.hasImage?.(TPL_STOP_BUS_ICON_ID)) {
    return;
  }

  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');

  if (!context) {
    return;
  }

  context.fillStyle = '#18212b';
  roundRect(context, 8, 13, 48, 39, 9);
  context.fill();

  context.fillStyle = '#fffefb';
  roundRect(context, 15, 21, 34, 14, 3);
  context.fill();

  context.fillStyle = '#18212b';
  roundRect(context, 20, 25, 24, 5, 2);
  context.fill();

  context.fillStyle = '#fffefb';
  context.beginPath();
  context.arc(22, 43, 4, 0, Math.PI * 2);
  context.arc(42, 43, 4, 0, Math.PI * 2);
  context.fill();

  context.fillStyle = '#fffefb';
  roundRect(context, 20, 15, 24, 4, 2);
  context.fill();

  mapInstance.addImage(TPL_STOP_BUS_ICON_ID, context.getImageData(0, 0, size, size), { pixelRatio: 2 });
}

function ensureStopTrainIcon(mapInstance) {
  if (!mapInstance?.addImage || mapInstance.hasImage?.(TPL_STOP_TRAIN_ICON_ID)) {
    return;
  }

  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');

  if (!context) {
    return;
  }

  context.fillStyle = '#005eb8';
  roundRect(context, 11, 9, 42, 45, 12);
  context.fill();

  context.fillStyle = '#fffefb';
  roundRect(context, 16, 15, 32, 22, 5);
  context.fill();

  context.fillStyle = '#18324a';
  roundRect(context, 20, 19, 10, 11, 3);
  roundRect(context, 34, 19, 10, 11, 3);
  context.fill();

  context.fillStyle = '#ffd166';
  context.beginPath();
  context.arc(22, 43, 3.2, 0, Math.PI * 2);
  context.arc(42, 43, 3.2, 0, Math.PI * 2);
  context.fill();

  context.strokeStyle = '#fffefb';
  context.lineWidth = 3.5;
  context.lineCap = 'round';
  context.beginPath();
  context.moveTo(22, 56);
  context.lineTo(29, 50);
  context.moveTo(42, 56);
  context.lineTo(35, 50);
  context.stroke();

  context.fillStyle = '#fffefb';
  roundRect(context, 24, 11, 16, 3, 1.5);
  context.fill();

  mapInstance.addImage(TPL_STOP_TRAIN_ICON_ID, context.getImageData(0, 0, size, size), { pixelRatio: 2 });
}

function roundRect(context, x, y, width, height, radius) {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.arcTo(x + width, y, x + width, y + height, radius);
  context.arcTo(x + width, y + height, x, y + height, radius);
  context.arcTo(x, y + height, x, y, radius);
  context.arcTo(x, y, x + width, y, radius);
  context.closePath();
}

function setSourceData(mapInstance, sourceId, data) {
  const source = mapInstance.getSource(sourceId);

  if (source?.setData) {
    source.setData(data);
  }
}

function clearMapTraceData(mapInstance) {
  if (!mapInstance) {
    return;
  }

  setSourceData(mapInstance, ALL_ROUTE_PATHS_SOURCE_ID, EMPTY_FEATURE_COLLECTION);
  setSourceData(mapInstance, ROUTE_CONTEXT_SOURCE_ID, EMPTY_FEATURE_COLLECTION);
  setSourceData(mapInstance, JOURNEY_SOURCE_ID, EMPTY_FEATURE_COLLECTION);
}

function mergePublicTransitAppConfig(baseConfig, payload) {
  const nextConfig = {
    ...baseConfig,
    ...payload,
    app: {
      ...baseConfig.app,
      ...(payload?.app || {}),
    },
    map: {
      ...baseConfig.map,
      ...(payload?.map || {}),
    },
    serviceArea: payload?.serviceArea ?? baseConfig.serviceArea ?? null,
    serviceAreas: Array.isArray(payload?.serviceAreas)
      ? payload.serviceAreas
      : Array.isArray(baseConfig.serviceAreas)
        ? baseConfig.serviceAreas
        : [],
    operator: {
      ...baseConfig.operator,
      ...(payload?.operator || {}),
    },
    routeColors: {
      ...baseConfig.routeColors,
      ...(payload?.routeColors || {}),
      routes: payload?.routeColors ? payload.routeColors.routes || {} : baseConfig.routeColors.routes,
      nightRoute: {
        ...baseConfig.routeColors.nightRoute,
        ...(payload?.routeColors?.nightRoute || {}),
      },
    },
    routeCategories: {
      ...baseConfig.routeCategories,
      ...(payload?.routeCategories || {}),
      routes: payload?.routeCategories ? payload.routeCategories.routes || {} : baseConfig.routeCategories.routes,
    },
    cache: {
      ...baseConfig.cache,
      ...(payload?.cache || {}),
    },
    features: {
      ...baseConfig.features,
      ...(payload?.features || {}),
    },
  };

  if (!Array.isArray(nextConfig.map.center) || nextConfig.map.center.length !== 2) {
    nextConfig.map.center = baseConfig.map.center || DEFAULT_CENTER;
  }

  return nextConfig;
}

function normalizeMapBounds(bounds) {
  if (!Array.isArray(bounds) || bounds.length !== 4) {
    return null;
  }

  const [west, south, east, north] = bounds.map(Number);

  if (![west, south, east, north].every(Number.isFinite) || west >= east || south >= north) {
    return null;
  }

  return [west, south, east, north];
}

function toMapboxMaxBounds(bounds) {
  const normalizedBounds = normalizeMapBounds(bounds);

  if (!normalizedBounds) {
    return undefined;
  }

  const [west, south, east, north] = normalizedBounds;
  return [
    [west, south],
    [east, north],
  ];
}

function isPointInsideBounds(longitude, latitude, bounds) {
  const normalizedBounds = normalizeMapBounds(bounds);
  const nextLongitude = Number(longitude);
  const nextLatitude = Number(latitude);

  if (!normalizedBounds || !Number.isFinite(nextLongitude) || !Number.isFinite(nextLatitude)) {
    return true;
  }

  const [west, south, east, north] = normalizedBounds;
  return nextLongitude >= west && nextLongitude <= east && nextLatitude >= south && nextLatitude <= north;
}

function getConfiguredServiceArea(config) {
  const mapBounds = normalizeMapBounds(config?.map?.bounds);
  const serviceAreaBounds = normalizeMapBounds(config?.serviceArea?.bounds);

  return {
    label: config?.serviceArea?.label || '',
    bounds: serviceAreaBounds || mapBounds,
    center: Array.isArray(config?.serviceArea?.center) ? config.serviceArea.center : config?.map?.center,
  };
}

function normalizeServiceAreaOption(area, fallbackId, fallbackLabel = t('fullCoverage')) {
  const bounds = normalizeMapBounds(area?.bounds);
  const center = Array.isArray(area?.center) && area.center.length === 2
    ? area.center.map(Number)
    : null;

  if (!bounds || !center || !center.every(Number.isFinite)) {
    return null;
  }

  return {
    id: String(area?.id || fallbackId),
    label: String(area?.label || fallbackLabel),
    bounds,
    center,
    zoom: Number(area?.zoom || DEFAULT_ZOOM),
    minZoom: Number(area?.minZoom || 5),
    stopCount: Number(area?.stopCount || 0),
  };
}

function getConfiguredServiceAreaOptions(config) {
  const defaultArea = normalizeServiceAreaOption(
    {
      ...(config?.serviceArea || {}),
      center: config?.serviceArea?.center || config?.map?.center,
      bounds: config?.serviceArea?.bounds || config?.map?.bounds,
      zoom: config?.serviceArea?.zoom || config?.map?.zoom,
      minZoom: config?.serviceArea?.minZoom || config?.map?.minZoom,
    },
    SERVICE_AREA_DEFAULT_ID,
    config?.serviceArea?.label || t('fullNetwork')
  );
  const configuredAreas = (Array.isArray(config?.serviceAreas) ? config.serviceAreas : [])
    .map((area, index) => normalizeServiceAreaOption(area, area?.id || `area-${index + 1}`, area?.label))
    .filter(Boolean);
  const seenAreaIds = new Set();

  return [defaultArea, ...configuredAreas].filter((area) => {
    if (!area || seenAreaIds.has(area.id)) {
      return false;
    }

    seenAreaIds.add(area.id);
    return true;
  });
}

function normalizeMapStyleOption(style, fallbackId = 'style') {
  const styleUrl = typeof style === 'string' ? style : style?.url || style?.style;

  if (!styleUrl) {
    return null;
  }

  const id = String(
    typeof style === 'string'
      ? fallbackId
      : style.id || style.key || fallbackId
  );

  return {
    id,
    label: String(typeof style === 'string' ? styleUrl.replace(/^mapbox:\/\/styles\/mapbox\//, '') : style.label || id),
    url: String(styleUrl),
  };
}

function getMapStyleOptions(config) {
  const configuredStyles = Array.isArray(config?.map?.styles) && config.map.styles.length > 0
    ? config.map.styles
    : DEFAULT_MAP_STYLE_OPTIONS;
  const defaultStyle = normalizeMapStyleOption(
    {
      id: 'default',
      label: 'Standard',
      url: config?.map?.style || MAP_STYLE,
    },
    'default'
  );
  const seenStyles = new Set();

  return [...configuredStyles.map((style, index) => normalizeMapStyleOption(style, `style-${index + 1}`)), defaultStyle]
    .filter(Boolean)
    .filter((style) => {
      const styleKey = style.url;

      if (seenStyles.has(styleKey)) {
        return false;
      }

      seenStyles.add(styleKey);
      return true;
    });
}

function getServiceAreaStorageKey(config) {
  return `${SERVICE_AREA_STORAGE_KEY_PREFIX}:${config?.id || INITIAL_PUBLIC_APP_CONFIG?.id || 'tpl'}`;
}

function getMapStyleStorageKey(config) {
  return `${MAP_STYLE_STORAGE_KEY_PREFIX}:${config?.id || INITIAL_PUBLIC_APP_CONFIG?.id || 'tpl'}`;
}

function readStoredServiceAreaId(config) {
  const defaultAreaId = config?.defaultServiceAreaId || SERVICE_AREA_DEFAULT_ID;

  if (typeof window === 'undefined') {
    return defaultAreaId;
  }

  try {
    return window.localStorage.getItem(getServiceAreaStorageKey(config)) || defaultAreaId;
  } catch {
    return defaultAreaId;
  }
}

function writeStoredServiceAreaId(config, areaId) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(getServiceAreaStorageKey(config), areaId);
  } catch {
    // Ignore storage failures: area selection still works for the current session.
  }
}

function readStoredMapStyleId(config) {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return window.localStorage.getItem(getMapStyleStorageKey(config)) || '';
  } catch {
    return '';
  }
}

function writeStoredMapStyleId(config, styleId) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(getMapStyleStorageKey(config), styleId);
  } catch {
    // Ignore storage failures: the map style still changes for this session.
  }
}

function findStoredOrDefaultMapStyleId(config, options) {
  const storedStyleId = readStoredMapStyleId(config);

  if (storedStyleId && options.some((style) => style.id === storedStyleId)) {
    return storedStyleId;
  }

  const configuredStyleUrl = config?.map?.style || MAP_STYLE;
  return options.find((style) => style.url === configuredStyleUrl)?.id || options[0]?.id || 'default';
}

function getTransitDataSource(config) {
  const operator = config?.operator || {};
  const sourceName =
    operator.dataSourceName ||
    operator.name ||
    config?.app?.shortName ||
    config?.app?.name ||
    'azienda TPL';
  const sourceUrl = operator.dataSourceUrl || operator.website || '';

  return {
    name: sourceName,
    url: sourceUrl,
    policy: config?.dataPolicy || null,
  };
}

function DataPolicyDisclosure({ policy }) {
  if (!policy) {
    return null;
  }

  const items = Array.isArray(policy.items) ? policy.items.filter(Boolean) : [];
  const links = Array.isArray(policy.links) ? policy.links.filter((link) => link?.label && link?.url) : [];

  return (
    <details className="data-policy">
      <summary>{policy.title || 'Data policy'}</summary>
      {policy.summary ? <p>{policy.summary}</p> : null}
      {items.length > 0 ? (
        <ul>
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      {policy.citation ? (
        <p>
          <strong>Citation:</strong> {policy.citation}
        </p>
      ) : null}
      {links.length > 0 ? (
        <p className="data-policy__links">
          {links.map((link, index) => (
            <React.Fragment key={link.url}>
              {index > 0 ? <span aria-hidden="true"> · </span> : null}
              <a href={link.url} rel="noreferrer" target={link.url.startsWith('mailto:') ? undefined : '_blank'}>
                {link.label}
              </a>
            </React.Fragment>
          ))}
        </p>
      ) : null}
    </details>
  );
}

function getInitialPublicAppConfig() {
  if (typeof window === 'undefined') {
    return DEFAULT_PUBLIC_APP_CONFIG;
  }

  return mergePublicTransitAppConfig(
    DEFAULT_PUBLIC_APP_CONFIG,
    HOST_PUBLIC_APP_CONFIGS[window.location.hostname] || {}
  );
}

const INITIAL_PUBLIC_APP_CONFIG = getInitialPublicAppConfig();
activeBrowserCachePrefix = INITIAL_PUBLIC_APP_CONFIG.id;
activeRouteColorConfig = INITIAL_PUBLIC_APP_CONFIG.routeColors;
activeConfiguredRouteColorKeys = new Set(Object.keys(activeRouteColorConfig.routes || {}));
activeRouteCategoryConfig = INITIAL_PUBLIC_APP_CONFIG.routeCategories;
activeCacheConfig = INITIAL_PUBLIC_APP_CONFIG.cache;

function normalizePublicTransitAppConfig(payload) {
  return mergePublicTransitAppConfig(INITIAL_PUBLIC_APP_CONFIG, payload);
}

function applyPublicTransitAppConfig(config) {
  activeBrowserCachePrefix = String(config?.id || INITIAL_PUBLIC_APP_CONFIG.id);
  activeRouteColorConfig = config?.routeColors || INITIAL_PUBLIC_APP_CONFIG.routeColors;
  activeConfiguredRouteColorKeys = new Set(Object.keys(activeRouteColorConfig.routes || {}));
  activeRouteCategoryConfig = config?.routeCategories || INITIAL_PUBLIC_APP_CONFIG.routeCategories;
  activeCacheConfig = config?.cache || INITIAL_PUBLIC_APP_CONFIG.cache;
}

function getBrowserCacheKey(key) {
  return `${activeBrowserCachePrefix}:${APP_RELEASE_ID}:${key}`;
}

function getAreaScopedCacheKey(key, areaId) {
  return `${key}:area:${encodeURIComponent(String(areaId || SERVICE_AREA_DEFAULT_ID))}`;
}

function getBrowserStaticCacheTtlMs() {
  const configuredTtl = Number(activeCacheConfig?.browserStaticTtlMs);
  return Number.isFinite(configuredTtl) && configuredTtl > 0 ? configuredTtl : STATIC_GTFS_CACHE_TTL_MS;
}

function getRouteContextCacheKey(requestParams) {
  return [
    ROUTE_CONTEXT_CACHE_KEY_PREFIX,
    requestParams?.areaId || SERVICE_AREA_DEFAULT_ID,
    requestParams?.routeId || '',
    requestParams?.tripId || '',
    requestParams?.shapeId || '',
    requestParams?.startDate || '',
  ]
    .map((value) => encodeURIComponent(String(value || 'any')))
    .join(':');
}

function openBrowserCacheDb() {
  if (typeof window === 'undefined' || !window.indexedDB) {
    return Promise.resolve(null);
  }

  if (browserCacheDbPromise) {
    return browserCacheDbPromise;
  }

  browserCacheDbPromise = new Promise((resolve) => {
    let request;

    try {
      request = window.indexedDB.open(BROWSER_CACHE_DB_NAME, BROWSER_CACHE_VERSION);
    } catch {
      resolve(null);
      return;
    }

    request.onupgradeneeded = () => {
      const database = request.result;

      if (!database.objectStoreNames.contains(BROWSER_CACHE_STORE_NAME)) {
        database.createObjectStore(BROWSER_CACHE_STORE_NAME, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      browserCacheDbPromise = null;
      resolve(null);
    };
    request.onblocked = () => {
      browserCacheDbPromise = null;
      resolve(null);
    };
  });

  return browserCacheDbPromise;
}

async function readBrowserCache(key, ttlMs) {
  const database = await openBrowserCacheDb();

  if (!database) {
    return null;
  }

  return new Promise((resolve) => {
    try {
      const transaction = database.transaction(BROWSER_CACHE_STORE_NAME, 'readonly');
      const request = transaction.objectStore(BROWSER_CACHE_STORE_NAME).get(getBrowserCacheKey(key));

      request.onsuccess = () => {
        const entry = request.result;
        const isFresh =
          entry &&
          entry.value &&
          Number.isFinite(Number(entry.savedAt)) &&
          Date.now() - Number(entry.savedAt) <= ttlMs;

        resolve(isFresh ? entry.value : null);
      };
      request.onerror = () => resolve(null);
      transaction.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function writeBrowserCache(key, value) {
  const database = await openBrowserCacheDb();

  if (!database || !value) {
    return;
  }

  return new Promise((resolve) => {
    try {
      const transaction = database.transaction(BROWSER_CACHE_STORE_NAME, 'readwrite');
      const request = transaction.objectStore(BROWSER_CACHE_STORE_NAME).put({
        key: getBrowserCacheKey(key),
        value,
        savedAt: Date.now(),
      });

      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
      transaction.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

async function deleteBrowserCache(key) {
  const database = await openBrowserCacheDb();

  if (!database) {
    return;
  }

  return new Promise((resolve) => {
    try {
      const transaction = database.transaction(BROWSER_CACHE_STORE_NAME, 'readwrite');
      const request = transaction.objectStore(BROWSER_CACHE_STORE_NAME).delete(getBrowserCacheKey(key));

      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
      transaction.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

function normalizeBuses(payload) {
  if (Array.isArray(payload?.vehicles)) {
    return payload.vehicles
      .flatMap((vehicle) => {
        const longitude = Number(vehicle?.longitude);
        const latitude = Number(vehicle?.latitude);
        const rawRouteShortName = String(vehicle.routeShortName ?? vehicle.routeId);

        if (!vehicle?.routeId || !Number.isFinite(longitude) || !Number.isFinite(latitude)) {
          return [];
        }

        registerGtfsRouteColor(vehicle.routeId, rawRouteShortName, vehicle.routeColor);

        return [
          {
            id: String(vehicle.id),
            label: String(vehicle.label ?? vehicle.id),
            routeId: String(vehicle.routeId),
            routeShortName: formatRouteShortName(rawRouteShortName),
            routeColor: normalizeRouteColor(vehicle.routeColor),
            routeTextColor: normalizeRouteColor(vehicle.routeTextColor),
            routeLongName: formatRouteLongName(
              {
                id: String(vehicle.routeId),
                shortName: rawRouteShortName,
                longName: String(vehicle.routeLongName ?? '').replaceAll('_', ' ').trim(),
              },
              vehicle.routeId
            ),
            tripHeadsign: String(vehicle.tripHeadsign ?? '').replaceAll('_', ' ').trim(),
            tripId: String(vehicle.tripId ?? ''),
            shapeId: String(vehicle.shapeId ?? ''),
            directionId: vehicle.directionId ?? null,
            startTime: vehicle.startTime ?? 'n/d',
            startDate: vehicle.startDate ?? '',
            stopId: vehicle.stopId ?? 'n/d',
            stopName: String(vehicle.stopName ?? '').replaceAll('_', ' ').trim(),
            startStopName: String(vehicle.startStopName ?? '').replaceAll('_', ' ').trim(),
            endStopName: String(vehicle.endStopName ?? '').replaceAll('_', ' ').trim(),
            nextStopId: vehicle.nextStopId ?? '',
            nextStopName: String(vehicle.nextStopName ?? '').replaceAll('_', ' ').trim(),
            nextStopPredictedTime: Number(vehicle.nextStopPredictedTime || vehicle.calculatedNextStopPredictedTime || 0),
            nextStopScheduledTime: Number(vehicle.nextStopScheduledTime ?? 0),
            calculatedNextStopPredictedTime: Number(vehicle.calculatedNextStopPredictedTime ?? 0),
            calculatedNextStopEtaSeconds: Number(vehicle.calculatedNextStopEtaSeconds ?? 0),
            delaySeconds: Number.isFinite(Number(vehicle.delaySeconds)) ? Number(vehicle.delaySeconds) : null,
            journeyProgress: vehicle.journeyProgress ?? null,
            longitude,
            latitude,
            bearing: Number(vehicle.bearing ?? 0),
            speedKmh: toKmPerHour(vehicle.speed),
            timestamp: Number(vehicle.timestamp ?? 0),
          },
        ];
      })
      .sort(sortByRouteAndLabel);
  }

  const entities = payload?.data?.entity ?? [];

  return entities
    .flatMap((entity) => {
      const vehicle = entity?.vehicle;
      const trip = vehicle?.trip;
      const position = vehicle?.position;
      const longitude = Number(position?.longitude);
      const latitude = Number(position?.latitude);

      if (!trip?.routeId || !Number.isFinite(longitude) || !Number.isFinite(latitude)) {
        return [];
      }

      return [
        {
          id: String(entity.id),
          label: String(vehicle?.vehicle?.label ?? entity.id),
          routeId: String(trip.routeId),
          routeShortName: formatRouteShortName(trip.routeId),
          routeLongName: formatRouteLongName(trip.routeId),
          tripHeadsign: '',
          tripId: '',
          shapeId: '',
          directionId: trip?.directionId ?? null,
          startTime: trip?.startTime ?? 'n/d',
          startDate: trip?.startDate ?? '',
          stopId: vehicle?.stopId ?? 'n/d',
          stopName: '',
          startStopName: '',
          endStopName: '',
          nextStopId: '',
          nextStopName: '',
          nextStopPredictedTime: 0,
          nextStopScheduledTime: 0,
          delaySeconds: null,
          longitude,
          latitude,
          bearing: Number(position?.bearing ?? 0),
          speedKmh: toKmPerHour(position?.speed),
          timestamp: Number(vehicle?.timestamp ?? 0),
        },
      ];
    })
    .sort(sortByRouteAndLabel);
}

function normalizeServiceAlerts(payload) {
  const alerts = Array.isArray(payload?.alerts) ? payload.alerts : [];

  return alerts
    .map((alert, index) => {
      const header = String(alert?.header || '').trim();
      const description = String(alert?.description || '').trim();
      const informedEntities = Array.isArray(alert?.informedEntities) ? alert.informedEntities : [];

      return {
        id: String(alert?.id || `${header}-${index}` || index),
        header,
        description,
        url: String(alert?.url || '').trim(),
        cause: String(alert?.cause || '').trim(),
        effect: String(alert?.effect || '').trim(),
        severityLevel: String(alert?.severityLevel || '').trim(),
        informedEntityCount: Number(alert?.informedEntityCount || informedEntities.length || 0),
        routeIds: sortRouteLabels(informedEntities.map((entity) => entity?.routeId).filter(Boolean)),
        stopIds: [...new Set(informedEntities.map((entity) => String(entity?.stopId || '')).filter(Boolean))],
      };
    })
    .filter((alert) => alert.header || alert.description || alert.informedEntityCount > 0);
}

function normalizeTransitCatalog(payload) {
  const routes = Array.isArray(payload?.routes)
    ? payload.routes
        .map((route) => ({
          id: String(route.id),
          shortName: formatRouteShortName(route.shortName || route.id),
          longName: formatRouteLongName(
            {
              id: String(route.id),
              shortName: String(route.shortName || route.id),
              longName: String(route.longName || '').replaceAll('_', ' ').trim(),
            },
            route.id
          ),
          color: normalizeRouteColor(route.color),
          textColor: normalizeRouteColor(route.textColor),
          type: route.type ?? null,
          tripCount: Number(route.tripCount || 0),
          stopCount: Number(route.stopCount || 0),
          shapeCount: Number(route.shapeCount || 0),
        }))
        .map((route) => {
          registerGtfsRouteColor(route.id, route.shortName, route.color);
          return route;
        })
        .sort((firstRoute, secondRoute) =>
          firstRoute.shortName.localeCompare(secondRoute.shortName, 'it', { numeric: true })
        )
    : [];
  const stops = Array.isArray(payload?.stops)
    ? payload.stops
        .flatMap((stop) => {
          const longitude = Number(stop.longitude);
          const latitude = Number(stop.latitude);

          if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
            return [];
          }

          return [
            {
              id: String(stop.id),
              name: String(stop.name || '').replaceAll('_', ' ').trim(),
              latitude,
              longitude,
              routeIds: Array.isArray(stop.routeIds) ? stop.routeIds.map(String) : [],
              routeShortNames: Array.isArray(stop.routeShortNames)
                ? stop.routeShortNames.map(formatRouteShortName)
                : [],
              hasTrainRoutes: Boolean(stop.hasTrainRoutes),
            },
          ];
        })
        .sort((firstStop, secondStop) => firstStop.name.localeCompare(secondStop.name, 'it'))
    : [];

  return {
    meta: payload?.meta ?? {},
    routes,
    stops,
  };
}

function normalizeSearchToken(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function buildJourneyStopSuggestions(stops, searchText, limit = 6) {
  const query = normalizeSearchToken(searchText);

  if (query.length < 3 || !Array.isArray(stops)) {
    return [];
  }

  return stops
    .flatMap((stop) => {
      const name = String(stop?.name || '').trim();
      const stopId = String(stop?.id || '').trim();
      const routeLabels = Array.isArray(stop?.routeShortNames) ? stop.routeShortNames.filter(Boolean) : [];
      const searchableName = normalizeSearchToken(name);
      const searchableId = normalizeSearchToken(stopId);
      const searchableRoutes = normalizeSearchToken(routeLabels.join(' '));

      if (!name || !Number.isFinite(stop?.longitude) || !Number.isFinite(stop?.latitude)) {
        return [];
      }

      const matchesName = searchableName.includes(query);
      const matchesId = searchableId.includes(query);
      const matchesRoute = searchableRoutes.includes(query);

      if (!matchesName && !matchesId && !matchesRoute) {
        return [];
      }

      const routeSuffix = routeLabels.length
        ? t('stopLineRoutesSuffix', { routes: routeLabels.slice(0, 4).join(', ') })
        : '';
      const score =
        (searchableName === query ? 120 : 0) +
        (searchableName.startsWith(query) ? 90 : 0) +
        (matchesName ? 45 : 0) +
        (searchableId === query ? 80 : matchesId ? 30 : 0) +
        (matchesRoute ? 10 : 0) -
        Math.min(name.length / 10, 8);

      return [
        {
          id: `stop-${stopId}`,
          label: name,
          subtitle: t('stopLineSummary', { stopId, routes: routeSuffix }),
          typeLabel: t('journeyStopResult'),
          longitude: stop.longitude,
          latitude: stop.latitude,
          featureType: 'stop',
          stopId,
          routeShortNames: routeLabels,
          score,
        },
      ];
    })
    .sort((first, second) => second.score - first.score || first.label.localeCompare(second.label, 'it'))
    .slice(0, limit);
}

function isTransitCatalogUsableForArea(catalog, areaId) {
  if (!catalog || !Array.isArray(catalog.routes) || !Array.isArray(catalog.stops)) {
    return false;
  }

  if (catalog.routes.length === 0 && catalog.stops.length === 0) {
    return false;
  }

  const expectedAreaId = String(areaId || SERVICE_AREA_DEFAULT_ID);
  const catalogAreaId = catalog.meta?.area?.id ? String(catalog.meta.area.id) : SERVICE_AREA_DEFAULT_ID;

  return expectedAreaId === SERVICE_AREA_DEFAULT_ID || catalogAreaId === expectedAreaId;
}

function normalizeRoutePathFeature(feature) {
  const coordinates = Array.isArray(feature?.geometry?.coordinates)
    ? feature.geometry.coordinates.filter(
        (coordinate) =>
          Array.isArray(coordinate) &&
          coordinate.length >= 2 &&
          Number.isFinite(Number(coordinate[0])) &&
          Number.isFinite(Number(coordinate[1]))
      )
    : [];

  if (coordinates.length <= 1) {
    return null;
  }

  const properties = feature?.properties ?? {};
  const routeId = String(properties.routeId || '');
  const routeShortName = formatRouteShortName(properties.routeShortName || routeId);
  const routeLongName = formatRouteLongName(
    {
      id: routeId,
      shortName: properties.routeShortName || routeId,
      longName: String(properties.routeLongName || '').replaceAll('_', ' ').trim(),
    },
    routeId
  );

  return {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: coordinates.map((coordinate) => [Number(coordinate[0]), Number(coordinate[1])]),
    },
    properties: {
      ...properties,
      routeId,
      routeShortName,
      routeLongName,
      routeCategory: getRouteCategory(routeId, routeShortName),
      color: getRouteColor(routeId, routeShortName),
    },
  };
}

function normalizeRoutePathCollection(payload) {
  return Array.isArray(payload?.features)
    ? payload.features.map(normalizeRoutePathFeature).filter(Boolean)
    : [];
}

function buildStopFeatureCollection(stops, selectedRouteIds = [], selectedStopId = '') {
  const selectedRouteIdSet = new Set(
    (Array.isArray(selectedRouteIds) ? selectedRouteIds : [selectedRouteIds]).filter(Boolean)
  );

  return {
    type: 'FeatureCollection',
    features: stops
      .filter((stop) => {
        if (selectedRouteIdSet.size === 0 || stop.routeIds.length === 0) {
          return true;
        }

        return stop.routeIds.some((routeId) => selectedRouteIdSet.has(routeId));
      })
      .map((stop) => {
        const isRouteStop =
          selectedRouteIdSet.size > 0 && stop.routeIds.some((routeId) => selectedRouteIdSet.has(routeId));

        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [stop.longitude, stop.latitude],
          },
          properties: {
            id: stop.id,
            name: stop.name,
            routeIds: stop.routeIds.join(','),
            routeShortNames: stop.routeShortNames.join(', '),
            hasTrainRoutes: Boolean(stop.hasTrainRoutes),
            isRouteStop,
            isDimmed: Boolean(selectedRouteIdSet.size > 0 && stop.routeIds.length > 0 && !isRouteStop),
            isSelected: stop.id === selectedStopId,
          },
        };
      }),
  };
}

function normalizeMapStopFeature(feature) {
  const [longitude, latitude] = feature?.geometry?.coordinates ?? [];
  const properties = feature?.properties ?? {};

  if (!properties.id || !Number.isFinite(Number(longitude)) || !Number.isFinite(Number(latitude))) {
    return null;
  }

  return {
    id: String(properties.id),
    name: String(properties.name || ''),
    longitude: Number(longitude),
    latitude: Number(latitude),
    routeIds: String(properties.routeIds || '')
      .split(',')
      .map((routeId) => routeId.trim())
      .filter(Boolean),
    routeShortNames: String(properties.routeShortNames || '')
      .split(',')
      .map((routeName) => routeName.trim())
      .filter(Boolean),
    hasTrainRoutes: Boolean(properties.hasTrainRoutes),
  };
}

function createMarkerMarkup(bus, isSelected) {
  return `
    <span class="map-marker__pill ${isSelected ? 'is-selected' : ''}">
      <span class="map-marker__route">${bus.routeShortName || bus.routeId}</span>
      <span class="map-marker__bearing" aria-hidden="true">
        <span class="map-marker__bearing-arrow">↑</span>
      </span>
    </span>
  `;
}

function updateMarkerElement(element, bus, isSelected, journeyStatus = '') {
  element.classList.add('map-marker');
  element.classList.toggle('is-selected', isSelected);
  element.classList.toggle('is-journey-route', journeyStatus === 'route');
  element.classList.toggle('is-journey-live', journeyStatus === 'live');
  element.type = 'button';
  const routeColor = getRouteColor(bus.routeId, bus.routeShortName);
  element.style.setProperty('--route-color', routeColor);
  element.style.setProperty('--route-text-color', getReadableTextColor(routeColor));
  element.style.setProperty('--bearing-deg', `${bus.bearing || 0}deg`);
  element.style.zIndex = journeyStatus === 'live' || isSelected ? '20' : journeyStatus === 'route' ? '4' : '2';
  element.innerHTML = createMarkerMarkup(bus, isSelected);
  element.setAttribute('aria-label', t('busMarkerLabel', { label: bus.label, route: bus.routeShortName || bus.routeId }));
  element.title = `${bus.routeShortName || bus.routeId} · Bus ${bus.label}`;
}

function buildMarkerElement(bus, isSelected, onSelect, journeyStatus = '') {
  const element = document.createElement('button');
  updateMarkerElement(element, bus, isSelected, journeyStatus);
  element.addEventListener('click', (event) => {
    event.stopPropagation();
    onSelect();
  });
  return element;
}

function updateStopMarkerElement(element, stop, isSelected, routeColor) {
  element.classList.add('stop-marker');
  element.classList.toggle('is-selected', isSelected);
  element.classList.toggle('is-terminal', Boolean(stop.isTerminal));
  element.style.setProperty('--route-color', routeColor);
  element.setAttribute('aria-label', t('stopMarkerLabel', { name: stop.name }));
  element.title = stop.name;
  element.type = 'button';
  element.innerHTML = `
    <span class="stop-marker__pin" aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path d="M7 6.8C7 5.25 8.25 4 9.8 4h4.4C15.75 4 17 5.25 17 6.8v7.4c0 .72-.58 1.3-1.3 1.3h-.25l1.05 2H15l-.78-1.5H9.78L9 17.5H7.5l1.05-2H8.3c-.72 0-1.3-.58-1.3-1.3V6.8Z" />
        <path d="M9 7h6M9 10.2h6M9.4 13.2h.1M14.5 13.2h.1" />
      </svg>
    </span>
  `;
}

function buildStopMarkerElement(stop, isSelected, routeColor, onSelect) {
  const element = document.createElement('button');
  updateStopMarkerElement(element, stop, isSelected, routeColor);
  element.addEventListener('click', (event) => {
    event.stopPropagation();
    onSelect();
  });
  return element;
}

function getGtfsProgressLabel(bus) {
  const progress = bus?.journeyProgress;

  if (!progress) {
    return '';
  }

  const remainingDistance = Number.isFinite(Number(progress.remainingToNextStopMeters))
    ? t('nextStopDistance', { distance: formatDistanceMeters(progress.remainingToNextStopMeters) })
    : '';
  const etaLabel = formatDurationSeconds(bus.calculatedNextStopEtaSeconds);

  return [remainingDistance, etaLabel ? `${etaLabel}` : '']
    .filter(Boolean)
    .join(' · ');
}

function normalizeJourneyStopName(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function findJourneyStopIndexByName(stops, name) {
  const normalizedName = normalizeJourneyStopName(name);

  if (!normalizedName) {
    return -1;
  }

  const exactIndex = stops.findIndex((stop) => normalizeJourneyStopName(stop?.name) === normalizedName);

  if (exactIndex >= 0) {
    return exactIndex;
  }

  return stops.findIndex((stop) => {
    const stopName = normalizeJourneyStopName(stop?.name);
    return stopName && (stopName.includes(normalizedName) || normalizedName.includes(stopName));
  });
}

function findJourneyStopIndexById(stops, stopId) {
  const normalizedStopId = String(stopId || '');

  return normalizedStopId
    ? stops.findIndex((stop) => String(stop.id || '') === normalizedStopId)
    : -1;
}

function getJourneyLiveBusNextStopIndex(stops, liveBus) {
  const projectedNextStopId = String(liveBus?.journeyProgress?.calculatedNextStopId || '');
  const projectedNextStopIndex = findJourneyStopIndexById(stops, projectedNextStopId);

  if (projectedNextStopIndex >= 0) {
    return projectedNextStopIndex;
  }

  const progressNextStopId = String(liveBus?.journeyProgress?.nextStopId || '');
  const progressNextStopIndex = findJourneyStopIndexById(stops, progressNextStopId);

  if (progressNextStopIndex >= 0) {
    return progressNextStopIndex;
  }

  const nextStopIndex = findJourneyStopIndexById(stops, liveBus?.nextStopId);

  return nextStopIndex >= 0
    ? nextStopIndex
    : findJourneyStopIndexByName(stops, liveBus?.nextStopName);
}

function getJourneyLiveBusStopState(leg) {
  const liveBus = leg?.liveBus;
  const stops = Array.isArray(leg?.stops) ? leg.stops : [];

  if (!liveBus || stops.length === 0) {
    return null;
  }

  const currentStopId = String(liveBus.stopId || '');
  const currentStopIndexById = currentStopId && currentStopId !== 'n/d'
    ? findJourneyStopIndexById(stops, currentStopId)
    : -1;
  const currentStopIndex = currentStopIndexById >= 0
    ? currentStopIndexById
    : findJourneyStopIndexByName(stops, liveBus.stopName);
  const nextStopIndex = getJourneyLiveBusNextStopIndex(stops, liveBus);
  const nextStop = nextStopIndex >= 0 ? stops[nextStopIndex] : null;
  const currentStop = currentStopIndex >= 0 ? stops[currentStopIndex] : null;

  if (nextStop) {
    const isBeforeNextStop = nextStopIndex === 0;

    return {
      markerBeforeIndex: isBeforeNextStop ? 0 : null,
      markerAfterIndex: isBeforeNextStop ? null : nextStopIndex - 1,
      nextStopIndex,
      currentStopIndex,
      text: t('journeyBusTowardsStop', {
        label: liveBus.label,
        stop: liveBus.nextStopName || nextStop.name,
      }),
      detail: getGtfsProgressLabel(liveBus),
    };
  }

  if (currentStop) {
    return {
      markerAfterIndex: currentStopIndex,
      nextStopIndex,
      currentStopIndex,
      text: t('journeyBusAtStop', {
        label: liveBus.label,
        stop: currentStop.name,
      }),
      detail: getGtfsProgressLabel(liveBus),
    };
  }

  if (liveBus.nextStopName) {
    return {
      markerBeforeIndex: 0,
      markerAfterIndex: null,
      nextStopIndex,
      currentStopIndex,
      text: t('journeyBusTowardsStop', {
        label: liveBus.label,
        stop: liveBus.nextStopName,
      }),
      detail: getGtfsProgressLabel(liveBus),
    };
  }

  return null;
}

function getJourneyProgressIndexFromState(stopState) {
  if (!stopState) {
    return -1;
  }

  const markerAfterIndex = Number(stopState.markerAfterIndex);
  const currentStopIndex = Number(stopState.currentStopIndex);
  const nextStopIndex = Number(stopState.nextStopIndex);
  const progressIndexes = [];

  if (Number.isFinite(markerAfterIndex) && markerAfterIndex >= 0) {
    progressIndexes.push(markerAfterIndex);
  }

  if (Number.isFinite(currentStopIndex) && currentStopIndex >= 0) {
    progressIndexes.push(currentStopIndex);
  }

  if (Number.isFinite(nextStopIndex) && nextStopIndex > 0) {
    progressIndexes.push(nextStopIndex - 1);
  }

  return progressIndexes.length ? Math.max(...progressIndexes) : -1;
}

function getMostAdvancedJourneyStopState(liveBusStopState, gpsStopState) {
  const liveProgressIndex = getJourneyProgressIndexFromState(liveBusStopState);
  const gpsProgressIndex = getJourneyProgressIndexFromState(gpsStopState);

  return gpsProgressIndex > liveProgressIndex ? gpsStopState : liveBusStopState;
}

function getJourneyRemainingStopCount(leg, liveBusStopState, gpsStopState) {
  const stops = Array.isArray(leg?.stops) ? leg.stops : [];
  const progressState = getMostAdvancedJourneyStopState(liveBusStopState, gpsStopState);

  if (!progressState) {
    return Number(leg?.stopCount || 0);
  }

  if (stops.length <= 1) {
    return Number(leg?.stopCount || 0);
  }

  const arrivalIndex = stops.length - 1;
  const progressIndex = getJourneyProgressIndexFromState(progressState);

  if (progressIndex >= 0) {
    return Math.max(0, arrivalIndex - Math.min(arrivalIndex, progressIndex));
  }

  return Number(leg?.stopCount || Math.max(0, stops.length - 1));
}

function getJourneyStopDistanceMeters(stop, userLocation) {
  const latitude = Number(stop?.latitude);
  const longitude = Number(stop?.longitude);

  if (!userLocation || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return measureDistanceMeters(userLocation, { latitude, longitude });
}

function getNearestJourneyStopByLocation(leg, userLocation) {
  const stops = Array.isArray(leg?.stops) ? leg.stops : [];

  if (!userLocation || stops.length === 0) {
    return null;
  }

  return stops.reduce((nearestStop, stop, index) => {
    const distanceMeters = getJourneyStopDistanceMeters(stop, userLocation);

    if (!Number.isFinite(distanceMeters)) {
      return nearestStop;
    }

    if (!nearestStop || distanceMeters < nearestStop.distanceMeters) {
      return { stop, index, distanceMeters };
    }

    return nearestStop;
  }, null);
}

function getJourneyNavigationGpsState(leg, userLocation, liveBusStopState) {
  if (!leg) {
    return null;
  }

  if (!userLocation) {
    return {
      status: 'waiting',
      text: t('journeyNavigationGpsWaiting'),
      detail: '',
    };
  }

  const stops = Array.isArray(leg.stops) ? leg.stops : [];
  const boardingStop = stops[0] || leg.from || null;
  const arrivalStop = stops[stops.length - 1] || leg.to || null;
  const boardingDistance = getJourneyStopDistanceMeters(boardingStop, userLocation);
  const arrivalDistance = getJourneyStopDistanceMeters(arrivalStop, userLocation);
  const nearestStop = getNearestJourneyStopByLocation(leg, userLocation);

  if (Number.isFinite(arrivalDistance) && arrivalDistance <= JOURNEY_NAVIGATION_STOP_RADIUS_METERS) {
    const arrivalIndex = stops.length > 1 ? stops.length - 1 : 0;

    return {
      status: 'arrival',
      markerAfterIndex: arrivalIndex,
      currentStopIndex: arrivalIndex,
      nextStopIndex: arrivalIndex,
      text: t('journeyNavigationGpsAtArrival'),
      detail: t('journeyExitAt', { stop: getJourneyPlaceName(arrivalStop, 'destination') }),
    };
  }

  if (
    Number.isFinite(boardingDistance) &&
    boardingDistance <= JOURNEY_NAVIGATION_STOP_RADIUS_METERS &&
    (!liveBusStopState || liveBusStopState.nextStopIndex <= 0)
  ) {
    return {
      status: 'boarding',
      markerAfterIndex: 0,
      currentStopIndex: 0,
      nextStopIndex: stops.length > 1 ? 1 : 0,
      text: t('journeyNavigationGpsAtBoarding'),
      detail: t('journeyNavigationWaitingBus', { line: leg.route }),
    };
  }

  if (nearestStop?.stop && nearestStop.distanceMeters <= JOURNEY_NAVIGATION_NEAR_STOP_RADIUS_METERS) {
    const arrivalIndex = Math.max(0, stops.length - 1);
    const nearestStopIndex = Math.max(0, nearestStop.index);

    return {
      status: 'near-stop',
      markerAfterIndex: nearestStopIndex,
      currentStopIndex: nearestStopIndex,
      nextStopIndex: Math.min(arrivalIndex, nearestStopIndex + 1),
      text: t('journeyNavigationGpsNearStop', {
        stop: getJourneyPlaceName(nearestStop.stop, 'destination'),
      }),
      detail: t('journeyNavigationGpsDistance', {
        distance: formatDistanceMeters(nearestStop.distanceMeters),
      }),
    };
  }

  return {
    status: 'tracking',
    text: t('journeyNavigationGpsTracking'),
    detail: nearestStop?.stop
      ? t('journeyNavigationGpsDistance', {
          distance: formatDistanceMeters(nearestStop.distanceMeters),
        })
      : '',
  };
}

function getJourneyLiveBusScheduledGapSeconds(leg, bus, nextStopIndex) {
  const stops = Array.isArray(leg?.stops) ? leg.stops : [];
  const stop = Number.isFinite(nextStopIndex) && nextStopIndex >= 0 ? stops[nextStopIndex] : stops[0];
  const scheduledUnix = getJourneyStopScheduledUnix(leg, stop) || Number(leg?.startTime || 0);
  const predictedUnix = Number(
    bus?.nextStopPredictedTime ||
      bus?.calculatedNextStopPredictedTime ||
      (Number(bus?.calculatedNextStopEtaSeconds) > 0
        ? Math.floor(Date.now() / 1000) + Number(bus.calculatedNextStopEtaSeconds)
        : 0)
  );

  if (!Number.isFinite(scheduledUnix) || scheduledUnix <= 0 || !Number.isFinite(predictedUnix) || predictedUnix <= 0) {
    return null;
  }

  return predictedUnix - scheduledUnix;
}

function isJourneyLiveBusRouteCompatible(leg, bus) {
  const legRouteLabels = [leg?.route, leg?.routeId].map(getComparableRouteLabel).filter(Boolean);
  const busRouteLabels = [bus?.routeShortName, bus?.routeId].map(getComparableRouteLabel).filter(Boolean);

  return legRouteLabels.some((routeLabel) => busRouteLabels.includes(routeLabel));
}

function areJourneyLiveBusHeadsignsCompatible(leg, bus) {
  const legHeadsign = normalizePassageText(leg?.headsign || leg?.to?.name || '');
  const busHeadsign = normalizePassageText(bus?.tripHeadsign || bus?.endStopName || '');

  return !legHeadsign ||
    !busHeadsign ||
    legHeadsign === busHeadsign ||
    legHeadsign.includes(busHeadsign) ||
    busHeadsign.includes(legHeadsign);
}

function getJourneyLiveBusBoardingState(leg, bus) {
  const stops = Array.isArray(leg?.stops) ? leg.stops : [];

  if (stops.length === 0) {
    return {
      currentStopIndex: -1,
      projectedNextStopIndex: -1,
      nextStopIndex: -1,
      isAfterBoarding: false,
      isAtOrBeforeBoarding: true,
      scheduledGapSeconds: getJourneyLiveBusScheduledGapSeconds(leg, bus, -1),
    };
  }

  const currentStopId = String(bus?.stopId || '');
  const currentStopIndexById = currentStopId && currentStopId !== 'n/d'
    ? findJourneyStopIndexById(stops, currentStopId)
    : -1;
  const currentStopIndex = currentStopIndexById >= 0
    ? currentStopIndexById
    : findJourneyStopIndexByName(stops, bus?.stopName);
  const projectedNextStopIndex = findJourneyStopIndexById(stops, bus?.journeyProgress?.calculatedNextStopId);
  const nextStopIndex = getJourneyLiveBusNextStopIndex(stops, bus);
  const isAfterBoarding = currentStopIndex > 0 || nextStopIndex > 0 || projectedNextStopIndex > 0;
  const isAtOrBeforeBoarding =
    currentStopIndex === 0 ||
    nextStopIndex === 0 ||
    projectedNextStopIndex === 0 ||
    (currentStopIndex < 0 && nextStopIndex < 0 && projectedNextStopIndex < 0);

  return {
    currentStopIndex,
    projectedNextStopIndex,
    nextStopIndex,
    isAfterBoarding,
    isAtOrBeforeBoarding,
    scheduledGapSeconds: getJourneyLiveBusScheduledGapSeconds(
      leg,
      bus,
      nextStopIndex >= 0 ? nextStopIndex : projectedNextStopIndex
    ),
  };
}

function scoreJourneyLiveBusCandidate(leg, bus, { requireTimeMatch = false } = {}) {
  if (!isJourneyLiveBusRouteCompatible(leg, bus) || !areJourneyLiveBusHeadsignsCompatible(leg, bus)) {
    return null;
  }

  const boardingState = getJourneyLiveBusBoardingState(leg, bus);

  if (boardingState.isAfterBoarding || !boardingState.isAtOrBeforeBoarding) {
    return null;
  }

  const gapSeconds = boardingState.scheduledGapSeconds;
  const hasUsableTime = Number.isFinite(gapSeconds);

  if (requireTimeMatch && (!hasUsableTime || Math.abs(gapSeconds) > JOURNEY_LIVE_MATCH_WINDOW_SECONDS)) {
    return null;
  }

  const stopScore =
    boardingState.currentStopIndex === 0 ? 0 :
    boardingState.nextStopIndex === 0 ? 1 :
    boardingState.projectedNextStopIndex === 0 ? 2 :
    4;
  const timeScore = hasUsableTime ? Math.abs(gapSeconds) / 60 : 8;
  const routeIdScore = String(bus?.routeId || '') === String(leg?.routeId || '') ? 0 : 1;

  return stopScore * 20 + timeScore + routeIdScore;
}

function findJourneyLiveBusForLeg(leg, buses, { following = false } = {}) {
  if (leg?.mode === 'WALK' || !Array.isArray(buses)) {
    return null;
  }

  const exactTripBus = leg?.tripId
    ? buses.find((bus) => bus.tripId === leg.tripId)
    : null;

  if (exactTripBus) {
    if (following) {
      return exactTripBus;
    }

    return scoreJourneyLiveBusCandidate(leg, exactTripBus) === null ? null : exactTripBus;
  }

  return buses
    .map((bus) => ({
      bus,
      score: scoreJourneyLiveBusCandidate(leg, bus, { requireTimeMatch: !following }),
    }))
    .filter((candidate) => candidate.score !== null)
    .sort((firstCandidate, secondCandidate) => firstCandidate.score - secondCandidate.score)[0]?.bus || null;
}

function getCatchableJourneyLiveBus(leg, buses) {
  return findJourneyLiveBusForLeg(leg, buses);
}

function getFollowingJourneyLiveBus(leg, buses) {
  return findJourneyLiveBusForLeg(leg, buses, { following: true });
}

function getJourneyOptionWithLiveBuses(option, buses) {
  if (!option) {
    return null;
  }

  return {
    ...option,
    legs: (option.legs || []).map((leg) => ({
      ...leg,
      liveBus: getCatchableJourneyLiveBus(leg, buses),
    })),
  };
}

function getJourneyOptionLiveBusKeys(option) {
  return [
    ...new Set(
      (option?.legs || [])
        .map((leg) => leg?.liveBus)
        .filter(Boolean)
        .map((bus) => String(bus.id || bus.tripId || bus.label || ''))
        .filter(Boolean)
    ),
  ];
}

function getJourneyOptionDedupeKey(option) {
  const liveBusKeys = getJourneyOptionLiveBusKeys(option);

  return liveBusKeys.length
    ? `live:${liveBusKeys.join('|')}`
    : `scheduled:${getJourneyOptionSignature(option) || option?.id || ''}`;
}

function getJourneyLegAlerts(leg, alerts) {
  if (!leg || leg.mode === 'WALK' || !Array.isArray(alerts) || alerts.length === 0) {
    return [];
  }

  const routeKeys = new Set(
    [leg.routeId, leg.route]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  );
  const stopKeys = new Set(
    [leg.from?.id, leg.to?.id, ...(Array.isArray(leg.stops) ? leg.stops.map((stop) => stop.id) : [])]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  );

  return alerts.filter((alert) => {
    const routeMatch = Array.isArray(alert.routeIds) && alert.routeIds.some((routeId) => routeKeys.has(String(routeId)));
    const stopMatch = Array.isArray(alert.stopIds) && alert.stopIds.some((stopId) => stopKeys.has(String(stopId)));
    const genericMatch = !alert.routeIds?.length && !alert.stopIds?.length && alert.informedEntityCount > 0;

    return routeMatch || stopMatch || genericMatch;
  });
}

function getDelayToneClassName(delaySeconds) {
  const normalizedDelay = Number(delaySeconds);

  if (!Number.isFinite(normalizedDelay)) {
    return '';
  }

  if (normalizedDelay > 180) {
    return 'delay-tone delay-tone--late';
  }

  if (normalizedDelay < -180) {
    return 'delay-tone delay-tone--early';
  }

  return '';
}

function getJourneyStopDelayClassName(delaySeconds) {
  const normalizedDelay = Number(delaySeconds);

  if (!Number.isFinite(normalizedDelay)) {
    return '';
  }

  if (normalizedDelay > 180) {
    return 'is-late';
  }

  if (normalizedDelay < -180) {
    return 'is-early';
  }

  return '';
}

function hasDelaySeconds(delaySeconds) {
  return delaySeconds !== null && delaySeconds !== undefined && delaySeconds !== '' && Number.isFinite(Number(delaySeconds));
}

function getGtfsClockLabel(timeValue) {
  const [rawHours, rawMinutes] = String(timeValue || '').split(':').map(Number);

  if (!Number.isFinite(rawHours) || !Number.isFinite(rawMinutes)) {
    return '';
  }

  return `${String(rawHours % 24).padStart(2, '0')}:${String(rawMinutes).padStart(2, '0')}`;
}

function getJourneyStopScheduledUnix(leg, stop) {
  const serviceDate = String(leg?.serviceDate || '');
  const timeValue = String(stop?.arrivalTime || stop?.departureTime || '');
  const [rawHours, rawMinutes, rawSeconds = 0] = timeValue.split(':').map(Number);

  if (
    !/^\d{8}$/.test(serviceDate) ||
    !Number.isFinite(rawHours) ||
    !Number.isFinite(rawMinutes)
  ) {
    return null;
  }

  const year = Number(serviceDate.slice(0, 4));
  const month = Number(serviceDate.slice(4, 6));
  const day = Number(serviceDate.slice(6, 8));
  const date = new Date(year, month - 1, day, 0, 0, 0, 0);
  date.setSeconds(rawHours * 3600 + rawMinutes * 60 + (Number.isFinite(rawSeconds) ? rawSeconds : 0));

  return Math.floor(date.getTime() / 1000);
}

function getJourneyStopTimelineState(leg, stop, stopIndex, liveBusStopState) {
  const scheduledUnix = getJourneyStopScheduledUnix(leg, stop);
  const hasLiveBus = Boolean(leg?.liveBus);
  const delaySeconds = hasDelaySeconds(leg?.liveBus?.delaySeconds) ? Number(leg.liveBus.delaySeconds) : 0;
  const adjustedUnix = Number.isFinite(scheduledUnix)
    ? scheduledUnix + (hasLiveBus ? delaySeconds : 0)
    : null;
  const isPast = Number.isFinite(liveBusStopState?.markerAfterIndex) &&
    liveBusStopState.markerAfterIndex >= 0 &&
    stopIndex <= liveBusStopState.markerAfterIndex;
  const isNext = Number.isFinite(liveBusStopState?.nextStopIndex) && stopIndex === liveBusStopState.nextStopIndex;
  const secondsUntil = Number.isFinite(adjustedUnix) ? adjustedUnix - Math.floor(Date.now() / 1000) : null;
  const dueLabel = Number.isFinite(secondsUntil) && secondsUntil > 0
    ? t('journeyStopDueIn', { time: formatDurationMinutes(secondsUntil) })
    : '';
  const delayLabel = hasLiveBus && hasDelaySeconds(leg?.liveBus?.delaySeconds)
    ? formatDelay(leg.liveBus.delaySeconds)
    : '';

  if (isPast) {
    return {
      className: ['is-past', hasLiveBus ? 'is-live' : 'is-scheduled'].join(' '),
      timeLabel: Number.isFinite(adjustedUnix)
        ? formatTimestamp(adjustedUnix)
        : getGtfsClockLabel(stop?.arrivalTime || stop?.departureTime),
      statusLabel: t('journeyStopPassed'),
    };
  }

  return {
    className: [
      isNext ? 'is-next' : '',
      hasLiveBus ? 'is-live' : 'is-scheduled',
      getJourneyStopDelayClassName(delaySeconds),
    ].filter(Boolean).join(' '),
    timeLabel: Number.isFinite(adjustedUnix)
      ? formatTimestamp(adjustedUnix)
      : getGtfsClockLabel(stop?.arrivalTime || stop?.departureTime),
    statusLabel: [delayLabel || (!hasLiveBus ? t('journeyStopScheduled') : ''), dueLabel]
      .filter(Boolean)
      .join(' · '),
  };
}

function getPassageDestinationClassName(baseClassName, destination) {
  const textLength = String(destination || '').trim().length;
  return textLength > 44 ? `${baseClassName} ${baseClassName}--long` : baseClassName;
}

function formatJourneyMoveStart(startTime) {
  const secondsUntilStart = Number(startTime || 0) - Math.floor(Date.now() / 1000);

  if (!Number.isFinite(secondsUntilStart)) {
    return '';
  }

  if (secondsUntilStart <= 30) {
    return t('journeyMoveNow');
  }

  if (secondsUntilStart > 90 * 60) {
    return t('journeyMoveAt', { time: formatTimestamp(startTime) });
  }

  return t('journeyMoveIn', { minutes: Math.max(1, Math.ceil(secondsUntilStart / 60)) });
}

function formatJourneyLegTimeRange(leg) {
  if (!Number.isFinite(Number(leg?.startTime)) || !Number.isFinite(Number(leg?.endTime))) {
    return '';
  }

  return `${formatTimestamp(leg.startTime)}-${formatTimestamp(leg.endTime)}`;
}

function getDefaultJourneyDateTimeValue(date = new Date()) {
  const roundedDate = new Date(date.getTime());
  const minutes = roundedDate.getMinutes();
  roundedDate.setMinutes(Math.ceil(minutes / 5) * 5, 0, 0);

  const year = roundedDate.getFullYear();
  const month = String(roundedDate.getMonth() + 1).padStart(2, '0');
  const day = String(roundedDate.getDate()).padStart(2, '0');
  const hours = String(roundedDate.getHours()).padStart(2, '0');
  const nextMinutes = String(roundedDate.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${nextMinutes}`;
}

function getJourneyTimeInputType(timeMode) {
  return timeMode === JOURNEY_TIME_LAST ? 'date' : 'datetime-local';
}

function normalizeJourneyDateTimeValue(value, timeMode) {
  const fallbackValue = getDefaultJourneyDateTimeValue();
  const normalizedValue = String(value || fallbackValue);

  if (timeMode === JOURNEY_TIME_LAST) {
    return `${normalizedValue.slice(0, 10) || fallbackValue.slice(0, 10)}T23:59`;
  }

  return normalizedValue.includes('T') ? normalizedValue : fallbackValue;
}

function getDateTimeValueAfterMinutes(minutes) {
  return getDefaultJourneyDateTimeValue(new Date(Date.now() + Number(minutes || 0) * 60000));
}

function getJourneyOptionSortScore(option) {
  const summary = option?.summary || {};
  return [
    Number(summary.durationSeconds || 0) + Number(summary.transfers || 0) * 10 * 60 +
      Math.round(Number(summary.walkTimeSeconds || 0) * 0.35) +
      Math.round(Number(summary.waitingTimeSeconds || 0) * 0.2),
    Number(summary.transfers || 0),
    Number(summary.walkTimeSeconds || 0),
    Number(summary.waitingTimeSeconds || 0),
    Number(summary.endTime || 0),
  ];
}

function compareJourneyOptions(firstOption, secondOption) {
  const firstScore = getJourneyOptionSortScore(firstOption);
  const secondScore = getJourneyOptionSortScore(secondOption);

  for (let index = 0; index < firstScore.length; index += 1) {
    if (firstScore[index] !== secondScore[index]) {
      return firstScore[index] - secondScore[index];
    }
  }

  return 0;
}

function getPassageRouteLabel(passage) {
  return formatRouteShortName(passage.routeShortName || passage.routeId);
}

function getPassageDestinationText(passage) {
  if (passage.tripHeadsign) {
    return passage.tripHeadsign;
  }

  if (passage.isRealtime) {
    const routeLabel = getPassageRouteLabel(passage);
    return routeLabel ? t('realtimeRouteFallback', { route: routeLabel }) : t('activeTrip');
  }

  return t('scheduledTrip');
}

function getComparableRouteLabel(routeIdentifier) {
  const routeLabel = formatRouteShortName(routeIdentifier);
  const compactRouteLabel = String(routeLabel || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/\/+$/g, '');

  if (!compactRouteLabel) {
    return '';
  }

  const numericMatch = compactRouteLabel.match(/^0*(\d+)([A-Z]*)$/);

  if (numericMatch) {
    return `${String(Number(numericMatch[1]))}${numericMatch[2] || ''}`;
  }

  return compactRouteLabel;
}

function getComparablePassageRouteLabel(passage) {
  const routeCandidates = [passage.routeShortName, passage.routeId]
    .map(getComparableRouteLabel)
    .filter(Boolean);

  return routeCandidates[0] || '';
}

function normalizePassageText(value) {
  return String(value || '')
    .replaceAll('_', ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function arePassageHeadsignsCompatible(realtimePassage, scheduledPassage) {
  const realtimeHeadsign = normalizePassageText(realtimePassage.tripHeadsign);
  const scheduledHeadsign = normalizePassageText(scheduledPassage.tripHeadsign);

  return !realtimeHeadsign ||
    !scheduledHeadsign ||
    realtimeHeadsign === scheduledHeadsign ||
    realtimeHeadsign.includes(scheduledHeadsign) ||
    scheduledHeadsign.includes(realtimeHeadsign);
}

function isScheduledPassageCoveredByRealtime(scheduledPassage, realtimePassages) {
  const scheduledRouteLabel = getComparablePassageRouteLabel(scheduledPassage);
  const scheduledArrivalTime = getPassageArrivalTime(scheduledPassage);

  if (!scheduledRouteLabel || !scheduledArrivalTime) {
    return false;
  }

  return realtimePassages.some((realtimePassage) => {
    const realtimeArrivalTime = getPassageArrivalTime(realtimePassage);

    return (
      getComparablePassageRouteLabel(realtimePassage) === scheduledRouteLabel &&
      realtimeArrivalTime &&
      Math.abs(realtimeArrivalTime - scheduledArrivalTime) <= STOP_PASSAGE_FUZZY_DEDUPLICATION_SECONDS &&
      arePassageHeadsignsCompatible(realtimePassage, scheduledPassage)
    );
  });
}

function findScheduledPassageForRealtime(realtimePassage, scheduledPassages) {
  const realtimeRouteLabel = getComparablePassageRouteLabel(realtimePassage);
  const realtimeArrivalTime = getPassageArrivalTime(realtimePassage);

  if (!realtimeRouteLabel || !realtimeArrivalTime) {
    return null;
  }

  return scheduledPassages
    .filter((scheduledPassage) => {
      const scheduledArrivalTime = getPassageArrivalTime(scheduledPassage);

      return (
        getComparablePassageRouteLabel(scheduledPassage) === realtimeRouteLabel &&
        scheduledArrivalTime &&
        Math.abs(realtimeArrivalTime - scheduledArrivalTime) <= STOP_PASSAGE_FUZZY_DEDUPLICATION_SECONDS &&
        scheduledPassage.tripHeadsign
      );
    })
    .sort(
      (firstPassage, secondPassage) =>
        Math.abs(getPassageArrivalTime(firstPassage) - realtimeArrivalTime) -
        Math.abs(getPassageArrivalTime(secondPassage) - realtimeArrivalTime)
    )[0] || null;
}

function enrichRealtimePassageFromSchedule(realtimePassage, scheduledPassages) {
  if (realtimePassage.tripHeadsign) {
    return realtimePassage;
  }

  const matchingScheduledPassage = findScheduledPassageForRealtime(realtimePassage, scheduledPassages);

  if (!matchingScheduledPassage?.tripHeadsign) {
    return realtimePassage;
  }

  return {
    ...realtimePassage,
    tripHeadsign: matchingScheduledPassage.tripHeadsign,
    inferredTripHeadsign: true,
  };
}

function buildStopPassageRows(realtimePassages, scheduledPassages, limit = 8) {
  const enrichedRealtimePassages = realtimePassages.map((passage) =>
    enrichRealtimePassageFromSchedule(passage, scheduledPassages)
  );
  const realtimeTripIds = new Set(
    enrichedRealtimePassages.map((passage) => passage.tripId).filter(Boolean).map(String)
  );
  const rows = [
    ...enrichedRealtimePassages.map((passage) => ({
      ...passage,
      isRealtime: true,
      rowKey: `realtime-${passage.vehicleId || passage.tripId}-${passage.stopSequence}`,
    })),
    ...scheduledPassages
      .filter((passage) => {
        if (passage.tripId && realtimeTripIds.has(String(passage.tripId))) {
          return false;
        }

        return !isScheduledPassageCoveredByRealtime(passage, enrichedRealtimePassages);
      })
      .map((passage) => ({
        ...passage,
        isRealtime: false,
        rowKey: `scheduled-${passage.tripId}-${passage.stopSequence}`,
      })),
  ];

  return rows
    .sort((firstPassage, secondPassage) => getPassageArrivalTime(firstPassage) - getPassageArrivalTime(secondPassage))
    .slice(0, Math.max(1, limit));
}

function getStopPageIdFromPathname(pathname = '') {
  const [, pageName, stopId] = pathname.match(/^\/(stop|fermata)\/([^/?#]+)/i) ?? [];
  return pageName && stopId ? decodeURIComponent(stopId) : '';
}

function readStopPageIdFromLocation() {
  if (typeof window === 'undefined') {
    return '';
  }

  return getStopPageIdFromPathname(window.location.pathname);
}

function getStopPagePath(stopId) {
  return `/stop/${encodeURIComponent(String(stopId || ''))}`;
}

function getDateInputValue(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getGtfsServiceDateFromInput(value = '') {
  const normalizedValue = String(value || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)
    ? normalizedValue.replaceAll('-', '')
    : '';
}

function getDateInputValueFromGtfsDate(value = '') {
  const normalizedValue = String(value || '').trim();
  return /^\d{8}$/.test(normalizedValue)
    ? `${normalizedValue.slice(0, 4)}-${normalizedValue.slice(4, 6)}-${normalizedValue.slice(6, 8)}`
    : '';
}

function isDateInputInPast(value = '', todayValue = getDateInputValue()) {
  const normalizedValue = String(value || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(normalizedValue) && normalizedValue < todayValue;
}

function isDateInputAfter(value = '', maxValue = '') {
  const normalizedValue = String(value || '').trim();
  const normalizedMaxValue = String(maxValue || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(normalizedValue) &&
    /^\d{4}-\d{2}-\d{2}$/.test(normalizedMaxValue) &&
    normalizedValue > normalizedMaxValue;
}

function getDateFromInputValue(value = '') {
  const normalizedValue = String(value || '').trim();

  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
    return new Date();
  }

  const [year, month, day] = normalizedValue.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatStopPageDate(value = getDateInputValue()) {
  return new Intl.DateTimeFormat(getLocale(activeLanguage), {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(getDateFromInputValue(value));
}

function getJourneyPlaceFromUrlParams(searchParams, prefix) {
  const latitude = Number(searchParams.get(`${prefix}Lat`));
  const longitude = Number(searchParams.get(`${prefix}Lng`));

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return {
    id: `shared-${prefix}`,
    label: searchParams.get(`${prefix}Label`) || (prefix === 'from' ? t('journeyOrigin') : t('journeyDestination')),
    name: searchParams.get(`${prefix}Label`) || '',
    latitude,
    longitude,
  };
}

function getJourneyUrlTimeParams(searchParams) {
  const timeMode = searchParams.get('timeMode') || JOURNEY_TIME_DEPART_NOW;
  const normalizedTimeMode = [JOURNEY_TIME_DEPART_NOW, JOURNEY_TIME_DEPART_AT, JOURNEY_TIME_ARRIVE_BY, JOURNEY_TIME_LAST].includes(timeMode)
    ? timeMode
    : JOURNEY_TIME_DEPART_NOW;
  const time = Number(searchParams.get('time'));

  return {
    timeMode: normalizedTimeMode,
    time: Number.isFinite(time) ? time : null,
  };
}

function getJourneyRequestSignature(fromPlace, toPlace, timeParams) {
  if (!fromPlace || !toPlace || !timeParams?.timeMode) {
    return '';
  }

  const formatCoordinate = (value) => Number(value).toFixed(6);

  return [
    formatCoordinate(fromPlace.latitude),
    formatCoordinate(fromPlace.longitude),
    formatCoordinate(toPlace.latitude),
    formatCoordinate(toPlace.longitude),
    timeParams.timeMode,
    timeParams.time || '',
  ].join('|');
}

function normalizeStoredJourneyPlace(place) {
  const latitude = Number(place?.latitude);
  const longitude = Number(place?.longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  const label = String(place?.label || place?.name || '').trim();

  return {
    id: String(place?.id || `${longitude},${latitude}`),
    label: label || t('addressResult'),
    name: String(place?.name || label || '').trim(),
    latitude,
    longitude,
  };
}

function isPersistableJourneyPlace(place) {
  const normalizedPlace = normalizeStoredJourneyPlace(place);
  return Boolean(normalizedPlace && normalizedPlace.id !== 'current-location');
}

function normalizeJourneyTimeMode(timeMode) {
  return [JOURNEY_TIME_DEPART_NOW, JOURNEY_TIME_DEPART_AT, JOURNEY_TIME_ARRIVE_BY, JOURNEY_TIME_LAST].includes(timeMode)
    ? timeMode
    : JOURNEY_TIME_DEPART_NOW;
}

function readStoredRecentJourneys() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(JOURNEY_RECENT_STORAGE_KEY) || '[]');
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((journey) => {
        const from = normalizeStoredJourneyPlace(journey?.from);
        const to = normalizeStoredJourneyPlace(journey?.to);

        if (!from || !to) {
          return null;
        }

        const timeMode = normalizeJourneyTimeMode(journey?.timeMode);
        const time = Number(journey?.time);
        const savedAt = Number(journey?.savedAt);

        return {
          id: String(journey?.id || `${from.id}-${to.id}-${timeMode}-${Number.isFinite(time) ? time : ''}`),
          from,
          to,
          timeMode,
          time: Number.isFinite(time) ? time : null,
          savedAt: Number.isFinite(savedAt) ? savedAt : 0,
          summary: {
            durationSeconds: Number(journey?.summary?.durationSeconds) || 0,
            routes: Array.isArray(journey?.summary?.routes)
              ? journey.summary.routes.map(formatRouteShortName).filter(Boolean).slice(0, 3)
              : [],
          },
        };
      })
      .filter(Boolean)
      .slice(0, JOURNEY_RECENT_LIMIT);
  } catch {
    return [];
  }
}

function writeStoredRecentJourneys(journeys) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(JOURNEY_RECENT_STORAGE_KEY, JSON.stringify(journeys.slice(0, JOURNEY_RECENT_LIMIT)));
  } catch {
    // Local storage can fail in private browsing; recent journeys are an optional convenience.
  }
}

function buildRecentJourneyEntry(fromPlace, toPlace, timeParams, option) {
  if (!isPersistableJourneyPlace(fromPlace) || !isPersistableJourneyPlace(toPlace)) {
    return null;
  }

  const from = normalizeStoredJourneyPlace(fromPlace);
  const to = normalizeStoredJourneyPlace(toPlace);
  const timeMode = normalizeJourneyTimeMode(timeParams?.timeMode);
  const time = Number(timeParams?.time);
  const routes = (option?.legs || [])
    .filter((leg) => leg.mode !== 'WALK')
    .map((leg) => formatRouteShortName(leg.route || leg.routeId))
    .filter(Boolean);

  const signatureParts = [
    from.latitude.toFixed(5),
    from.longitude.toFixed(5),
    to.latitude.toFixed(5),
    to.longitude.toFixed(5),
    timeMode,
    Number.isFinite(time) ? time : '',
  ];

  return {
    id: signatureParts.join(':'),
    from,
    to,
    timeMode,
    time: Number.isFinite(time) ? time : null,
    savedAt: Math.floor(Date.now() / 1000),
    summary: {
      durationSeconds: Number(option?.summary?.durationSeconds) || 0,
      routes: [...new Set(routes)].slice(0, 3),
    },
  };
}

function getFavoriteJourneyStorageId(from, to) {
  if (!from || !to) {
    return '';
  }

  return [
    from.latitude.toFixed(5),
    from.longitude.toFixed(5),
    to.latitude.toFixed(5),
    to.longitude.toFixed(5),
  ].join(':');
}

function getJourneySummaryFromOption(option) {
  const routes = (option?.legs || [])
    .filter((leg) => leg.mode !== 'WALK')
    .map((leg) => formatRouteShortName(leg.route || leg.routeId))
    .filter(Boolean);

  return {
    durationSeconds: Number(option?.summary?.durationSeconds) || 0,
    routes: [...new Set(routes)].slice(0, 3),
  };
}

function normalizeFavoriteJourney(journey) {
  const from = normalizeStoredJourneyPlace(journey?.from);
  const to = normalizeStoredJourneyPlace(journey?.to);

  if (!from || !to || from.id === 'current-location' || to.id === 'current-location') {
    return null;
  }

  const id = getFavoriteJourneyStorageId(from, to);

  if (!id) {
    return null;
  }

  const savedAt = Number(journey?.savedAt);

  return {
    id,
    from,
    to,
    savedAt: Number.isFinite(savedAt) ? savedAt : Math.floor(Date.now() / 1000),
    summary: {
      durationSeconds: Number(journey?.summary?.durationSeconds) || 0,
      routes: Array.isArray(journey?.summary?.routes)
        ? journey.summary.routes.map(formatRouteShortName).filter(Boolean).slice(0, 3)
        : [],
    },
  };
}

function buildFavoriteJourneyEntry(fromPlace, toPlace, option) {
  if (!isPersistableJourneyPlace(fromPlace) || !isPersistableJourneyPlace(toPlace)) {
    return null;
  }

  return normalizeFavoriteJourney({
    from: fromPlace,
    to: toPlace,
    savedAt: Math.floor(Date.now() / 1000),
    summary: getJourneySummaryFromOption(option),
  });
}

function readStoredFavoriteJourneys() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(FAVORITE_JOURNEYS_STORAGE_KEY) || '[]');

    if (!Array.isArray(parsed)) {
      return [];
    }

    const journeysById = new Map();
    parsed.forEach((journey) => {
      const normalizedJourney = normalizeFavoriteJourney(journey);
      if (normalizedJourney) {
        journeysById.set(normalizedJourney.id, normalizedJourney);
      }
    });

    return [...journeysById.values()].slice(0, FAVORITE_JOURNEYS_LIMIT);
  } catch {
    return [];
  }
}

function writeStoredFavoriteJourneys(journeys) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      FAVORITE_JOURNEYS_STORAGE_KEY,
      JSON.stringify(journeys.slice(0, FAVORITE_JOURNEYS_LIMIT))
    );
  } catch {
    // Favorite journeys are an optional device-local convenience.
  }
}

function readStoredFavoriteRoutes() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(FAVORITE_ROUTES_STORAGE_KEY) || '[]');
    return Array.isArray(parsed)
      ? [...new Set(parsed.map(formatRouteShortName).filter(Boolean))]
      : [];
  } catch {
    return [];
  }
}

function writeStoredFavoriteRoutes(routeLabels) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      FAVORITE_ROUTES_STORAGE_KEY,
      JSON.stringify([...new Set(routeLabels.map(formatRouteShortName).filter(Boolean))])
    );
  } catch {
    // Favorite routes are a local convenience and can fail silently in restricted storage modes.
  }
}

function normalizeFavoriteStop(stop) {
  const latitude = Number(stop?.latitude);
  const longitude = Number(stop?.longitude);
  const id = String(stop?.id || '').trim();

  if (!id || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  const name = String(stop?.name || t('stopMarkerLabel', { name: id })).trim();

  return {
    id,
    name,
    latitude,
    longitude,
    routeShortNames: sortRouteLabels(stop?.routeShortNames || []),
    routeIds: Array.isArray(stop?.routeIds) ? stop.routeIds.map(String).filter(Boolean) : [],
  };
}

function readStoredFavoriteStops() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(FAVORITE_STOPS_STORAGE_KEY) || '[]');
    if (!Array.isArray(parsed)) {
      return [];
    }

    const stopsById = new Map();
    parsed.forEach((stop) => {
      const normalizedStop = normalizeFavoriteStop(stop);
      if (normalizedStop) {
        stopsById.set(normalizedStop.id, normalizedStop);
      }
    });

    return [...stopsById.values()].slice(0, FAVORITE_STOPS_LIMIT);
  } catch {
    return [];
  }
}

function writeStoredFavoriteStops(stops) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(FAVORITE_STOPS_STORAGE_KEY, JSON.stringify(stops.slice(0, FAVORITE_STOPS_LIMIT)));
  } catch {
    // Favorite stops are optional and remain device-local.
  }
}

function getFavoritePlaceStorageId(place) {
  const latitude = Number(place?.latitude);
  const longitude = Number(place?.longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return '';
  }

  const label = String(place?.label || place?.name || '').trim().toLowerCase();
  return [
    longitude.toFixed(5),
    latitude.toFixed(5),
    label,
  ].join(':');
}

function normalizeFavoritePlace(place) {
  const normalizedPlace = normalizeStoredJourneyPlace(place);

  if (!normalizedPlace || normalizedPlace.id === 'current-location') {
    return null;
  }

  const storageId = getFavoritePlaceStorageId(normalizedPlace);

  if (!storageId) {
    return null;
  }

  return {
    ...normalizedPlace,
    id: storageId,
    sourceId: String(place?.sourceId || place?.id || '').trim(),
    savedAt: Number.isFinite(Number(place?.savedAt)) ? Number(place.savedAt) : Math.floor(Date.now() / 1000),
  };
}

function readStoredFavoritePlaces() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(FAVORITE_PLACES_STORAGE_KEY) || '[]');

    if (!Array.isArray(parsed)) {
      return [];
    }

    const placesById = new Map();
    parsed.forEach((place) => {
      const normalizedPlace = normalizeFavoritePlace(place);
      if (normalizedPlace) {
        placesById.set(normalizedPlace.id, normalizedPlace);
      }
    });

    return [...placesById.values()].slice(0, FAVORITE_PLACES_LIMIT);
  } catch {
    return [];
  }
}

function writeStoredFavoritePlaces(places) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(FAVORITE_PLACES_STORAGE_KEY, JSON.stringify(places.slice(0, FAVORITE_PLACES_LIMIT)));
  } catch {
    // Favorite places are a local convenience and can fail in restricted storage modes.
  }
}

function getRouteFavoriteKey(routeOptionOrLabel) {
  return formatRouteShortName(
    typeof routeOptionOrLabel === 'object'
      ? routeOptionOrLabel?.shortName || routeOptionOrLabel?.id
      : routeOptionOrLabel
  );
}

function StopTimetablePage({
  appName,
  errorMessage,
  hasArrivalCountdown,
  hasRealtimeArrivalPredictions,
  hasScheduleAdherence,
  isFavorite,
  isLoading,
  lastLoadedAt,
  maxDate,
  onBack,
  onDateChange,
  onToggleFavorite,
  onRefresh,
  onToday,
  realtimeCount,
  rows,
  scheduledCount,
  selectedDate,
  stop,
  stopId,
}) {
  const stopName = stop?.name || t('stopMarkerLabel', { name: stopId });
  const todayValue = getDateInputValue();
  const isSelectedToday = selectedDate === todayValue;
  const stopRouteLabels = sortRouteLabels([
    ...(stop?.routeShortNames || []),
    ...rows.map(getPassageRouteLabel),
  ]);

  return (
    <div className="app-shell stop-page-shell">
      <main className="stop-page">
        <header className="stop-page__header">
          <button className="button button--ghost stop-page__back" onClick={onBack} type="button">
            {t('backToMap')}
          </button>
          <span className="panel-heading__eyebrow">TPL TRACKER</span>
          <div className="stop-page__title-row">
            <h1>{stopName}</h1>
            <button
              aria-label={isFavorite ? t('removeFavoriteStop') : t('addFavoriteStop')}
              aria-pressed={isFavorite}
              className={`favorite-toggle ${isFavorite ? 'is-active' : ''}`}
              onClick={() => onToggleFavorite?.(stop)}
              type="button"
            >
              {isFavorite ? '★' : '☆'}
            </button>
          </div>
          <p>
            {t('stopLineSummary', { stopId, routes: '' })} · {formatStopPageDate(selectedDate)}
          </p>
          <div className="stop-page__date-controls" aria-label={t('stopDateSelector')}>
            <button
              className={isSelectedToday ? 'is-active' : ''}
              disabled={isSelectedToday}
              onClick={onToday}
              type="button"
            >
              {t('today')}
            </button>
            <label>
              <span>{t('date')}</span>
              <input
                aria-label={t('stopDateSelector')}
                max={maxDate || undefined}
                min={todayValue}
                onChange={(event) => onDateChange(event.target.value)}
                type="date"
                value={selectedDate}
              />
            </label>
            {maxDate ? (
              <small>{t('gtfsCoverageUntil', { date: formatStopPageDate(maxDate) })}</small>
            ) : null}
          </div>
          {stopRouteLabels.length > 0 ? (
            <div className="stop-page__routes" aria-label={`Linee ${stopRouteLabels.join(', ')}`}>
              <span>Linee</span>
              {stopRouteLabels.map((routeLabel) => (
                <strong className="stop-page__route-chip" key={routeLabel} style={getRouteBadgeStyle(routeLabel)}>
                  {routeLabel}
                </strong>
              ))}
            </div>
          ) : null}
          <div className="stop-page__stats" aria-label={t('stopScheduleSummaryLabel')}>
            <span>{t('passagesToday', { count: rows.length })}</span>
            <span>{realtimeCount} {t('realtime')}</span>
            <span>{scheduledCount} {t('scheduledPlural')}</span>
            {lastLoadedAt ? <span>sync {formatTime(lastLoadedAt)}</span> : null}
          </div>
        </header>

        <section className="stop-page__panel" aria-label={t('stopScheduleAria', { stopId })}>
          <div className="stop-page__panel-header">
            <div>
              <span>{appName}</span>
              <strong>{t('dailySchedule')}</strong>
            </div>
            <button className="button button--ghost" disabled={isLoading} onClick={onRefresh} type="button">
              {isLoading ? t('refreshing') : t('refresh')}
            </button>
          </div>

          {errorMessage ? <p className="stop-page__notice">{errorMessage}</p> : null}
          {isLoading && rows.length === 0 ? <p className="stop-page__notice">{t('loadingStopSchedule')}</p> : null}

          {rows.length > 0 ? (
            <div className="stop-page__list">
              {rows.map((passage) => {
                const routeLabel = getPassageRouteLabel(passage);
                const delayToneClassName = passage.isRealtime && hasScheduleAdherence && hasDelaySeconds(passage.delaySeconds)
                  ? getDelayToneClassName(passage.delaySeconds)
                  : '';

                return (
                  <article
                    className={`stop-page__row ${
                      passage.isRealtime ? 'stop-page__row--realtime' : 'stop-page__row--scheduled'
                    }`}
                    key={passage.rowKey}
                  >
                    <span className="route-badge" style={getRouteBadgeStyle(routeLabel)}>
                      {routeLabel}
                    </span>
                    <div className="stop-page__row-main">
                      <strong className={getPassageDestinationClassName('stop-page__destination', getPassageDestinationText(passage))}>
                        {getPassageDestinationText(passage)}
                      </strong>
                      <span className={delayToneClassName || undefined}>
                        {passage.isRealtime
                          ? hasScheduleAdherence && hasDelaySeconds(passage.delaySeconds)
                            ? formatDelay(passage.delaySeconds)
                            : 'Realtime'
                          : t('scheduledTime')}
                      </span>
                    </div>
                    <div className="stop-page__row-side">
                      <strong>
                        {formatPredictedTime(
                          passage.isRealtime && hasRealtimeArrivalPredictions ? passage.predictedTime : 0,
                          passage.scheduledTime
                        )}
                      </strong>
                      <span>
                        {passage.isRealtime
                          ? hasArrivalCountdown
                            ? formatArrivalCountdown(getPassageArrivalTime(passage))
                            : t('realtimeBus')
                          : t('scheduled')}
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : !isLoading ? (
            <p className="stop-page__notice">{t('noPassagesToday')}</p>
          ) : null}
        </section>
      </main>
    </div>
  );
}

function getBusSubtitle(bus) {
  if (bus.tripHeadsign) {
    return bus.tripHeadsign;
  }

  if (bus.routeLongName) {
    return bus.routeLongName;
  }

  return getDirectionLabel(bus.directionId);
}

function getDirectionLabel(directionId) {
  if (directionId === 0) {
    return t('routeA');
  }

  if (directionId === 1) {
    return t('routeB');
  }

  return t('routeUnavailable');
}

function fitMapToBuses(mapInstance, buses) {
  if (!mapInstance || buses.length === 0) {
    return;
  }

  if (buses.length === 1) {
    mapInstance.flyTo({
      center: [buses[0].longitude, buses[0].latitude],
      zoom: Math.max(mapInstance.getZoom(), 14),
      essential: true,
    });
    return;
  }

  const bounds = new mapboxgl.LngLatBounds();
  buses.forEach((bus) => bounds.extend([bus.longitude, bus.latitude]));
  mapInstance.fitBounds(bounds, {
    padding: 72,
    maxZoom: 14.5,
    duration: 1000,
  });
}

function fitMapToStops(mapInstance, stops) {
  if (!mapInstance || stops.length === 0) {
    return;
  }

  if (stops.length === 1) {
    mapInstance.easeTo({
      center: [stops[0].longitude, stops[0].latitude],
      zoom: Math.max(mapInstance.getZoom(), 15),
      essential: true,
      duration: 700,
    });
    return;
  }

  const bounds = new mapboxgl.LngLatBounds();
  stops.forEach((stop) => bounds.extend([stop.longitude, stop.latitude]));
  mapInstance.fitBounds(bounds, {
    padding: 72,
    maxZoom: 14,
    duration: 900,
  });
}

function fitMapToJourney(mapInstance, features = []) {
  const coordinates = features.flatMap((feature) => {
    const geometry = feature?.geometry;
    if (geometry?.type === 'Polygon') {
      return [];
    }
    return geometry?.type === 'Point' ? [geometry.coordinates] : geometry?.coordinates || [];
  });

  if (!mapInstance || coordinates.length === 0) {
    return;
  }

  if (coordinates.length === 1) {
    mapInstance.flyTo({ center: coordinates[0], zoom: 15, duration: 700, essential: true });
    return;
  }

  const bounds = new mapboxgl.LngLatBounds();
  coordinates.forEach((coordinate) => bounds.extend(coordinate));
  mapInstance.fitBounds(bounds, {
    padding: { top: 110, right: 90, bottom: 110, left: 430 },
    maxZoom: 15,
    duration: 900,
  });
}

function fitMapToRouteFromStop(mapInstance, focusStop, routeStops = []) {
  const focusLongitude = Number(focusStop?.longitude);
  const focusLatitude = Number(focusStop?.latitude);

  if (!mapInstance || !Number.isFinite(focusLongitude) || !Number.isFinite(focusLatitude)) {
    return false;
  }

  const focusPoint = {
    latitude: focusLatitude,
    longitude: focusLongitude,
  };
  const nearbyStops = routeStops
    .map((stop) => ({
      ...stop,
      distanceMeters: measureDistanceMeters(focusPoint, {
        latitude: stop.latitude,
        longitude: stop.longitude,
      }),
    }))
    .filter((stop) => Number.isFinite(stop.distanceMeters) && stop.distanceMeters <= 1800)
    .sort((firstStop, secondStop) => firstStop.distanceMeters - secondStop.distanceMeters)
    .slice(0, 12);

  if (nearbyStops.length < 2) {
    mapInstance.easeTo({
      center: [focusLongitude, focusLatitude],
      zoom: Math.min(Math.max(mapInstance.getZoom(), 13.4), 14.2),
      essential: true,
      duration: 800,
    });
    return true;
  }

  const bounds = new mapboxgl.LngLatBounds();
  bounds.extend([focusLongitude, focusLatitude]);
  nearbyStops.forEach((stop) => bounds.extend([stop.longitude, stop.latitude]));
  mapInstance.fitBounds(bounds, {
    padding: {
      top: 120,
      right: 86,
      bottom: 160,
      left: 86,
    },
    maxZoom: 14.2,
    duration: 850,
  });

  return true;
}

function fitMapToBusAndStop(mapInstance, bus, stop) {
  if (!mapInstance || !bus) {
    return;
  }

  const hasStop =
    stop &&
    Number.isFinite(Number(stop.longitude)) &&
    Number.isFinite(Number(stop.latitude));

  if (!hasStop) {
    mapInstance.easeTo({
      center: [bus.longitude, bus.latitude],
      zoom: Math.min(Math.max(mapInstance.getZoom(), 13), 14),
      essential: true,
      duration: 750,
    });
    return;
  }

  const busPoint = { latitude: bus.latitude, longitude: bus.longitude };
  const stopPoint = { latitude: Number(stop.latitude), longitude: Number(stop.longitude) };
  const distanceMeters = measureDistanceMeters(busPoint, stopPoint);

  if (distanceMeters < 35) {
    mapInstance.easeTo({
      center: [
        (busPoint.longitude + stopPoint.longitude) / 2,
        (busPoint.latitude + stopPoint.latitude) / 2,
      ],
      zoom: Math.min(Math.max(mapInstance.getZoom(), 13.2), 14.4),
      essential: true,
      duration: 750,
    });
    return;
  }

  const bounds = new mapboxgl.LngLatBounds();
  bounds.extend([busPoint.longitude, busPoint.latitude]);
  bounds.extend([stopPoint.longitude, stopPoint.latitude]);

  mapInstance.fitBounds(bounds, {
    padding: {
      top: 130,
      right: 86,
      bottom: 170,
      left: 86,
    },
    maxZoom: 14.2,
    duration: 850,
  });
}

function getGeolocationMessage(error, permissionState = 'unknown') {
  if (!error) {
    return '';
  }

  const isSafari = isSafariBrowser();
  const isAppleMobile = isAppleMobileBrowser();

  if (error.code === 1) {
    if (permissionState === 'granted') {
      return isAppleMobile
        ? t('locationGrantedNoPositionApple')
        : t('locationGrantedNoPosition');
    }

    return isSafari
      ? t('locationPermissionSafari')
      : t('locationPermissionDenied');
  }

  if (error.code === 2) {
    return t('locationUnavailable');
  }

  if (error.code === 3) {
    return t('locationTimeout');
  }

  return t('locationFailed');
}

function normalizeGeocodingFeature(feature) {
  const routablePoint = feature?.properties?.coordinates?.routable_points?.[0];
  const longitude = Number(
    routablePoint?.longitude ?? feature?.properties?.coordinates?.longitude ?? feature?.geometry?.coordinates?.[0]
  );
  const latitude = Number(
    routablePoint?.latitude ?? feature?.properties?.coordinates?.latitude ?? feature?.geometry?.coordinates?.[1]
  );

  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
    return null;
  }

  const featureType = String(feature?.properties?.feature_type || '');
  const isPointOfInterest = featureType === 'poi' || featureType === 'category';
  const primaryName = String(
    isPointOfInterest
      ? feature?.properties?.name || feature?.text || feature?.properties?.full_address
      : feature?.properties?.full_address || feature?.properties?.name || feature?.text || ''
  ).trim();
  const secondaryName = String(
    isPointOfInterest
      ? feature?.properties?.full_address || feature?.properties?.place_formatted || feature?.place_name || ''
      : feature?.properties?.place_formatted || feature?.place_name || ''
  ).trim();
  const primaryNameLower = primaryName.toLowerCase();
  const secondaryNameLower = secondaryName.toLowerCase();
  const label =
    secondaryName && primaryName && !primaryNameLower.includes(secondaryNameLower)
      ? `${primaryName}, ${secondaryName}`
      : primaryName || secondaryName;

  return {
    id: feature?.properties?.mapbox_id || feature?.id || `${longitude},${latitude}`,
    label: label || t('addressResult'),
    longitude,
    latitude,
    featureType,
    fullText: `${primaryName} ${secondaryName} ${feature?.properties?.context?.place?.name || ''} ${
      feature?.properties?.context?.region?.name || ''
    }`.toLowerCase(),
  };
}

function rankGeocodingResults(results, searchText, referencePoint, bounds) {
  const query = String(searchText || '').trim().toLowerCase();
  const queryTokens = query.split(/\s+/).filter(Boolean);
  const reference = Array.isArray(referencePoint) && referencePoint.length === 2
    ? { longitude: Number(referencePoint[0]), latitude: Number(referencePoint[1]) }
    : null;

  return [...results].sort((firstResult, secondResult) => {
    function score(result) {
      const text = `${result.label || ''} ${result.fullText || ''}`.toLowerCase();
      const isInBounds = isPointInsideBounds(result.longitude, result.latitude, bounds);
      const distancePenalty = reference && Number.isFinite(reference.longitude) && Number.isFinite(reference.latitude)
        ? Math.min(
            18,
            measureDistanceMeters(reference, {
              longitude: result.longitude,
              latitude: result.latitude,
            }) / 1000
          )
        : 0;
      const queryMatchScore = queryTokens.reduce(
        (total, token) => total + (text.includes(token) ? 3 : 0),
        0
      );
      const parmaScore = /\bparma\b|provincia di parma|emilia[- ]romagna/.test(text) ? 10 : 0;
      const poiScore = result.featureType === 'poi' || result.featureType === 'category' ? 2 : 0;

      return (isInBounds ? 40 : -40) + parmaScore + queryMatchScore + poiScore - distancePenalty;
    }

    return score(secondResult) - score(firstResult);
  });
}

function buildJourneyFeatures(legs = []) {
  const focusFeature = {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [[[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]]],
    },
    properties: {
      id: 'journey-focus',
      mode: 'FOCUS',
    },
  };
  const lineFeatures = legs
    .filter((leg) => Array.isArray(leg.coordinates) && leg.coordinates.length > 1)
    .map((leg) => ({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: leg.coordinates },
      properties: {
        id: leg.id,
        mode: leg.mode,
        route: leg.route,
        routeId: leg.routeId,
        color: leg.color,
      },
    }));
  const stopFeatures = legs.flatMap((leg) => {
    if (leg.mode === 'WALK') {
      return [];
    }

    return (leg.stops || []).map((stop, stopIndex, stops) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [Number(stop.longitude), Number(stop.latitude)] },
      properties: {
        id: `${leg.id}-stop-${stop.id || stopIndex}`,
        mode: 'STOP',
        route: leg.route,
        routeId: leg.routeId,
        color: leg.color,
        name: stop.name,
        stopNumber: stopIndex + 1,
        isEndpoint: stopIndex === 0 || stopIndex === stops.length - 1,
      },
    })).filter((feature) => feature.geometry.coordinates.every(Number.isFinite));
  });

  return [focusFeature, ...lineFeatures, ...stopFeatures];
}

function getJourneyOptionSignature(option) {
  return (option?.legs || [])
    .filter((leg) => leg.mode !== 'WALK')
    .map((leg) => `${leg.routeId || leg.route}:${leg.from?.stopId || ''}:${leg.to?.stopId || ''}`)
    .join('|');
}

async function getWalkingDirections(leg) {
  const coordinates = Array.isArray(leg.coordinates) ? leg.coordinates : [];
  const from = coordinates[0];
  const to = coordinates[coordinates.length - 1];

  if (leg.mode !== 'WALK' || !from?.every(Number.isFinite) || !to?.every(Number.isFinite)) {
    return leg;
  }

  const cacheKey = `${from.join(',')};${to.join(',')}`;
  if (!walkingDirectionsCache.has(cacheKey)) {
    walkingDirectionsCache.set(cacheKey, (async () => {
      const requestOptions = typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
        ? { signal: AbortSignal.timeout(2500) }
        : {};
      const response = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/walking/${cacheKey}?geometries=geojson&overview=full&steps=false&access_token=${MAPBOX_TOKEN}`,
        requestOptions
      );
      const data = response.ok ? await response.json() : null;
      return data?.routes?.[0] || null;
    })().catch(() => null));
  }

  const route = await walkingDirectionsCache.get(cacheKey);
  return route?.geometry?.coordinates?.length
    ? { ...leg, coordinates: route.geometry.coordinates, distanceMeters: Math.round(route.distance || leg.distanceMeters) }
    : leg;
}

function getJourneyPlaceName(place, fallbackKey) {
  const name = String(place?.name || '').trim();
  const normalizedName = name.toLowerCase();

  if (!name || normalizedName === 'origin') {
    return t(fallbackKey === 'destination' ? 'journeyDestination' : 'journeyOrigin');
  }

  if (normalizedName === 'destination') {
    return t('journeyDestination');
  }

  return name;
}

function busMatchesJourneyLeg(bus, leg) {
  if (!bus || !leg || leg.mode === 'WALK') {
    return false;
  }

  if (leg.tripId && bus.tripId === leg.tripId) {
    return true;
  }

  const legRouteCandidates = new Set(getRouteLookupCandidates(leg.routeId, leg.route));
  return getRouteLookupCandidates(bus.routeId, bus.routeShortName).some((routeKey) =>
    legRouteCandidates.has(routeKey)
  );
}

function busMatchesJourneyLiveBus(bus, leg) {
  const liveBus = leg?.liveBus;

  if (!bus || !liveBus || leg?.mode === 'WALK') {
    return false;
  }

  const comparablePairs = [
    [bus.id, liveBus.id],
    [bus.label, liveBus.label],
    [bus.tripId, liveBus.tripId],
  ];

  return comparablePairs.some(([leftValue, rightValue]) => {
    const left = String(leftValue || '').trim();
    const right = String(rightValue || '').trim();
    return left && right && left === right;
  });
}

function busMatchesActiveJourneyLeg(bus, leg) {
  if (!bus || !leg || leg.mode === 'WALK') {
    return false;
  }

  return busMatchesJourneyLiveBus(bus, leg) || Boolean(leg.tripId && bus.tripId === leg.tripId);
}

function buildJourneyStopMarker(place, variant) {
  const element = document.createElement('div');
  const isBoarding = variant === 'boarding';
  const isTransfer = variant === 'transfer';
  element.className = `journey-stop-marker journey-stop-marker--${variant}`;
  const dot = document.createElement('span');
  dot.className = 'journey-stop-marker__dot';
  dot.setAttribute('aria-hidden', 'true');
  const label = document.createElement('span');
  label.className = 'journey-stop-marker__label';
  const caption = document.createElement('small');
  caption.textContent = isTransfer
    ? t('journeyTransferHere')
    : isBoarding
      ? t('journeyBoardHere')
      : t('journeyLeaveHere');
  const stopName = document.createElement('strong');
  stopName.textContent = getJourneyPlaceName(place, isBoarding ? 'origin' : 'destination');
  label.append(caption, stopName);

  if (isTransfer && place?.nextRoute) {
    const nextLine = document.createElement('em');
    nextLine.textContent = t('journeyTakeLine', { line: place.nextRoute });
    label.append(nextLine);
  }

  element.append(dot, label);
  return element;
}

function requestCurrentPosition(options) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

async function getGeolocationPermissionState() {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return 'unknown';
  }

  try {
    const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
    return permissionStatus.state;
  } catch {
    return 'unknown';
  }
}

function normalizeAdminColorInput(value) {
  const sanitizedValue = String(value || '').replace('#', '').trim();
  return /^[0-9a-fA-F]{6}$/.test(sanitizedValue) ? `#${sanitizedValue.toUpperCase()}` : '';
}

function buildAdminRouteRows(routes, routeColors, routeCategories, query, categoryFilter) {
  const routeMap = new Map();

  routes.forEach((route) => {
    const shortName = formatRouteShortName(route.shortName || route.id);
    const routeKey = shortName || route.id;
    routeMap.set(routeKey, {
      id: route.id,
      key: routeKey,
      shortName: routeKey,
      longName: route.longName || `${routeKey}`,
      color: routeColors[routeKey] || routeColors[route.id] || '',
      category: routeCategories[routeKey] || routeCategories[route.id] || '',
    });
  });

  Object.entries(routeColors).forEach(([routeKey, color]) => {
    if (!routeMap.has(routeKey)) {
      routeMap.set(routeKey, {
        id: routeKey,
        key: routeKey,
        shortName: routeKey,
        longName: `${routeKey}`,
        color,
        category: routeCategories[routeKey] || '',
      });
    }
  });

  Object.entries(routeCategories).forEach(([routeKey, category]) => {
    if (!routeMap.has(routeKey)) {
      routeMap.set(routeKey, {
        id: routeKey,
        key: routeKey,
        shortName: routeKey,
        longName: `${routeKey}`,
        color: routeColors[routeKey] || '',
        category,
      });
    }
  });

  return [...routeMap.values()]
    .filter((route) => {
      const searchableText = `${route.key} ${route.id} ${route.longName}`.toLowerCase();
      const matchesQuery = !query || searchableText.includes(query.toLowerCase());
      const matchesCategory = !categoryFilter || route.category === categoryFilter;
      return matchesQuery && matchesCategory;
    })
    .sort((firstRoute, secondRoute) =>
      firstRoute.shortName.localeCompare(secondRoute.shortName, 'it', { numeric: true })
    );
}

function AdminConfigPanel() {
  const [adminToken, setAdminToken] = useState(() =>
    typeof window === 'undefined' ? '' : window.localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) || ''
  );
  const [tokenInput, setTokenInput] = useState(adminToken);
  const [publicConfig, setPublicConfig] = useState(null);
  const [catalogRoutes, setCatalogRoutes] = useState([]);
  const [routeColors, setRouteColors] = useState({});
  const [routeCategories, setRouteCategories] = useState({});
  const [routeQuery, setRouteQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    async function loadAdminBootstrap() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const [configResponse, catalogResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/app-config`),
          axios.get(`${API_BASE_URL}/api/transit/catalog`),
        ]);

        if (isCancelled) {
          return;
        }

        const nextPublicConfig = normalizePublicTransitAppConfig(configResponse.data || {});
        setPublicConfig(nextPublicConfig);
        setCatalogRoutes(catalogResponse.data?.routes || []);
        setRouteColors(nextPublicConfig.routeColors?.routes || {});
        setRouteCategories(nextPublicConfig.routeCategories?.routes || {});
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(error.response?.data?.error || 'Impossibile caricare la configurazione admin');
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    loadAdminBootstrap();

    return () => {
      isCancelled = true;
    };
  }, []);

  async function unlockAdmin(event) {
    event.preventDefault();
    const nextToken = tokenInput.trim();

    if (!nextToken) {
      setErrorMessage('Inserisci il token admin');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/config`, {
        headers: {
          Authorization: `Bearer ${nextToken}`,
        },
      });
      const editableConfig = response.data?.editableConfig || {};
      const nextPublicConfig = normalizePublicTransitAppConfig(response.data?.publicConfig || {});
      setAdminToken(nextToken);
      setPublicConfig(nextPublicConfig);
      setRouteColors(editableConfig.routeColors?.routes || nextPublicConfig.routeColors?.routes || {});
      setRouteCategories(editableConfig.routeCategories?.routes || nextPublicConfig.routeCategories?.routes || {});
      window.localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, nextToken);
      setStatusMessage('Accesso admin attivo');
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Token admin non valido');
    } finally {
      setIsLoading(false);
    }
  }

  function updateRouteColor(routeKey, nextColor) {
    setRouteColors((currentColors) => ({
      ...currentColors,
      [routeKey]: nextColor,
    }));
  }

  function updateRouteCategory(routeKey, nextCategory) {
    setRouteCategories((currentCategories) => ({
      ...currentCategories,
      [routeKey]: nextCategory,
    }));
  }

  function resetAdminChanges() {
    setRouteColors(publicConfig?.routeColors?.routes || {});
    setRouteCategories(publicConfig?.routeCategories?.routes || {});
    setStatusMessage('Modifiche locali annullate');
  }

  async function saveAdminConfig() {
    const normalizedRouteColors = {};
    const normalizedRouteCategories = {};

    Object.entries(routeColors).forEach(([routeKey, routeColor]) => {
      const normalizedColor = normalizeAdminColorInput(routeColor);
      if (routeKey.trim() && normalizedColor) {
        normalizedRouteColors[routeKey.trim()] = normalizedColor;
      }
    });

    Object.entries(routeCategories).forEach(([routeKey, routeCategory]) => {
      if (routeKey.trim() && ['urban', 'night', 'interurban'].includes(routeCategory)) {
        normalizedRouteCategories[routeKey.trim()] = routeCategory;
      }
    });

    setIsSaving(true);
    setErrorMessage('');
    setStatusMessage('');

    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/admin/config`,
        {
          routeColors: {
            default: publicConfig?.routeColors?.default || DEFAULT_ROUTE_COLOR,
            useGtfsColors: Boolean(publicConfig?.routeColors?.useGtfsColors),
            routes: normalizedRouteColors,
          },
          routeCategories: {
            routes: normalizedRouteCategories,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        }
      );
      const nextPublicConfig = normalizePublicTransitAppConfig(response.data?.publicConfig || {});
      setPublicConfig(nextPublicConfig);
      setRouteColors(nextPublicConfig.routeColors?.routes || {});
      setRouteCategories(nextPublicConfig.routeCategories?.routes || {});
      setStatusMessage('Configurazione salvata');
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Salvataggio non riuscito');
    } finally {
      setIsSaving(false);
    }
  }

  const adminRouteRows = buildAdminRouteRows(
    catalogRoutes,
    routeColors,
    routeCategories,
    routeQuery,
    categoryFilter
  );
  const configuredColorCount = Object.values(routeColors).filter(normalizeAdminColorInput).length;
  const configuredCategoryCount = Object.values(routeCategories).filter(Boolean).length;

  return (
    <main className="admin-shell">
      <section className="admin-panel">
        <div className="admin-panel__header">
          <div>
            <span className="panel-heading__eyebrow">Admin</span>
            <h1>Configuratore linee {publicConfig?.operator?.shortName || 'TPL'}</h1>
            <p>Gestisci colori e categoria linea senza toccare codice o branch.</p>
          </div>
          <a className="admin-panel__map-link" href="/">
            Torna alla mappa
          </a>
        </div>

        {!adminToken ? (
          <form className="admin-login" onSubmit={unlockAdmin}>
            <label htmlFor="admin-token">Token admin</label>
            <div>
              <input
                id="admin-token"
                onChange={(event) => setTokenInput(event.target.value)}
                placeholder="ADMIN_TOKEN"
                type="password"
                value={tokenInput}
              />
              <button disabled={isLoading} type="submit">
                Entra
              </button>
            </div>
            <span>Il token resta solo nel browser di questo dispositivo.</span>
          </form>
        ) : null}

        <div className="admin-toolbar">
          <input
            aria-label="Cerca linea admin"
            onChange={(event) => setRouteQuery(event.target.value)}
            placeholder="Cerca linea, es. 4N, 16 CD, STAR"
            type="search"
            value={routeQuery}
          />
          <select
            aria-label="Filtra categoria admin"
            onChange={(event) => setCategoryFilter(event.target.value)}
            value={categoryFilter}
          >
            <option value="">Tutte le categorie</option>
            <option value="urban">Urbane configurate</option>
            <option value="night">Notturne configurate</option>
            <option value="interurban">Extraurbane configurate</option>
          </select>
          <button disabled={!adminToken || isSaving} onClick={resetAdminChanges} type="button">
            Annulla
          </button>
          <button className="admin-toolbar__save" disabled={!adminToken || isSaving} onClick={saveAdminConfig} type="button">
            {isSaving ? 'Salvo...' : 'Salva config'}
          </button>
        </div>

        <div className="admin-meta">
          <span>{catalogRoutes.length} linee GTFS</span>
          <span>{configuredColorCount} colori configurati</span>
          <span>{configuredCategoryCount} categorie manuali</span>
          {isLoading ? <span>caricamento...</span> : null}
          {statusMessage ? <span className="admin-meta__success">{statusMessage}</span> : null}
          {errorMessage ? <span className="admin-meta__error">{errorMessage}</span> : null}
        </div>

        <div className="admin-route-table" role="table" aria-label="Configurazione linee">
          <div className="admin-route-table__head" role="row">
            <span>Linea</span>
            <span>Colore</span>
            <span>Categoria</span>
          </div>
          {adminRouteRows.map((route) => {
            const normalizedColor = normalizeAdminColorInput(route.color);
            const routeColor = normalizedColor || publicConfig?.routeColors?.default || DEFAULT_ROUTE_COLOR;

            return (
              <div className="admin-route-row" key={route.key} role="row">
                <div className="admin-route-row__identity">
                  <span className="admin-route-row__badge" style={{ '--route-color': routeColor }}>
                    {route.shortName}
                  </span>
                  <div>
                    <strong>{route.shortName}</strong>
                    <small>{route.longName}</small>
                    <small>ID GTFS: {route.id}</small>
                  </div>
                </div>
                <div className="admin-route-row__color">
                  <input
                    aria-label={`Colore linea ${route.shortName}`}
                    disabled={!adminToken}
                    onChange={(event) => updateRouteColor(route.key, event.target.value)}
                    type="color"
                    value={routeColor}
                  />
                  <input
                    aria-label={`Hex colore linea ${route.shortName}`}
                    disabled={!adminToken}
                    onChange={(event) => updateRouteColor(route.key, event.target.value)}
                    placeholder="#0057B8"
                    value={route.color || ''}
                  />
                </div>
                <select
                  aria-label={`Categoria linea ${route.shortName}`}
                  disabled={!adminToken}
                  onChange={(event) => updateRouteCategory(route.key, event.target.value)}
                  value={route.category || ''}
                >
                  {ROUTE_CATEGORY_OPTIONS.map((option) => (
                    <option key={option.id || 'auto'} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}

function TrackerApp() {
  const [language, setLanguage] = useState(() => getInitialLanguage());
  setActiveLanguage(language);

  const mapContainerRef = useRef(null);
  const mapWorkspaceRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const stopPopupRef = useRef(null);
  const userLocationMarkerRef = useRef(null);
  const addressSearchMarkerRef = useRef(null);
  const addressSearchDebounceRef = useRef(null);
  const journeySearchDebounceRef = useRef(null);
  const journeyAutoUpdateDebounceRef = useRef(null);
  const journeyRefreshRef = useRef(async () => {});
  const lastJourneyRequestSignatureRef = useRef('');
  const shouldAutoCalculateJourneyAfterPlaceSelectionRef = useRef(false);
  const hasLoadedSharedJourneyRef = useRef(false);
  const markersRef = useRef(new Map());
  const stopMarkersRef = useRef(new Map());
  const journeyStopMarkersRef = useRef([]);
  const busesRef = useRef([]);
  const historyRef = useRef(new Map());
  const hasAutoFittedRef = useRef(false);
  const isRequestRunningRef = useRef(false);
  const loadJourneyPathRef = useRef(async () => {});
  const openRealtimePassageRef = useRef(() => {});
  const openRoutePassageRef = useRef(() => {});
  const openStopTimetablePageRef = useRef(() => {});
  const selectBusRef = useRef(() => {});
  const selectStopRef = useRef(() => {});
  const handleRouteSelectionRef = useRef(() => {});
  const routeOptionsRef = useRef([]);
  const hasInitializedRoutePathSelectionRef = useRef(false);
  const ignoreNextMapClickRef = useRef(false);
  const addressSearchRequestIdRef = useRef(0);
  const journeySearchRequestIdRef = useRef(0);
  const journeyPathRequestIdRef = useRef(0);
  const journeyPathAbortControllerRef = useRef(null);
  const catalogRequestIdRef = useRef(0);
  const skipNextServiceAreaCameraRef = useRef(false);
  const isUserLocationCameraActiveRef = useRef(false);
  const finalizeUserLocationCameraTimeoutRef = useRef(null);
  const restoreUserLocationBoundsTimeoutRef = useRef(null);
  const releaseUserLocationCameraTimeoutRef = useRef(null);
  const mapReturnRefitTimeoutRef = useRef(null);

  const [buses, setBuses] = useState([]);
  const [transitAppConfig, setTransitAppConfig] = useState(INITIAL_PUBLIC_APP_CONFIG);
  const [transitCatalog, setTransitCatalog] = useState({ meta: {}, routes: [], stops: [] });
  const [gtfsServiceWindow, setGtfsServiceWindow] = useState(null);
  const [isCatalogLoading, setIsCatalogLoading] = useState(true);
  const [catalogErrorMessage, setCatalogErrorMessage] = useState('');
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [selectedBusId, setSelectedBusId] = useState('');
  const [selectedServiceAreaId, setSelectedServiceAreaId] = useState(() => readStoredServiceAreaId(INITIAL_PUBLIC_APP_CONFIG));
  const [selectedMapStyleId, setSelectedMapStyleId] = useState(() =>
    findStoredOrDefaultMapStyleId(INITIAL_PUBLIC_APP_CONFIG, getMapStyleOptions(INITIAL_PUBLIC_APP_CONFIG))
  );
  const [mapStyleRevision, setMapStyleRevision] = useState(0);
  const [routeQuery, setRouteQuery] = useState('');
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false);
  const [addressQuery, setAddressQuery] = useState('');
  const [routeListFilter, setRouteListFilter] = useState('urban-night');
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLocatingUser, setIsLocatingUser] = useState(false);
  const [isAddressSearching, setIsAddressSearching] = useState(false);
  const [showLocationHint, setShowLocationHint] = useState(false);
  const [showAddressSearchHint, setShowAddressSearchHint] = useState(true);
  const [showJourneyPlannerHint, setShowJourneyPlannerHint] = useState(true);
  const [showMapToolsHint, setShowMapToolsHint] = useState(false);
  const [isServiceAreaPickerOpen, setIsServiceAreaPickerOpen] = useState(false);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [isMapToolsOpen, setIsMapToolsOpen] = useState(false);
  const [mapInitErrorMessage, setMapInitErrorMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [userLocationMessage, setUserLocationMessage] = useState('');
  const [addressSearchMessage, setAddressSearchMessage] = useState('');
  const [routeContextError, setRouteContextError] = useState('');
  const [journeyErrorMessage, setJourneyErrorMessage] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [routeContext, setRouteContext] = useState(null);
  const [isRouteContextLoading, setIsRouteContextLoading] = useState(false);
  const [selectedStop, setSelectedStop] = useState(null);
  const [stopUpcoming, setStopUpcoming] = useState([]);
  const [stopTimetable, setStopTimetable] = useState([]);
  const [stopPassagesStopId, setStopPassagesStopId] = useState('');
  const [isStopUpcomingLoading, setIsStopUpcomingLoading] = useState(false);
  const [journeyOverlay, setJourneyOverlay] = useState(null);
  const [journeyOptions, setJourneyOptions] = useState([]);
  const [journeyNavigation, setJourneyNavigation] = useState(null);
  const [isJourneyLoading, setIsJourneyLoading] = useState(false);
  const [journeyLoadingStep, setJourneyLoadingStep] = useState(JOURNEY_LOADING_STEPS[0]);
  const [isJourneyRefreshing, setIsJourneyRefreshing] = useState(false);
  const [journeyUpdatedAt, setJourneyUpdatedAt] = useState(null);
  const [journeyShareMessage, setJourneyShareMessage] = useState('');
  const [recentJourneys, setRecentJourneys] = useState(() => readStoredRecentJourneys());
  const [favoriteJourneys, setFavoriteJourneys] = useState(() => readStoredFavoriteJourneys());
  const [favoriteRoutes, setFavoriteRoutes] = useState(() => readStoredFavoriteRoutes());
  const [favoriteStops, setFavoriteStops] = useState(() => readStoredFavoriteStops());
  const [favoritePlaces, setFavoritePlaces] = useState(() => readStoredFavoritePlaces());
  const [serviceAlerts, setServiceAlerts] = useState([]);
  const [isServiceAlertsOpen, setIsServiceAlertsOpen] = useState(false);
  const [isFavoritesOpen, setIsFavoritesOpen] = useState(false);
  const [isOffline, setIsOffline] = useState(() => (typeof navigator === 'undefined' ? false : !navigator.onLine));
  const [isJourneyPlannerOpen, setIsJourneyPlannerOpen] = useState(false);
  const [journeyFromQuery, setJourneyFromQuery] = useState('');
  const [journeyToQuery, setJourneyToQuery] = useState('');
  const [journeyFromPlace, setJourneyFromPlace] = useState(null);
  const [journeyToPlace, setJourneyToPlace] = useState(null);
  const [journeyTimeMode, setJourneyTimeMode] = useState(JOURNEY_TIME_DEPART_NOW);
  const [journeyDateTime, setJourneyDateTime] = useState(() => getDefaultJourneyDateTimeValue());
  const [journeyActiveField, setJourneyActiveField] = useState('');
  const [journeySuggestions, setJourneySuggestions] = useState([]);
  const [isJourneyGeocoding, setIsJourneyGeocoding] = useState(false);
  const [showAllRoutePaths, setShowAllRoutePaths] = useState(false);
  const [areMapStopsVisible, setAreMapStopsVisible] = useState(true);
  const [areLiveBusesVisible, setAreLiveBusesVisible] = useState(true);
  const [isRoutePathPickerOpen, setIsRoutePathPickerOpen] = useState(false);
  const [selectedRoutePathIds, setSelectedRoutePathIds] = useState([]);
  const [allRoutePaths, setAllRoutePaths] = useState([]);
  const [isAllRoutePathsLoading, setIsAllRoutePathsLoading] = useState(false);
  const [allRoutePathsError, setAllRoutePathsError] = useState('');
  const [addressSearchResults, setAddressSearchResults] = useState([]);
  const [stopPageStopId, setStopPageStopId] = useState(() => readStopPageIdFromLocation());
  const [stopPageStop, setStopPageStop] = useState(null);
  const [stopPageScheduled, setStopPageScheduled] = useState([]);
  const [stopPageRealtime, setStopPageRealtime] = useState([]);
  const [stopPageLastLoadedAt, setStopPageLastLoadedAt] = useState(null);
  const [isStopPageLoading, setIsStopPageLoading] = useState(false);
  const [stopPageErrorMessage, setStopPageErrorMessage] = useState('');
  const [stopPageReloadKey, setStopPageReloadKey] = useState(0);
  const [stopPageSelectedDate, setStopPageSelectedDate] = useState(() => getDateInputValue());
  const journeyDisplayOption = journeyNavigation?.option || journeyOverlay;
  const isJourneyNavigationActive = Boolean(journeyNavigation?.option?.summary);
  const hasJourneyResult = Boolean(journeyDisplayOption?.summary);

  const enterMapFullscreenOnMobile = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const isCompactViewport =
      window.matchMedia?.('(max-width: 640px)').matches ?? window.innerWidth <= 640;

    if (isCompactViewport) {
      setIsMapFullscreen(true);
    }
  }, []);

  const selectStop = useCallback(
    (stop) => {
      if (!stop) {
        return;
      }

      enterMapFullscreenOnMobile();
      setSelectedBusId('');
      setSelectedStop(stop);
      setJourneyNavigation(null);
      setJourneyOverlay(null);
      setJourneyErrorMessage('');
    },
    [enterMapFullscreenOnMobile]
  );

  selectStopRef.current = selectStop;

  const deferredRouteQuery = useDeferredValue(routeQuery.trim());
  const serviceAreaOptions = useMemo(() => getConfiguredServiceAreaOptions(transitAppConfig), [transitAppConfig]);
  const mapStyleOptions = useMemo(() => getMapStyleOptions(transitAppConfig), [transitAppConfig]);
  const activeMapStyle = useMemo(
    () => mapStyleOptions.find((style) => style.id === selectedMapStyleId) || mapStyleOptions[0] || null,
    [mapStyleOptions, selectedMapStyleId]
  );
  const activeServiceArea = useMemo(
    () => serviceAreaOptions.find((area) => area.id === selectedServiceAreaId) || serviceAreaOptions[0] || null,
    [selectedServiceAreaId, serviceAreaOptions]
  );
  const activeServiceAreaRef = useRef(activeServiceArea);
  const activeServiceAreaId = activeServiceArea?.id || SERVICE_AREA_DEFAULT_ID;
  const activeServiceAreaApiParams = useMemo(
    () => ({
      areaId: activeServiceAreaId,
    }),
    [activeServiceAreaId]
  );
  const activeServiceAreaIdRef = useRef(activeServiceAreaId);
  const hasServiceAreaPicker = serviceAreaOptions.length > 1;
  const isDefaultServiceArea = !activeServiceArea || activeServiceArea.id === SERVICE_AREA_DEFAULT_ID;
  const [isAppConfigReady, setIsAppConfigReady] = useState(false);
  const stopPageRows = useMemo(
    () => buildStopPassageRows(stopPageRealtime, stopPageScheduled, STOP_PAGE_SCHEDULE_LIMIT),
    [stopPageRealtime, stopPageScheduled]
  );

  function openStopTimetablePage(stop) {
    if (!stop?.id) {
      return;
    }

    const nextStopId = String(stop.id);
    setIsMapFullscreen(false);
    setStopPageStopId(nextStopId);
    setStopPageStop(stop);
    setStopPageSelectedDate(getDateInputValue());

    if (typeof window !== 'undefined') {
      window.history.pushState({ tplStopPage: nextStopId }, '', getStopPagePath(nextStopId));
    }
  }

  function closeStopTimetablePage() {
    setIsMapFullscreen(false);
    setStopPageStopId('');
    setStopPageErrorMessage('');
    setStopPageSelectedDate(getDateInputValue());
    hasAutoFittedRef.current = false;

    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', '/');
    }
  }

  openStopTimetablePageRef.current = openStopTimetablePage;

  function handleStopPageDateChange(nextDate) {
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(String(nextDate || '')) ||
      isDateInputInPast(nextDate) ||
      isDateInputAfter(nextDate, gtfsServiceWindow?.maxDate)
    ) {
      return;
    }

    setStopPageSelectedDate(nextDate);
    setStopPageReloadKey((currentKey) => currentKey + 1);
  }

  function refitMapToCurrentNetwork(mapInstance) {
    if (!mapInstance) {
      return;
    }

    if (typeof mapInstance.resize === 'function') {
      mapInstance.resize();
    }

    if (busesRef.current.length > 0) {
      fitMapToBuses(mapInstance, busesRef.current);
      hasAutoFittedRef.current = true;
      return;
    }

    const currentArea = activeServiceAreaRef.current;

    if (currentArea?.bounds) {
      mapInstance.fitBounds(toMapboxMaxBounds(currentArea.bounds), {
        padding: 64,
        maxZoom: currentArea.zoom,
        duration: 700,
      });
    }
  }

  useEffect(() => {
    busesRef.current = buses;
  }, [buses]);

  useEffect(() => {
    activeServiceAreaIdRef.current = activeServiceAreaId;
  }, [activeServiceAreaId]);

  useEffect(() => {
    activeServiceAreaRef.current = activeServiceArea;
  }, [activeServiceArea]);

  useEffect(
    () => () => {
      window.clearTimeout(finalizeUserLocationCameraTimeoutRef.current);
      window.clearTimeout(restoreUserLocationBoundsTimeoutRef.current);
      window.clearTimeout(releaseUserLocationCameraTimeoutRef.current);
      window.clearTimeout(mapReturnRefitTimeoutRef.current);
    },
    []
  );

  useEffect(() => {
    function handlePopState() {
      setStopPageStopId(readStopPageIdFromLocation());
    }

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if (stopPageStopId) {
      setIsMapFullscreen(false);
    }
  }, [stopPageStopId]);

  useEffect(() => {
    if (!stopPageStopId) {
      setStopPageStop(null);
      setStopPageScheduled([]);
      setStopPageRealtime([]);
      setStopPageLastLoadedAt(null);
      setIsStopPageLoading(false);
      return undefined;
    }

    let isCancelled = false;
    const requestedStopId = String(stopPageStopId);
    const selectedServiceDate = getGtfsServiceDateFromInput(stopPageSelectedDate);
    const isSelectedDateToday = stopPageSelectedDate === getDateInputValue();
    const pageAreaParams = {
      ...activeServiceAreaApiParams,
      routeId: '',
      serviceDate: selectedServiceDate,
    };

    setIsStopPageLoading(true);
    setStopPageErrorMessage('');

    function applyStopPayload(stopPayload) {
      if (stopPayload?.id) {
        setStopPageStop(stopPayload);
        return;
      }

      const catalogStop = transitCatalog.stops.find((stop) => String(stop.id) === requestedStopId);

      if (catalogStop) {
        setStopPageStop(catalogStop);
      }
    }

    function loadStopPageRealtime() {
      if (!isSelectedDateToday) {
        setStopPageRealtime([]);
        return Promise.resolve();
      }

      return axios
        .get(`${API_BASE_URL}/api/stops/${requestedStopId}/realtime`, {
          params: {
            ...pageAreaParams,
            limit: STOP_PAGE_REALTIME_LIMIT,
          },
        })
        .then((response) => {
          if (isCancelled) {
            return;
          }

          applyStopPayload(response.data?.stop);
          setStopPageRealtime(response.data?.passages ?? []);
          setStopPageLastLoadedAt(new Date());
        });
    }

    function loadStopPageSchedule() {
      return axios
        .get(`${API_BASE_URL}/api/stops/${requestedStopId}/schedule`, {
          params: {
            ...pageAreaParams,
            timetableLimit: STOP_PAGE_SCHEDULE_LIMIT,
          },
        })
        .then((response) => {
          if (isCancelled) {
            return;
          }

          applyStopPayload(response.data?.stop);
          setStopPageScheduled(response.data?.scheduledPassages ?? []);
          setStopPageLastLoadedAt(new Date());
        });
    }

    Promise.allSettled([loadStopPageSchedule(), loadStopPageRealtime()])
      .then((results) => {
        if (isCancelled) {
          return;
        }

        const hasFailure = results.some((result) => result.status === 'rejected');

        if (hasFailure && results.every((result) => result.status === 'rejected')) {
          setStopPageErrorMessage(t('stopPageLoadFailed'));
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsStopPageLoading(false);
        }
      });

    const refreshId = window.setInterval(() => {
      if (!isSelectedDateToday) {
        return;
      }

      loadStopPageRealtime().catch(() => {
        if (!isCancelled) {
          setStopPageErrorMessage(t('stopRealtimeUnavailable'));
        }
      });
    }, REFRESH_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(refreshId);
    };
  }, [activeServiceAreaApiParams, stopPageReloadKey, stopPageSelectedDate, stopPageStopId, transitCatalog.stops]);

  useEffect(() => {
    if (userLocation) {
      setShowLocationHint(false);
      setShowJourneyPlannerHint(false);
      setShowMapToolsHint(false);
    }
  }, [userLocation]);

  useEffect(() => {
    if (!showLocationHint && !showAddressSearchHint && !showJourneyPlannerHint && !showMapToolsHint) {
      return undefined;
    }

    const hideHints = window.setTimeout(() => {
      setShowLocationHint(false);
      setShowAddressSearchHint(false);
      setShowJourneyPlannerHint(false);
      setShowMapToolsHint(false);
    }, MAP_HINT_AUTO_HIDE_MS);

    return () => window.clearTimeout(hideHints);
  }, [showLocationHint, showAddressSearchHint, showJourneyPlannerHint, showMapToolsHint]);

  useEffect(() => {
    if (!isServiceAreaPickerOpen) {
      return undefined;
    }

    const closePicker = window.setTimeout(() => {
      setIsServiceAreaPickerOpen(false);
    }, MAP_POPOVER_AUTO_HIDE_MS);

    return () => window.clearTimeout(closePicker);
  }, [isServiceAreaPickerOpen]);

  useEffect(() => {
    if (!serviceAreaOptions.length) {
      return;
    }

    const storedAreaId = readStoredServiceAreaId(transitAppConfig);

    if (storedAreaId !== selectedServiceAreaId && serviceAreaOptions.some((area) => area.id === storedAreaId)) {
      setSelectedServiceAreaId(storedAreaId);
      return;
    }

    const hasSelectedArea = serviceAreaOptions.some((area) => area.id === selectedServiceAreaId);

    if (!hasSelectedArea) {
      setSelectedServiceAreaId(transitAppConfig.defaultServiceAreaId || SERVICE_AREA_DEFAULT_ID);
    }
  }, [selectedServiceAreaId, serviceAreaOptions, transitAppConfig]);

  useEffect(() => {
    if (!mapStyleOptions.length) {
      return;
    }

    const storedStyleId = readStoredMapStyleId(transitAppConfig);

    if (storedStyleId && storedStyleId !== selectedMapStyleId && mapStyleOptions.some((style) => style.id === storedStyleId)) {
      setSelectedMapStyleId(storedStyleId);
      return;
    }

    if (!mapStyleOptions.some((style) => style.id === selectedMapStyleId)) {
      setSelectedMapStyleId(findStoredOrDefaultMapStyleId(transitAppConfig, mapStyleOptions));
    }
  }, [mapStyleOptions, selectedMapStyleId, transitAppConfig]);

  useEffect(() => {
    if (stopPageStopId) {
      return undefined;
    }

    if (!mapContainerRef.current || mapRef.current) {
      return undefined;
    }

    if (typeof mapboxgl.supported === 'function' && !mapboxgl.supported()) {
      setMapInitErrorMessage(t('mapUnsupported'));
      return undefined;
    }

    let mapInstance;

    try {
      const initialMapBounds = toMapboxMaxBounds(INITIAL_PUBLIC_APP_CONFIG.map?.bounds);
      const initialStyleOptions = getMapStyleOptions(INITIAL_PUBLIC_APP_CONFIG);
      const initialStyleId = findStoredOrDefaultMapStyleId(INITIAL_PUBLIC_APP_CONFIG, initialStyleOptions);
      const initialStyle = initialStyleOptions.find((style) => style.id === initialStyleId) || initialStyleOptions[0];
      mapInstance = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: initialStyle?.url || INITIAL_PUBLIC_APP_CONFIG.map?.style || MAP_STYLE,
        center: INITIAL_PUBLIC_APP_CONFIG.map?.center || DEFAULT_CENTER,
        zoom: Number(INITIAL_PUBLIC_APP_CONFIG.map?.zoom || DEFAULT_ZOOM),
        minZoom: Number(INITIAL_PUBLIC_APP_CONFIG.map?.minZoom || 5),
        maxBounds: initialMapBounds,
        renderWorldCopies: false,
        attributionControl: false,
      });
      mapInstance.__tplTrackerStyleUrl = initialStyle?.url || INITIAL_PUBLIC_APP_CONFIG.map?.style || MAP_STYLE;
    } catch {
      setMapInitErrorMessage(t('mapStartupFailed'));
      return undefined;
    }

    mapInstance.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');
    mapInstance.addControl(new mapboxgl.AttributionControl({ compact: false }), 'bottom-right');
    const handleMapClick = () => {
      if (ignoreNextMapClickRef.current) {
        ignoreNextMapClickRef.current = false;
        return;
      }

      setSelectedStop(null);
      setJourneyOverlay(null);
      setJourneyErrorMessage('');
    };
    const handleStopLayerClick = (event) => {
      const feature =
        event.features?.[0] ??
        mapInstance.queryRenderedFeatures?.(event.point, { layers: TPL_STOPS_INTERACTIVE_LAYER_IDS })?.[0];
      const stop = normalizeMapStopFeature(feature);

      if (!stop) {
        return;
      }

      ignoreNextMapClickRef.current = true;
      selectStopRef.current(stop);
    };
    const handleStopsMouseEnter = () => {
      mapInstance.getCanvas().style.cursor = 'pointer';
    };
    const handleStopsMouseLeave = () => {
      mapInstance.getCanvas().style.cursor = '';
    };
    const handleMapLoad = () => {
      ensureMapOverlayLayers(mapInstance);
      setMapStyleRevision((currentRevision) => currentRevision + 1);
      TPL_STOPS_INTERACTIVE_LAYER_IDS.forEach((layerId) => {
        mapInstance.on('click', layerId, handleStopLayerClick);
        mapInstance.on('mouseenter', layerId, handleStopsMouseEnter);
        mapInstance.on('mouseleave', layerId, handleStopsMouseLeave);
      });
      window.clearTimeout(mapReturnRefitTimeoutRef.current);
      mapReturnRefitTimeoutRef.current = window.setTimeout(() => {
        if (mapRef.current === mapInstance && !hasAutoFittedRef.current) {
          refitMapToCurrentNetwork(mapInstance);
        }
      }, MAP_RETURN_REFIT_DELAY_MS);
    };
    const handleMapStyleData = () => {
      setMapStyleRevision((currentRevision) => currentRevision + 1);
    };

    mapInstance.on('load', handleMapLoad);
    mapInstance.on('styledata', handleMapStyleData);
    mapInstance.on('click', handleMapClick);
    mapRef.current = mapInstance;
    const markerStore = markersRef.current;
    const stopMarkerStore = stopMarkersRef.current;

    return () => {
      addressSearchRequestIdRef.current += 1;
      journeySearchRequestIdRef.current += 1;
      clearTimeout(addressSearchDebounceRef.current);
      clearTimeout(journeySearchDebounceRef.current);
      clearTimeout(journeyAutoUpdateDebounceRef.current);
      window.clearTimeout(mapReturnRefitTimeoutRef.current);
      popupRef.current?.remove();
      stopPopupRef.current?.remove();
      userLocationMarkerRef.current?.remove();
      addressSearchMarkerRef.current?.remove();
      journeyStopMarkersRef.current.forEach((marker) => marker.remove());
      journeyStopMarkersRef.current = [];
      userLocationMarkerRef.current = null;
      addressSearchMarkerRef.current = null;
      markerStore.forEach(({ marker }) => marker.remove());
      markerStore.clear();
      stopMarkerStore.forEach(({ marker }) => marker.remove());
      stopMarkerStore.clear();
      mapInstance.off('load', handleMapLoad);
      mapInstance.off('styledata', handleMapStyleData);
      mapInstance.off('click', handleMapClick);
      TPL_STOPS_INTERACTIVE_LAYER_IDS.forEach((layerId) => {
        mapInstance.off('click', layerId, handleStopLayerClick);
        mapInstance.off('mouseenter', layerId, handleStopsMouseEnter);
        mapInstance.off('mouseleave', layerId, handleStopsMouseLeave);
      });
      mapInstance.remove();
      mapRef.current = null;
    };
  }, [stopPageStopId]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
      setIsMapFullscreen(fullscreenElement === mapWorkspaceRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const mapInstance = mapRef.current;
    const nextStyleUrl = activeMapStyle?.url || transitAppConfig.map?.style || MAP_STYLE;

    if (!mapInstance || !nextStyleUrl || typeof mapInstance.setStyle !== 'function') {
      return;
    }

    if (mapInstance.__tplTrackerStyleUrl === nextStyleUrl) {
      return;
    }

    mapInstance.__tplTrackerStyleUrl = nextStyleUrl;
    mapInstance.setStyle(nextStyleUrl);
    setMapStyleRevision((currentRevision) => currentRevision + 1);
  }, [activeMapStyle?.url, transitAppConfig.map?.style]);

  useEffect(() => {
    const applyViewportHeight = () => {
      document.documentElement.style.setProperty('--app-height', `${getViewportHeight()}px`);
    };

    applyViewportHeight();
    window.addEventListener('resize', applyViewportHeight);
    window.addEventListener('orientationchange', applyViewportHeight);
    window.visualViewport?.addEventListener('resize', applyViewportHeight);
    window.visualViewport?.addEventListener('scroll', applyViewportHeight);

    return () => {
      window.removeEventListener('resize', applyViewportHeight);
      window.removeEventListener('orientationchange', applyViewportHeight);
      window.visualViewport?.removeEventListener('resize', applyViewportHeight);
      window.visualViewport?.removeEventListener('scroll', applyViewportHeight);
    };
  }, []);

  useEffect(() => {
    const mapInstance = mapRef.current;

    if (!mapInstance) {
      return undefined;
    }

    const resizeMap = window.setTimeout(() => {
      mapInstance.resize();
    }, 180);
    const finalResizeMap = window.setTimeout(() => {
      mapInstance.resize();
    }, 420);
    const isCompactJourneyPlanner =
      isJourneyPlannerOpen && (window.matchMedia?.('(max-width: 640px)').matches ?? window.innerWidth <= 640);
    const shouldLockPage = isMapFullscreen || isCompactJourneyPlanner;

    document.body.style.overflow = shouldLockPage ? 'hidden' : '';
    document.documentElement.style.overflow = shouldLockPage ? 'hidden' : '';

    if (isMapFullscreen || isCompactJourneyPlanner) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    return () => {
      window.clearTimeout(resizeMap);
      window.clearTimeout(finalResizeMap);
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [hasJourneyResult, isJourneyPlannerOpen, isMapFullscreen]);

  useEffect(() => {
    if (!isMapFullscreen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMapFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMapFullscreen]);

  useEffect(() => {
    const mapInstance = mapRef.current;
    const configuredCenter = activeServiceArea?.center || transitAppConfig.map?.center;
    const configuredBounds = toMapboxMaxBounds(activeServiceArea?.bounds || transitAppConfig.map?.bounds);
    const configuredMinZoom = Number(activeServiceArea?.minZoom || transitAppConfig.map?.minZoom || 5);
    const configuredZoom = Number(activeServiceArea?.zoom || transitAppConfig.map?.zoom || DEFAULT_ZOOM);

    if (!mapInstance) {
      return;
    }

    if (isUserLocationCameraActiveRef.current) {
      return;
    }

    if (skipNextServiceAreaCameraRef.current) {
      skipNextServiceAreaCameraRef.current = false;
      return;
    }

    if (typeof mapInstance.setMaxBounds === 'function') {
      mapInstance.setMaxBounds(configuredBounds);
    }

    if (typeof mapInstance.setMinZoom === 'function' && Number.isFinite(configuredMinZoom)) {
      mapInstance.setMinZoom(configuredMinZoom);
    }

    if (
      hasAutoFittedRef.current ||
      !Array.isArray(configuredCenter) ||
      configuredCenter.length !== 2
    ) {
      if (selectedServiceAreaId === SERVICE_AREA_DEFAULT_ID) {
        return;
      }
    }

    mapInstance.easeTo({
      center: configuredCenter,
      zoom: configuredZoom,
      duration: 500,
      essential: true,
    });
  }, [activeServiceArea, selectedServiceAreaId, transitAppConfig]);

  const routeOptions = buildRouteOptions(transitCatalog.routes, buses);
  const categoryFilteredRouteOptions = routeOptions.filter((routeOption) => {
    const routeCategory = routeOption.category;

    if (routeListFilter === 'urban-night') {
      return routeCategory === 'urban' || routeCategory === 'night';
    }

    if (routeListFilter === 'all') {
      return true;
    }

    return routeCategory === routeListFilter;
  });

  const availableRouteOptions = deferredRouteQuery
    ? categoryFilteredRouteOptions.filter((routeOption) => {
        const searchableText = `${routeOption.shortName} ${routeOption.routeIds.join(' ')} ${
          routeOption.longName ?? ''
        }`.toLowerCase();
        return searchableText.includes(deferredRouteQuery.toLowerCase());
      })
    : categoryFilteredRouteOptions;

  const selectedRouteOption = getRouteOptionByRouteId(routeOptions, selectedRouteId);
  routeOptionsRef.current = routeOptions;
  const favoriteRouteSet = new Set(favoriteRoutes);
  const favoriteRouteOptions = favoriteRoutes
    .map((routeLabel) => getRouteOptionByRouteLabel(routeOptions, routeLabel))
    .filter(Boolean);
  const favoriteStopsById = new Map(favoriteStops.map((stop) => [stop.id, stop]));
  const favoriteStopEntries = favoriteStops.map((favoriteStop) => {
    const catalogStop = transitCatalog.stops.find((stop) => String(stop.id) === favoriteStop.id);
    return normalizeFavoriteStop(catalogStop || favoriteStop) || favoriteStop;
  });
  const favoritePlaceEntries = favoritePlaces.map((favoritePlace) => normalizeFavoritePlace(favoritePlace)).filter(Boolean);
  const favoritePlaceSet = new Set(favoritePlaceEntries.map((place) => place.id));
  const favoriteJourneyEntries = favoriteJourneys.map((favoriteJourney) => normalizeFavoriteJourney(favoriteJourney)).filter(Boolean);
  const activeFavoriteJourneyEntry = buildFavoriteJourneyEntry(journeyFromPlace, journeyToPlace, journeyDisplayOption);
  const favoriteJourneySet = new Set(favoriteJourneyEntries.map((journey) => journey.id));
  const favoriteStopIdsKey = favoriteStops.map((stop) => stop.id).join('|');
  const hasFavorites = favoriteJourneyEntries.length > 0 || favoriteRouteOptions.length > 0 || favoriteStopEntries.length > 0 || favoritePlaceEntries.length > 0;
  const serviceAlertCount = serviceAlerts.length;
  const selectedRouteIds = selectedRouteOption?.routeIds ?? (selectedRouteId ? [selectedRouteId] : []);
  const selectedRouteIdsKey = selectedRouteIds.join('|');
  const selectedRouteIdSet = new Set(selectedRouteIds);

  const visibleBuses = buses.filter(
    (bus) => !selectedRouteIdSet.size || selectedRouteIdSet.has(bus.routeId)
  );

  const selectedBus = visibleBuses.find((bus) => bus.id === selectedBusId) ?? null;
  const selectedRoute = selectedRouteOption;
  const selectedRouteContextRouteId =
    selectedBus?.routeId ||
    selectedRouteOption?.routeIds.find((routeId) => buses.some((bus) => bus.routeId === routeId)) ||
    selectedRouteOption?.id ||
    selectedRouteId;
  const appFeatures = transitAppConfig.features || DEFAULT_PUBLIC_APP_CONFIG.features;
  const hasVehicleSpeed = Boolean(appFeatures.vehicleSpeed);
  const hasScheduleAdherence = Boolean(appFeatures.scheduleAdherence);
  const hasRealtimeArrivalPredictions = Boolean(appFeatures.realtimeArrivalPredictions);
  const hasArrivalCountdown = Boolean(appFeatures.arrivalCountdown);
  const hasJourneyPath = Boolean(appFeatures.journeyPath);
  const journeyLegs = useMemo(
    () =>
      (journeyDisplayOption?.legs || []).map((leg) => ({
        ...leg,
        liveBus: isJourneyNavigationActive
          ? getFollowingJourneyLiveBus(leg, buses)
          : getCatchableJourneyLiveBus(leg, buses),
      })),
    [buses, isJourneyNavigationActive, journeyDisplayOption?.legs]
  );
  const visibleJourneyOptions = useMemo(() => {
    const activeSignature = getJourneyOptionSignature(journeyOverlay);
    const dedupedOptions = new Map();

    journeyOptions.forEach((option) => {
      const optionWithLiveBuses = getJourneyOptionWithLiveBuses(option, buses);

      if (!optionWithLiveBuses) {
        return;
      }

      const dedupeKey = getJourneyOptionDedupeKey(optionWithLiveBuses);
      const currentOption = dedupedOptions.get(dedupeKey);

      if (!currentOption) {
        dedupedOptions.set(dedupeKey, optionWithLiveBuses);
        return;
      }

      const isOptionActive = getJourneyOptionSignature(optionWithLiveBuses) === activeSignature;
      const isCurrentActive = getJourneyOptionSignature(currentOption) === activeSignature;

      if ((isOptionActive && !isCurrentActive) || (!isCurrentActive && compareJourneyOptions(optionWithLiveBuses, currentOption) < 0)) {
        dedupedOptions.set(dedupeKey, optionWithLiveBuses);
      }
    });

    return [...dedupedOptions.values()];
  }, [buses, journeyOptions, journeyOverlay]);
  const journeyFirstLeg = journeyLegs[0] || null;
  const journeyTransitLegs = useMemo(
    () => journeyLegs.filter((leg) => leg.mode !== 'WALK'),
    [journeyLegs]
  );
  const journeyRouteIds = useMemo(
    () => journeyTransitLegs.map((leg) => leg.routeId).filter(Boolean),
    [journeyTransitLegs]
  );
  const journeyFirstTransitLeg = journeyTransitLegs[0] || null;
  const journeyBoardingStop = journeyTransitLegs[0]?.from || null;
  const journeyArrivalStop = journeyTransitLegs[journeyTransitLegs.length - 1]?.to || null;
  const journeyNavigationLeg = journeyNavigation?.activeLegId
    ? journeyTransitLegs.find((leg) => leg.id === journeyNavigation.activeLegId) || journeyFirstTransitLeg
    : journeyFirstTransitLeg;
  const journeyNavigationLiveState = getJourneyLiveBusStopState(journeyNavigationLeg);
  const journeyNavigationGpsState = getJourneyNavigationGpsState(
    journeyNavigationLeg,
    userLocation,
    journeyNavigationLiveState
  );
  const journeyNavigationRemainingStops = getJourneyRemainingStopCount(
    journeyNavigationLeg,
    journeyNavigationLiveState,
    journeyNavigationGpsState
  );
  const journeyVisibleLegs = useMemo(() => {
    const indexedLegs = journeyLegs.map((leg, legIndex) => ({ leg, legIndex }));

    if (!isJourneyNavigationActive || !journeyNavigationLeg?.id) {
      return indexedLegs;
    }

    return indexedLegs.filter(({ leg }) => leg.id === journeyNavigationLeg.id);
  }, [isJourneyNavigationActive, journeyLegs, journeyNavigationLeg?.id]);
  const journeyTransferStops = useMemo(() => {
    const transferStops = [];

    journeyLegs.forEach((leg, legIndex) => {
      if (leg.mode === 'WALK') {
        return;
      }

      const nextTransitLeg = journeyLegs.slice(legIndex + 1).find((nextLeg) => nextLeg.mode !== 'WALK');

      if (!nextTransitLeg || !Number.isFinite(leg.to?.longitude) || !Number.isFinite(leg.to?.latitude)) {
        return;
      }

      transferStops.push({
        key: `${leg.id}-${nextTransitLeg.id}`,
        place: {
          ...leg.to,
          nextRoute: nextTransitLeg.route,
          nextHeadsign: nextTransitLeg.headsign,
        },
        variant: 'transfer',
      });
    });

    return transferStops;
  }, [journeyLegs]);
  const selectedRoutePathIdSet = new Set(selectedRoutePathIds);
  const selectedRoutePathFeatures = allRoutePaths.filter((feature) =>
    selectedRoutePathIdSet.has(String(feature.properties.routeId || ''))
  );
  const hasFocusedMapView = Boolean(selectedRouteId || selectedBusId || selectedStop || journeyDisplayOption);
  const routeContextStopsCount = routeContext?.stops?.length ?? 0;
  const visibleRoutesCount = new Set(visibleBuses.map((bus) => bus.routeId)).size;
  const operatorShortName = transitAppConfig.operator?.shortName || transitAppConfig.app?.shortName || 'TPL';
  const dataSource = getTransitDataSource(transitAppConfig);
  const copyrightYear = new Date().getFullYear();
  const averageSpeed =
    hasVehicleSpeed && visibleBuses.length > 0
      ? Number(
          (
            visibleBuses.reduce((speedSum, bus) => speedSum + bus.speedKmh, 0) / visibleBuses.length
          ).toFixed(1)
        )
      : 0;
  const lastUpdateAgeMs = lastUpdatedAt instanceof Date ? Date.now() - lastUpdatedAt.getTime() : null;
  const isDataStale = Number.isFinite(lastUpdateAgeMs) && lastUpdateAgeMs > REFRESH_INTERVAL_MS * 3;
  const dataQualityLabel = isOffline
    ? t('dataQualityOffline')
    : isRefreshing || isInitialLoading
      ? t('dataQualityRefreshing')
      : isDataStale
        ? t('dataQualityStale')
        : buses.length > 0
          ? t('dataQualityLive')
          : t('dataQualityStatic');
  const dataQualityClassName = [
    'data-quality-badge',
    isOffline ? 'is-offline' : '',
    isRefreshing || isInitialLoading ? 'is-refreshing' : '',
    isDataStale ? 'is-stale' : '',
  ].filter(Boolean).join(' ');

  useEffect(() => {
    document.documentElement.lang = getLocale(language);
  }, [language]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    function updateConnectivityState() {
      setIsOffline(!window.navigator.onLine);
    }

    updateConnectivityState();
    window.addEventListener('online', updateConnectivityState);
    window.addEventListener('offline', updateConnectivityState);

    return () => {
      window.removeEventListener('online', updateConnectivityState);
      window.removeEventListener('offline', updateConnectivityState);
    };
  }, []);

  useEffect(() => {
    loadGtfsServiceWindow();
    // GTFS service coverage is operator-scoped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitAppConfig.id]);

  useEffect(() => {
    if (hasLoadedSharedJourneyRef.current || !isAppConfigReady || typeof window === 'undefined') {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);

    if (searchParams.get('journey') !== '1') {
      return;
    }

    const fromPlace = getJourneyPlaceFromUrlParams(searchParams, 'from');
    const toPlace = getJourneyPlaceFromUrlParams(searchParams, 'to');

    if (!fromPlace || !toPlace) {
      return;
    }

    const timeParams = getJourneyUrlTimeParams(searchParams);
    const nextDateTime = timeParams.time
      ? getDefaultJourneyDateTimeValue(new Date(timeParams.time * 1000))
      : getDefaultJourneyDateTimeValue();

    hasLoadedSharedJourneyRef.current = true;
    setJourneyFromPlace(fromPlace);
    setJourneyToPlace(toPlace);
    setJourneyFromQuery(fromPlace.label);
    setJourneyToQuery(toPlace.label);
    setJourneyTimeMode(timeParams.timeMode);
    setJourneyDateTime(timeParams.timeMode === JOURNEY_TIME_LAST ? `${nextDateTime.slice(0, 10)}T23:59` : nextDateTime);
    setIsJourneyPlannerOpen(true);
    requestJourneyPath(fromPlace, toPlace, {
      timeParamsOverride: timeParams.time
        ? { timeMode: timeParams.timeMode, time: timeParams.time }
        : { timeMode: timeParams.timeMode },
    });
    // Shared journey bootstrap intentionally runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppConfigReady]);

  useEffect(() => {
    const maxDate = gtfsServiceWindow?.maxDate;

    if (!maxDate) {
      return;
    }

    if (isDateInputAfter(stopPageSelectedDate, maxDate)) {
      setStopPageSelectedDate(maxDate);
    }

    if (journeyTimeMode !== JOURNEY_TIME_DEPART_NOW && isDateInputAfter(journeyDateTime.slice(0, 10), maxDate)) {
      setJourneyDateTime((currentValue) => `${maxDate}${currentValue.includes('T') ? currentValue.slice(10) : 'T23:59'}`);
    }
  }, [gtfsServiceWindow?.maxDate, journeyDateTime, journeyTimeMode, stopPageSelectedDate]);

  function selectLanguage(nextLanguage) {
    const normalizedLanguage = normalizeLanguage(nextLanguage) || 'it';
    setLanguage(normalizedLanguage);
    writeStoredLanguage(normalizedLanguage);
  }

  async function loadAppConfig() {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/app-config`);
      const nextConfig = normalizePublicTransitAppConfig(response.data);

      applyPublicTransitAppConfig(nextConfig);
      startTransition(() => {
        setTransitAppConfig(nextConfig);
      });

      return nextConfig;
    } catch {
      applyPublicTransitAppConfig(INITIAL_PUBLIC_APP_CONFIG);
      return INITIAL_PUBLIC_APP_CONFIG;
    } finally {
      setIsAppConfigReady(true);
    }
  }

  async function loadGtfsServiceWindow() {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/gtfs-service-window`);
      const calendar = response.data?.calendar || null;
      setGtfsServiceWindow({
        minDate: getDateInputValueFromGtfsDate(calendar?.firstServiceDate),
        maxDate: getDateInputValueFromGtfsDate(calendar?.lastServiceDate),
        status: response.data?.status || '',
        warnings: response.data?.warnings || [],
      });
    } catch {
      setGtfsServiceWindow(null);
    }
  }

  async function loadTransitCatalog() {
    const requestAreaId = activeServiceAreaId;
    const requestId = catalogRequestIdRef.current + 1;
    catalogRequestIdRef.current = requestId;
    setIsCatalogLoading(true);
    setCatalogErrorMessage('');

    const catalogCacheKey = getAreaScopedCacheKey(TRANSIT_CATALOG_CACHE_KEY, activeServiceAreaId);
    const cachedCatalog = await readBrowserCache(catalogCacheKey, getBrowserStaticCacheTtlMs());

    if (cachedCatalog) {
      if (catalogRequestIdRef.current !== requestId || activeServiceAreaIdRef.current !== requestAreaId) {
        setIsCatalogLoading(false);
        return;
      }

      if (isTransitCatalogUsableForArea(cachedCatalog, requestAreaId)) {
        startTransition(() => {
          setTransitCatalog(cachedCatalog);
        });
        setIsCatalogLoading(false);
        return;
      }

      await deleteBrowserCache(catalogCacheKey);
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/transit/catalog`, {
        params: activeServiceAreaApiParams,
      });

      if (catalogRequestIdRef.current !== requestId || activeServiceAreaIdRef.current !== requestAreaId) {
        return;
      }

      const nextCatalog = normalizeTransitCatalog(response.data);

      if (!isTransitCatalogUsableForArea(nextCatalog, requestAreaId)) {
        await deleteBrowserCache(catalogCacheKey);
        throw new Error(`Catalogo area ${requestAreaId} senza fermate disponibili`);
      }

      startTransition(() => {
        setTransitCatalog(nextCatalog);
      });
      await writeBrowserCache(catalogCacheKey, nextCatalog);
    } catch (error) {
      if (catalogRequestIdRef.current !== requestId || activeServiceAreaIdRef.current !== requestAreaId) {
        return;
      }

      setCatalogErrorMessage(
        error?.response?.data?.error ?? t('unableLoadStatic', { operator: operatorShortName })
      );
    } finally {
      if (catalogRequestIdRef.current === requestId && activeServiceAreaIdRef.current === requestAreaId) {
        setIsCatalogLoading(false);
      }
    }
  }

  async function loadBuses(mode = 'manual') {
    if (isRequestRunningRef.current) {
      return;
    }

    const requestAreaId = activeServiceAreaId;
    isRequestRunningRef.current = true;
    setErrorMessage('');

    if (mode === 'initial') {
      setIsInitialLoading(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/vehicles`, {
        params: activeServiceAreaApiParams,
      });

      if (activeServiceAreaIdRef.current !== requestAreaId) {
        return;
      }

      const previousHistory = historyRef.current;
      const areaBounds = activeServiceAreaRef.current?.bounds || transitAppConfig.map?.bounds;
      const nextBuses = normalizeBuses(response.data).filter((bus) =>
        isPointInsideBounds(bus.longitude, bus.latitude, areaBounds)
      ).map((bus) => ({
        ...bus,
        movement: inferMovement(bus, previousHistory.get(bus.id)),
      }));
      const nextServiceAlerts = normalizeServiceAlerts(response.data);

      historyRef.current = new Map(
        nextBuses.map((bus) => [
          bus.id,
          {
            latitude: bus.latitude,
            longitude: bus.longitude,
            timestamp: bus.timestamp,
            bearing: bus.bearing,
          },
        ])
      );

      startTransition(() => {
        setBuses(nextBuses);
        setServiceAlerts(nextServiceAlerts);
        // A selected line is a user intent, not a property of the latest realtime snapshot.
        // Some feeds briefly omit vehicles; clearing here makes the selected line flash and disappear.
        setSelectedBusId((currentBusId) =>
          currentBusId && nextBuses.some((bus) => bus.id === currentBusId) ? currentBusId : ''
        );
        setLastUpdatedAt(new Date());
      });

      if (!hasAutoFittedRef.current && nextBuses.length > 0) {
        fitMapToBuses(mapRef.current, nextBuses);
        hasAutoFittedRef.current = true;
      }
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.error ??
          t('unableLoadFleet', { operator: operatorShortName })
      );
    } finally {
      isRequestRunningRef.current = false;
      setIsInitialLoading(false);
      setIsRefreshing(false);
    }
  }

  async function loadAllRoutePaths() {
    if (isAllRoutePathsLoading || allRoutePaths.length > 0) {
      return;
    }

    const requestAreaId = activeServiceAreaId;
    setIsAllRoutePathsLoading(true);
    setAllRoutePathsError('');

    const routePathsCacheKey = getAreaScopedCacheKey(ROUTE_PATHS_CACHE_KEY, activeServiceAreaId);
    const cachedRoutePaths = await readBrowserCache(routePathsCacheKey, getBrowserStaticCacheTtlMs());

    if (Array.isArray(cachedRoutePaths) && cachedRoutePaths.length > 0) {
      if (activeServiceAreaIdRef.current !== requestAreaId) {
        setIsAllRoutePathsLoading(false);
        return;
      }

      startTransition(() => {
        setAllRoutePaths(cachedRoutePaths);
      });
      setIsAllRoutePathsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/route-paths`, {
        params: {
          ...activeServiceAreaApiParams,
          maxPointsPerShape: 140,
        },
      });

      if (activeServiceAreaIdRef.current !== requestAreaId) {
        return;
      }

      const nextRoutePaths = normalizeRoutePathCollection(response.data);

      startTransition(() => {
        setAllRoutePaths(nextRoutePaths);
      });
      await writeBrowserCache(routePathsCacheKey, nextRoutePaths);
    } catch (error) {
      setAllRoutePathsError(error?.response?.data?.error ?? t('unableLoadAllRoutes', { operator: operatorShortName }));
      setShowAllRoutePaths(false);
    } finally {
      setIsAllRoutePathsLoading(false);
    }
  }

  function toggleAllRoutePaths() {
    setAllRoutePathsError('');
    setIsRoutePathPickerOpen((currentValue) => {
      const nextValue = !currentValue;

      if (nextValue && typeof window !== 'undefined' && window.history?.pushState) {
        window.history.pushState({ tepPanel: 'route-paths' }, '', window.location.href);
      }

      return nextValue;
    });
    loadAllRoutePaths();
  }

  function toggleRoutePathsVisibility() {
    setShowAllRoutePaths((currentValue) => !currentValue);
    loadAllRoutePaths();
  }

  function toggleMapStopsVisibility() {
    setAreMapStopsVisible((currentValue) => {
      const nextValue = !currentValue;

      if (!nextValue) {
        setSelectedStop(null);
        setJourneyOverlay(null);
        setJourneyErrorMessage('');
      }

      return nextValue;
    });
  }

  function toggleLiveBusesVisibility() {
    setAreLiveBusesVisible((currentValue) => {
      const nextValue = !currentValue;

      if (!nextValue) {
        setSelectedBusId('');
        popupRef.current?.remove();
      }

      return nextValue;
    });
  }

  function selectBus(busId) {
    const bus = busesRef.current.find((item) => item.id === busId);

    if (!bus) {
      return;
    }

    enterMapFullscreenOnMobile();

    if (typeof window !== 'undefined' && window.history?.pushState) {
      window.history.pushState({ tepMapFocus: 'bus' }, '', window.location.href);
    }

    startTransition(() => {
      setSelectedRouteId(bus.routeId);
      setSelectedBusId(busId);
    });
    setAreLiveBusesVisible(true);
    setSelectedStop(null);
    setJourneyOverlay(null);
    setJourneyOptions([]);
    setJourneyErrorMessage('');
  }

  selectBusRef.current = selectBus;

  function openRealtimePassage(passage) {
    const realtimeBus = busesRef.current.find(
      (bus) => bus.id === passage.vehicleId || bus.label === passage.vehicleLabel
    );

    if (!realtimeBus) {
      openRoutePassage(passage);
      return;
    }

    const referenceStop = selectedStop;
    selectBus(realtimeBus.id);
    fitMapToBusAndStop(mapRef.current, realtimeBus, referenceStop);
  }

  function openRoutePassage(passage) {
    const routeId = passage?.routeId || '';

    if (!routeId) {
      return;
    }

    handleRouteSelection(routeId, { focusStop: selectedStop });
  }

  openRealtimePassageRef.current = openRealtimePassage;
  openRoutePassageRef.current = openRoutePassage;

  function resetMapFocus() {
    startTransition(() => {
      setSelectedRouteId('');
      setSelectedBusId('');
    });

    setSelectedStop(null);
    setRouteContext(null);
    setRouteContextError('');
    setJourneyOverlay(null);
    setJourneyOptions([]);
    setJourneyErrorMessage('');
    setShowAllRoutePaths(false);
    setAreMapStopsVisible(true);
    setAreLiveBusesVisible(true);
    setIsRoutePathPickerOpen(false);
    setIsJourneyPlannerOpen(false);
    setIsServiceAreaPickerOpen(false);
    setAllRoutePathsError('');
    clearMapTraceData(mapRef.current);
    fitMapToBuses(mapRef.current, busesRef.current);
  }

  function focusServiceArea(area) {
    if (!mapRef.current || !area) {
      return;
    }

    mapRef.current.fitBounds(toMapboxMaxBounds(area.bounds), {
      padding: 64,
      maxZoom: area.zoom,
      duration: 700,
    });
  }

  function selectServiceArea(areaId) {
    const nextArea = serviceAreaOptions.find((area) => area.id === areaId) || serviceAreaOptions[0];

    if (!nextArea) {
      return;
    }

    setSelectedServiceAreaId(nextArea.id);
    writeStoredServiceAreaId(transitAppConfig, nextArea.id);
    setSelectedRouteId('');
    setSelectedBusId('');
    setSelectedStop(null);
    setTransitCatalog({ meta: {}, routes: [], stops: [] });
    setCatalogErrorMessage('');
    setIsCatalogLoading(true);
    setJourneyOverlay(null);
    setJourneyErrorMessage('');
    setAddressSearchResults([]);
    setAddressSearchMessage('');
    addressSearchMarkerRef.current?.remove();
    addressSearchMarkerRef.current = null;
    setIsServiceAreaPickerOpen(false);
    focusServiceArea(nextArea);
  }

  function resetServiceArea() {
    selectServiceArea(SERVICE_AREA_DEFAULT_ID);
  }

  function clearBusFocus() {
    setSelectedBusId('');
    setSelectedStop(null);
    setJourneyOverlay(null);
    setJourneyErrorMessage('');
  }

  function clearStopFocus() {
    setSelectedStop(null);
    setJourneyOverlay(null);
    setJourneyErrorMessage('');
  }

  function focusAddressResult(result) {
    if (!mapRef.current || !result) {
      return;
    }

    if (!addressSearchMarkerRef.current) {
      const markerElement = document.createElement('div');
      markerElement.className = 'address-search-marker';

      addressSearchMarkerRef.current = new mapboxgl.Marker({
        element: markerElement,
        anchor: 'bottom',
      })
        .setLngLat([result.longitude, result.latitude])
        .addTo(mapRef.current);
    } else {
      addressSearchMarkerRef.current.setLngLat([result.longitude, result.latitude]);
    }

    setSelectedBusId('');
    setSelectedStop(null);
    setJourneyOverlay(null);
    setJourneyErrorMessage('');
    setAddressQuery(result.label);
    setAddressSearchResults([]);
    setAddressSearchMessage(t('addressFound'));
    setIsAddressSearchOpen(true);
    setShowAddressSearchHint(false);

    mapRef.current.flyTo({
      center: [result.longitude, result.latitude],
      zoom: result.featureType === 'address' ? 16 : 14.4,
      duration: 850,
      essential: true,
    });
  }

  async function fetchAddressResults(searchText, { autocomplete = true, autoSelectFirst = false, showEmptyMessage = true } = {}) {
    if (searchText.length < 3) {
      setAddressSearchResults([]);
      setAddressSearchMessage(showEmptyMessage ? t('addressMinChars') : '');
      return;
    }

    if (typeof fetch !== 'function') {
      setAddressSearchResults([]);
      setAddressSearchMessage(t('addressUnavailable'));
      return;
    }

    const currentRequestId = addressSearchRequestIdRef.current + 1;
    addressSearchRequestIdRef.current = currentRequestId;
    setIsAddressSearching(true);
    setAddressSearchMessage('');
    setAddressSearchResults([]);

    try {
      const serviceArea = activeServiceArea || getConfiguredServiceArea(transitAppConfig);
      const serviceAreaBounds = normalizeMapBounds(serviceArea.bounds);
      const mapCenter = Array.isArray(serviceArea.center) && serviceArea.center.length === 2
        ? serviceArea.center
        : DEFAULT_CENTER;
      const searchParams = new URLSearchParams({
        q: searchText,
        access_token: MAPBOX_TOKEN,
        country: 'it',
        language: getLocale(activeLanguage),
        limit: '5',
        proximity: `${mapCenter[0]},${mapCenter[1]}`,
        types: 'poi,category,address,street,place,city,locality,neighborhood,postcode',
      });

      if (serviceAreaBounds) {
        searchParams.set('bbox', serviceAreaBounds.join(','));
      }

      const response = await fetch(`https://api.mapbox.com/search/searchbox/v1/forward?${searchParams.toString()}`);

      if (!response.ok) {
        throw new Error('Geocoding request failed');
      }

      const data = await response.json();

      if (addressSearchRequestIdRef.current !== currentRequestId) {
        return;
      }

      const results = rankGeocodingResults(
        (data.features || [])
        .map(normalizeGeocodingFeature)
        .filter(Boolean)
        .filter((result) => isPointInsideBounds(result.longitude, result.latitude, serviceAreaBounds)),
        searchText,
        mapCenter,
        serviceAreaBounds
      );

      if (results.length === 0) {
        const areaSuffix = serviceArea.label ? t('inArea', { area: serviceArea.label }) : '';
        setAddressSearchMessage(showEmptyMessage ? t('noAddressFound', { area: areaSuffix }) : '');
        return;
      }

      if (autoSelectFirst || results.length === 1) {
        focusAddressResult(results[0]);
        return;
      }

      setAddressSearchResults(results);
      setAddressSearchMessage(t('chooseAddress'));
    } catch {
      if (addressSearchRequestIdRef.current === currentRequestId) {
        setAddressSearchMessage(t('addressSearchFailed'));
      }
    } finally {
      if (addressSearchRequestIdRef.current === currentRequestId) {
        setIsAddressSearching(false);
      }
    }
  }

  function selectJourneySuggestion(field, result) {
    if (!result) {
      return;
    }

    const nextFromPlace = field === 'from' ? result : journeyFromPlace;
    const nextToPlace = field === 'to' ? result : journeyToPlace;
    const shouldAutoCalculate = shouldAutoCalculateJourneyAfterPlaceSelectionRef.current &&
      nextFromPlace &&
      nextToPlace;

    if (field === 'from') {
      setJourneyFromPlace(result);
      setJourneyFromQuery(result.label);
    } else {
      setJourneyToPlace(result);
      setJourneyToQuery(result.label);
    }

    setJourneySuggestions([]);
    setJourneyActiveField('');
    setJourneyErrorMessage('');

    if (shouldAutoCalculate) {
      shouldAutoCalculateJourneyAfterPlaceSelectionRef.current = false;
      requestJourneyPath(nextFromPlace, nextToPlace);
    }
  }

  async function fetchJourneySuggestions(searchText, field, { autoSelectFirst = false } = {}) {
    if (searchText.trim().length < 3 || typeof fetch !== 'function') {
      setJourneySuggestions([]);
      return null;
    }

    const currentRequestId = journeySearchRequestIdRef.current + 1;
    journeySearchRequestIdRef.current = currentRequestId;
    setIsJourneyGeocoding(true);
    const stopResults = buildJourneyStopSuggestions(transitCatalog.stops, searchText, 5);

    try {
      const serviceArea = activeServiceArea || getConfiguredServiceArea(transitAppConfig);
      const serviceAreaBounds = normalizeMapBounds(serviceArea.bounds);
      const mapCenter = Array.isArray(serviceArea.center) && serviceArea.center.length === 2
        ? serviceArea.center
        : DEFAULT_CENTER;
      const searchParams = new URLSearchParams({
        q: searchText.trim(),
        access_token: MAPBOX_TOKEN,
        country: 'it',
        language: getLocale(activeLanguage),
        limit: '5',
        proximity: `${mapCenter[0]},${mapCenter[1]}`,
        types: 'poi,category,address,street,place,city,locality,neighborhood,postcode',
      });

      if (serviceAreaBounds) {
        searchParams.set('bbox', serviceAreaBounds.join(','));
      }

      const response = await fetch(`https://api.mapbox.com/search/searchbox/v1/forward?${searchParams.toString()}`);

      if (!response.ok) {
        throw new Error('Journey geocoding request failed');
      }

      const data = await response.json();
      const mapResults = rankGeocodingResults(
        (data.features || [])
        .map(normalizeGeocodingFeature)
        .filter(Boolean)
        .filter((result) => isPointInsideBounds(result.longitude, result.latitude, serviceAreaBounds)),
        searchText,
        mapCenter,
        serviceAreaBounds
      );
      const results = [...stopResults, ...mapResults]
        .filter((result, index, allResults) => {
          const key = `${Number(result.longitude).toFixed(5)}:${Number(result.latitude).toFixed(5)}:${result.label}`;
          return allResults.findIndex((candidate) => {
            const candidateKey = `${Number(candidate.longitude).toFixed(5)}:${Number(candidate.latitude).toFixed(5)}:${candidate.label}`;
            return candidateKey === key;
          }) === index;
        })
        .slice(0, 7);

      if (journeySearchRequestIdRef.current !== currentRequestId) {
        return null;
      }

      if (autoSelectFirst) {
        const firstResult = results[0] || null;
        selectJourneySuggestion(field, firstResult);
        return firstResult;
      }

      setJourneyActiveField(field);
      setJourneySuggestions(results);
      return null;
    } catch {
      if (journeySearchRequestIdRef.current === currentRequestId) {
        if (autoSelectFirst && stopResults[0]) {
          selectJourneySuggestion(field, stopResults[0]);
        } else if (stopResults.length > 0) {
          setJourneyActiveField(field);
          setJourneySuggestions(stopResults);
        } else {
          setJourneySuggestions([]);
          setJourneyErrorMessage(t('addressSearchFailed'));
        }
      }
      return autoSelectFirst ? stopResults[0] || null : null;
    } finally {
      if (journeySearchRequestIdRef.current === currentRequestId) {
        setIsJourneyGeocoding(false);
      }
    }
  }

  function handleJourneyQueryChange(field, value) {
    clearTimeout(journeySearchDebounceRef.current);
    setJourneyActiveField(field);
    setJourneySuggestions([]);
    setJourneyErrorMessage('');
    setJourneyNavigation(null);
    shouldAutoCalculateJourneyAfterPlaceSelectionRef.current = Boolean(journeyOverlay?.summary || journeyOptions.length > 0);
    setJourneyOverlay(null);
    setJourneyOptions([]);

    if (field === 'from') {
      setJourneyFromQuery(value);
      setJourneyFromPlace(null);
    } else {
      setJourneyToQuery(value);
      setJourneyToPlace(null);
    }

    if (value.trim().length < 3) {
      journeySearchRequestIdRef.current += 1;
      setIsJourneyGeocoding(false);
      return;
    }

    journeySearchDebounceRef.current = setTimeout(() => {
      fetchJourneySuggestions(value, field);
    }, 320);
  }

  async function useCurrentLocationForJourney() {
    const location = userLocation || (await locateUser());

    if (!location) {
      return;
    }

    const place = {
      id: 'current-location',
      label: t('myLocation'),
      longitude: location.longitude,
      latitude: location.latitude,
      featureType: 'current-location',
    };
    setJourneyFromPlace(place);
    setJourneyFromQuery(place.label);
    setJourneyNavigation(null);
    setJourneySuggestions([]);
    setJourneyActiveField('');

    if ((journeyOverlay?.summary || shouldAutoCalculateJourneyAfterPlaceSelectionRef.current) && journeyToPlace) {
      shouldAutoCalculateJourneyAfterPlaceSelectionRef.current = false;
      requestJourneyPath(place, journeyToPlace);
    }
  }

  function swapJourneyPlaces() {
    const nextFromPlace = journeyToPlace;
    const nextToPlace = journeyFromPlace;
    const shouldAutoCalculate = Boolean(journeyOverlay?.summary && nextFromPlace && nextToPlace);

    setJourneyFromQuery(journeyToQuery);
    setJourneyToQuery(journeyFromQuery);
    setJourneyFromPlace(nextFromPlace);
    setJourneyToPlace(nextToPlace);
    setJourneyNavigation(null);
    setJourneySuggestions([]);
    setJourneyActiveField('');
    setJourneyOverlay(null);
    setJourneyOptions([]);

    if (shouldAutoCalculate) {
      requestJourneyPath(nextFromPlace, nextToPlace);
    }
  }

  function scheduleAddressAutocomplete(value) {
    clearTimeout(addressSearchDebounceRef.current);

    const searchText = value.trim();

    if (searchText.length < 3) {
      addressSearchRequestIdRef.current += 1;
      setIsAddressSearching(false);
      setAddressSearchResults([]);
      setAddressSearchMessage('');
      return;
    }

    addressSearchDebounceRef.current = setTimeout(() => {
      fetchAddressResults(searchText, {
        autocomplete: true,
        autoSelectFirst: false,
        showEmptyMessage: false,
      });
    }, 320);
  }

  function handleAddressQueryChange(value) {
    setAddressQuery(value);
    setAddressSearchMessage('');
    setAddressSearchResults([]);
    setShowLocationHint(false);
    setShowAddressSearchHint(false);
    scheduleAddressAutocomplete(value);
  }

  function openAddressSearch() {
    setIsAddressSearchOpen(true);
    setIsJourneyPlannerOpen(false);
    if (typeof window !== 'undefined' && window.innerWidth <= 640) {
      setIsRoutePathPickerOpen(false);
    }
    setIsFavoritesOpen(false);
    setIsServiceAlertsOpen(false);
    setShowLocationHint(false);
    setShowAddressSearchHint(false);
  }

  function closeAddressSearch() {
    clearTimeout(addressSearchDebounceRef.current);
    addressSearchRequestIdRef.current += 1;
    setIsAddressSearchOpen(false);
    setAddressSearchResults([]);
    setAddressSearchMessage('');
    setIsAddressSearching(false);
    setShowAddressSearchHint(false);
  }

  function toggleServiceAreaPicker() {
    setShowLocationHint(false);
    setShowAddressSearchHint(false);
    setIsFavoritesOpen(false);
    setIsServiceAlertsOpen(false);
    setIsServiceAreaPickerOpen((isOpen) => !isOpen);
  }

  function selectMapStyle(styleId) {
    const nextStyle = mapStyleOptions.find((style) => style.id === styleId);

    if (!nextStyle) {
      return;
    }

    setSelectedMapStyleId(nextStyle.id);
    writeStoredMapStyleId(transitAppConfig, nextStyle.id);
  }

  async function searchAddress(event) {
    event?.preventDefault();
    event?.stopPropagation();
    clearTimeout(addressSearchDebounceRef.current);
    setShowLocationHint(false);
    setShowAddressSearchHint(false);

    if (addressSearchResults.length > 0) {
      focusAddressResult(addressSearchResults[0]);
      return;
    }

    await fetchAddressResults(addressQuery.trim(), {
      autocomplete: false,
      autoSelectFirst: true,
      showEmptyMessage: true,
    });
  }

  function clearAddressSearch() {
    clearTimeout(addressSearchDebounceRef.current);
    addressSearchRequestIdRef.current += 1;
    addressSearchMarkerRef.current?.remove();
    addressSearchMarkerRef.current = null;
    setAddressQuery('');
    setAddressSearchResults([]);
    setAddressSearchMessage('');
    setIsAddressSearching(false);
    setShowAddressSearchHint(false);
  }

  function showUserLocation(longitude, latitude, { focusMap = true } = {}) {
    const mapInstance = mapRef.current;

    if (!mapInstance) {
      return;
    }

    const userPoint = [longitude, latitude];
    const isInsideActiveArea = activeServiceArea
      ? isPointInsideBounds(longitude, latitude, activeServiceArea.bounds)
      : true;
    const defaultServiceArea = serviceAreaOptions[0] || null;
    const isInsideDefaultArea = defaultServiceArea
      ? isPointInsideBounds(longitude, latitude, defaultServiceArea.bounds)
      : true;
    const boundsAreaToRestore = isInsideActiveArea
      ? activeServiceArea
      : isInsideDefaultArea
        ? defaultServiceArea
        : null;

    if (focusMap && !isInsideActiveArea && defaultServiceArea?.id) {
      skipNextServiceAreaCameraRef.current = true;
      setSelectedServiceAreaId(defaultServiceArea.id);
      writeStoredServiceAreaId(transitAppConfig, defaultServiceArea.id);
    }

    setUserLocation({ longitude, latitude });

    if (!userLocationMarkerRef.current) {
      const markerElement = document.createElement('div');
      markerElement.className = 'user-location-marker';

      userLocationMarkerRef.current = new mapboxgl.Marker({
        element: markerElement,
        anchor: 'center',
      })
        .setLngLat([longitude, latitude])
        .addTo(mapRef.current);
    } else {
      userLocationMarkerRef.current.setLngLat([longitude, latitude]);
    }

    if (!focusMap) {
      return;
    }

    isUserLocationCameraActiveRef.current = true;
    window.clearTimeout(finalizeUserLocationCameraTimeoutRef.current);
    window.clearTimeout(restoreUserLocationBoundsTimeoutRef.current);
    window.clearTimeout(releaseUserLocationCameraTimeoutRef.current);

    if (typeof mapInstance.setMaxBounds === 'function') {
      mapInstance.setMaxBounds(null);
    }

    if (typeof mapInstance.setMinZoom === 'function') {
      const nextMinZoom = Number(
        boundsAreaToRestore?.minZoom ||
          transitAppConfig.map?.minZoom ||
          5
      );

      if (Number.isFinite(nextMinZoom)) {
        mapInstance.setMinZoom(nextMinZoom);
      }
    }

    if (typeof mapInstance.stop === 'function') {
      mapInstance.stop();
    }

    if (typeof mapInstance.resize === 'function') {
      mapInstance.resize();
    }

    const currentZoom = Number(mapInstance.getZoom?.() || 0);
    const targetZoom = Math.max(currentZoom, 16);

    mapInstance.flyTo({
      center: userPoint,
      zoom: targetZoom,
      duration: 850,
      essential: true,
    });

    finalizeUserLocationCameraTimeoutRef.current = window.setTimeout(() => {
      if (mapRef.current !== mapInstance) {
        return;
      }

      const cameraOptions = {
        center: userPoint,
        zoom: targetZoom,
      };

      if (typeof mapInstance.jumpTo === 'function') {
        mapInstance.jumpTo(cameraOptions);
      } else if (typeof mapInstance.easeTo === 'function') {
        mapInstance.easeTo({
          ...cameraOptions,
          duration: 0,
          essential: true,
        });
      }
    }, USER_LOCATION_FINALIZE_CAMERA_MS);

    if (typeof mapInstance.setMaxBounds === 'function' && boundsAreaToRestore?.bounds) {
      restoreUserLocationBoundsTimeoutRef.current = window.setTimeout(() => {
        if (mapRef.current === mapInstance) {
          mapInstance.setMaxBounds(toMapboxMaxBounds(boundsAreaToRestore.bounds));
        }
      }, USER_LOCATION_RESTORE_BOUNDS_MS);
    }

    releaseUserLocationCameraTimeoutRef.current = window.setTimeout(() => {
      if (mapRef.current === mapInstance) {
        isUserLocationCameraActiveRef.current = false;
      }
    }, USER_LOCATION_CAMERA_LOCK_MS);
  }

  async function locateUser({ focusMap = true } = {}) {
    setShowLocationHint(false);
    setShowAddressSearchHint(false);
    setIsServiceAreaPickerOpen(false);

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setUserLocationMessage(t('geolocationUnsupported'));
      return;
    }

    setUserLocationMessage('');
    setIsLocatingUser(true);

    try {
      const position = await requestCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });

      setUserLocationMessage(t('locationFound'));
      showUserLocation(position.coords.longitude, position.coords.latitude, { focusMap });
      return { longitude: position.coords.longitude, latitude: position.coords.latitude };
    } catch (initialError) {
      const permissionState = await getGeolocationPermissionState();

      if (permissionState !== 'denied') {
        try {
          const fallbackPosition = await requestCurrentPosition({
            enableHighAccuracy: false,
            timeout: 15000,
            maximumAge: 60000,
          });

          setUserLocationMessage(t('locationFound'));
          showUserLocation(fallbackPosition.coords.longitude, fallbackPosition.coords.latitude, { focusMap });
          return { longitude: fallbackPosition.coords.longitude, latitude: fallbackPosition.coords.latitude };
        } catch (fallbackError) {
          setUserLocationMessage(getGeolocationMessage(fallbackError, permissionState));
          return;
        }
      }

      setUserLocationMessage(getGeolocationMessage(initialError, permissionState));
      return;
    } finally {
      setIsLocatingUser(false);
    }
  }

  async function toggleMapFullscreen() {
    const workspace = mapWorkspaceRef.current;
    const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;

    try {
      if (fullscreenElement) {
        const exitFullscreen = document.exitFullscreen || document.webkitExitFullscreen;
        await exitFullscreen?.call(document);
        setIsMapFullscreen(false);
      } else if (workspace) {
        const requestFullscreen = workspace.requestFullscreen || workspace.webkitRequestFullscreen;
        if (requestFullscreen) {
          await requestFullscreen.call(workspace);
          setIsMapFullscreen(true);
        } else {
          setIsMapFullscreen(true);
        }
      }
    } catch {
      setIsMapFullscreen((currentValue) => !currentValue);
    } finally {
      setIsMapToolsOpen(false);
    }
  }

  function getJourneyRequestTimeParams() {
    if (journeyTimeMode === JOURNEY_TIME_DEPART_NOW) {
      return { timeMode: JOURNEY_TIME_DEPART_NOW };
    }

    const normalizedValue = normalizeJourneyDateTimeValue(journeyDateTime, journeyTimeMode);

    if (
      isDateInputInPast(normalizedValue.slice(0, 10)) ||
      isDateInputAfter(normalizedValue.slice(0, 10), gtfsServiceWindow?.maxDate)
    ) {
      return null;
    }

    const requestedDate = new Date(normalizedValue);
    const requestedUnix = Math.floor(requestedDate.getTime() / 1000);

    if (!Number.isFinite(requestedUnix)) {
      return null;
    }

    return {
      timeMode: journeyTimeMode,
      time: requestedUnix,
    };
  }

  function handleJourneyTimeModeChange(nextMode) {
    setJourneyTimeMode(nextMode);
    setJourneyDateTime((currentValue) => normalizeJourneyDateTimeValue(currentValue, nextMode));
  }

  function buildJourneyShareUrl() {
    if (typeof window === 'undefined' || !journeyFromPlace || !journeyToPlace) {
      return '';
    }

    const url = new URL(window.location.href);
    url.pathname = '/';
    url.search = '';
    url.hash = '';
    const params = url.searchParams;
    const timeParams = getJourneyRequestTimeParams();

    params.set('journey', '1');
    params.set('fromLat', String(journeyFromPlace.latitude));
    params.set('fromLng', String(journeyFromPlace.longitude));
    params.set('toLat', String(journeyToPlace.latitude));
    params.set('toLng', String(journeyToPlace.longitude));
    params.set('fromLabel', getJourneyPlaceName(journeyFromPlace, 'origin'));
    params.set('toLabel', getJourneyPlaceName(journeyToPlace, 'destination'));

    if (timeParams?.timeMode) {
      params.set('timeMode', timeParams.timeMode);
    }

    if (timeParams?.time) {
      params.set('time', String(timeParams.time));
    }

    return url.toString();
  }

  async function copyJourneyShareLink() {
    const shareUrl = buildJourneyShareUrl();

    if (!shareUrl) {
      return;
    }

    try {
      await navigator.clipboard?.writeText(shareUrl);
      setJourneyShareMessage(t('journeyLinkCopied'));
    } catch {
      setJourneyShareMessage(shareUrl);
    }

    window.setTimeout(() => setJourneyShareMessage(''), 3200);
  }

  function getRecentJourneyRequestTimeParams(recentJourney) {
    const timeMode = normalizeJourneyTimeMode(recentJourney?.timeMode);

    if (timeMode === JOURNEY_TIME_DEPART_NOW) {
      return { timeMode: JOURNEY_TIME_DEPART_NOW };
    }

    const time = Number(recentJourney?.time);

    if (!Number.isFinite(time)) {
      return { timeMode: JOURNEY_TIME_DEPART_NOW };
    }

    const inputValue = getDefaultJourneyDateTimeValue(new Date(time * 1000));
    const dateValue = inputValue.slice(0, 10);

    if (isDateInputInPast(dateValue) || isDateInputAfter(dateValue, gtfsServiceWindow?.maxDate)) {
      return { timeMode: JOURNEY_TIME_DEPART_NOW };
    }

    return { timeMode, time };
  }

  function getRecentJourneyTimeLabel(recentJourney) {
    const timeParams = getRecentJourneyRequestTimeParams(recentJourney);

    if (timeParams.timeMode === JOURNEY_TIME_DEPART_NOW || !timeParams.time) {
      return t('journeyDepartNow');
    }

    if (timeParams.timeMode === JOURNEY_TIME_LAST) {
      return `${t('journeyLastAvailable')} · ${formatStopPageDate(getDefaultJourneyDateTimeValue(new Date(timeParams.time * 1000)).slice(0, 10))}`;
    }

    const labelKey = timeParams.timeMode === JOURNEY_TIME_ARRIVE_BY
      ? 'journeyArriveByOption'
      : 'journeyDepartAtOption';

    return `${t(labelKey)} · ${formatTimestamp(timeParams.time)}`;
  }

  function persistRecentJourney(fromPlace, toPlace, timeParams, option) {
    const recentJourney = buildRecentJourneyEntry(fromPlace, toPlace, timeParams, option);

    if (!recentJourney) {
      return;
    }

    setRecentJourneys((currentJourneys) => {
      const nextJourneys = [
        recentJourney,
        ...currentJourneys.filter((journey) => journey.id !== recentJourney.id),
      ].slice(0, JOURNEY_RECENT_LIMIT);
      writeStoredRecentJourneys(nextJourneys);
      return nextJourneys;
    });
  }

  function clearRecentJourneys() {
    setRecentJourneys([]);
    writeStoredRecentJourneys([]);
  }

  function toggleFavoriteJourney(journeyEntry) {
    const favoriteJourney = normalizeFavoriteJourney(journeyEntry);

    if (!favoriteJourney) {
      return;
    }

    setFavoriteJourneys((currentJourneys) => {
      const isFavorite = currentJourneys.some((journey) => journey.id === favoriteJourney.id);
      const nextJourneys = isFavorite
        ? currentJourneys.filter((journey) => journey.id !== favoriteJourney.id)
        : [favoriteJourney, ...currentJourneys.filter((journey) => journey.id !== favoriteJourney.id)].slice(0, FAVORITE_JOURNEYS_LIMIT);
      writeStoredFavoriteJourneys(nextJourneys);
      return nextJourneys;
    });
  }

  async function selectFavoriteJourney(journey) {
    const favoriteJourney = normalizeFavoriteJourney(journey);

    if (!favoriteJourney) {
      return;
    }

    const timeParams = { timeMode: JOURNEY_TIME_DEPART_NOW };
    setIsJourneyPlannerOpen(true);
    setIsFavoritesOpen(false);
    setJourneyFromPlace(favoriteJourney.from);
    setJourneyFromQuery(getJourneyPlaceName(favoriteJourney.from, 'origin'));
    setJourneyToPlace(favoriteJourney.to);
    setJourneyToQuery(getJourneyPlaceName(favoriteJourney.to, 'destination'));
    setJourneyTimeMode(JOURNEY_TIME_DEPART_NOW);
    setJourneyDateTime(getDefaultJourneyDateTimeValue());
    setJourneyActiveField('');
    setJourneySuggestions([]);
    setJourneyNavigation(null);
    await requestJourneyPath(favoriteJourney.from, favoriteJourney.to, {
      timeParamsOverride: timeParams,
    });
  }

  async function selectRecentJourney(recentJourney) {
    const fromPlace = normalizeStoredJourneyPlace(recentJourney?.from);
    const toPlace = normalizeStoredJourneyPlace(recentJourney?.to);

    if (!fromPlace || !toPlace) {
      return;
    }

    const timeParams = getRecentJourneyRequestTimeParams(recentJourney);
    setJourneyFromPlace(fromPlace);
    setJourneyFromQuery(getJourneyPlaceName(fromPlace, 'origin'));
    setJourneyToPlace(toPlace);
    setJourneyToQuery(getJourneyPlaceName(toPlace, 'destination'));
    setJourneyTimeMode(timeParams.timeMode);
    setJourneyDateTime(
      timeParams.time
        ? getDefaultJourneyDateTimeValue(new Date(timeParams.time * 1000))
        : getDefaultJourneyDateTimeValue()
    );
    await requestJourneyPath(fromPlace, toPlace, {
      timeParamsOverride: timeParams,
    });
  }

  function renderJourneyFavoriteJourneys() {
    const canSaveActiveJourney = Boolean(activeFavoriteJourneyEntry);
    const isActiveJourneyFavorite = Boolean(activeFavoriteJourneyEntry && favoriteJourneySet.has(activeFavoriteJourneyEntry.id));

    if (favoriteJourneyEntries.length === 0 && !canSaveActiveJourney) {
      return null;
    }

    return (
      <section className="journey-favorite-journeys" aria-label={t('favoriteJourneys')}>
        <div className="journey-favorite-places__title">
          <span>{t('favoriteJourneys')}</span>
        </div>
        {favoriteJourneyEntries.length > 0 ? (
          <div className="journey-favorite-journeys__list">
            {favoriteJourneyEntries.map((journey) => (
              <button
                aria-label={t('favoriteJourneyUse')}
                key={`journey-favorite-journey-${journey.id}`}
                onClick={() => selectFavoriteJourney(journey)}
                type="button"
              >
                <span>
                  {journey.summary.routes.length ? (
                    <span className="journey-recent__routes">
                      {journey.summary.routes.map((routeLabel) => (
                        <strong key={routeLabel} style={getRouteBadgeStyle(routeLabel)}>
                          {routeLabel}
                        </strong>
                      ))}
                    </span>
                  ) : null}
                  {journey.summary.durationSeconds ? (
                    <em>{formatDurationMinutes(journey.summary.durationSeconds)}</em>
                  ) : null}
                </span>
                <b>
                  {getJourneyPlaceName(journey.from, 'origin')} →{' '}
                  {getJourneyPlaceName(journey.to, 'destination')}
                </b>
              </button>
            ))}
          </div>
        ) : null}
        {canSaveActiveJourney ? (
          <button
            aria-pressed={isActiveJourneyFavorite}
            className={`journey-favorite-journeys__save ${isActiveJourneyFavorite ? 'is-active' : ''}`}
            onClick={() => toggleFavoriteJourney(activeFavoriteJourneyEntry)}
            type="button"
          >
            {isActiveJourneyFavorite ? t('favoriteJourneySaved') : t('favoriteJourneySave')}
          </button>
        ) : null}
      </section>
    );
  }

  function renderJourneyFavoritePlaces() {
    const selectedPlaces = [
      {
        key: 'from',
        place: normalizeFavoritePlace(journeyFromPlace),
        saveLabel: t('favoritePlaceSaveFrom'),
        savedLabel: t('favoritePlaceSavedFrom'),
      },
      {
        key: 'to',
        place: normalizeFavoritePlace(journeyToPlace),
        saveLabel: t('favoritePlaceSaveTo'),
        savedLabel: t('favoritePlaceSavedTo'),
      },
    ].filter(({ place }) => place);
    const hasSelectedUnsavedPlace = selectedPlaces.some(({ place }) => !favoritePlaceSet.has(place.id));

    if (favoritePlaceEntries.length === 0 && !hasSelectedUnsavedPlace) {
      return null;
    }

    return (
      <section className="journey-favorite-places" aria-label={t('favoritePlaces')}>
        <div className="journey-favorite-places__title">
          <span>{t('favoritePlaces')}</span>
        </div>
        {favoritePlaceEntries.length > 0 ? (
          <div className="journey-favorite-places__list">
            {favoritePlaceEntries.map((place) => (
              <div className="journey-favorite-place" key={`journey-favorite-place-${place.id}`}>
                <span>{getJourneyPlaceName(place, 'destination')}</span>
                <div>
                  <button onClick={() => selectFavoritePlaceForJourney('from', place)} type="button">
                    {t('journeyFrom')}
                  </button>
                  <button onClick={() => selectFavoritePlaceForJourney('to', place)} type="button">
                    {t('journeyTo')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {hasSelectedUnsavedPlace ? (
          <div className="journey-favorite-places__save">
            {selectedPlaces.map(({ key, place, saveLabel, savedLabel }) => {
              const isFavorite = favoritePlaceSet.has(place.id);

              return (
                <button
                  aria-pressed={isFavorite}
                  className={isFavorite ? 'is-active' : ''}
                  key={`journey-save-place-${key}`}
                  onClick={() => toggleFavoritePlace(place)}
                  type="button"
                >
                  {isFavorite ? savedLabel : saveLabel}
                </button>
              );
            })}
          </div>
        ) : null}
      </section>
    );
  }

  function renderJourneyTimeControls(compact = false) {
    return (
      <>
        <div className={`journey-planner__time ${compact ? 'journey-planner__time--compact' : ''}`}>
          <label>
            <span>{t('journeyTimeMode')}</span>
            <select
              aria-label={t('journeyTimeMode')}
              onChange={(event) => handleJourneyTimeModeChange(event.target.value)}
              value={journeyTimeMode}
            >
              <option value={JOURNEY_TIME_DEPART_NOW}>{t('journeyDepartNow')}</option>
              <option value={JOURNEY_TIME_DEPART_AT}>{t('journeyDepartAtOption')}</option>
              <option value={JOURNEY_TIME_ARRIVE_BY}>{t('journeyArriveByOption')}</option>
              <option value={JOURNEY_TIME_LAST}>{t('journeyLastAvailable')}</option>
            </select>
          </label>
          {journeyTimeMode !== JOURNEY_TIME_DEPART_NOW ? (
            <label>
              <span>{journeyTimeMode === JOURNEY_TIME_LAST ? t('journeyDay') : t('journeyDayTime')}</span>
              <input
                max={
                  gtfsServiceWindow?.maxDate
                    ? `${gtfsServiceWindow.maxDate}${journeyTimeMode === JOURNEY_TIME_LAST ? '' : 'T23:59'}`
                    : undefined
                }
                min={getDefaultJourneyDateTimeValue().slice(0, journeyTimeMode === JOURNEY_TIME_LAST ? 10 : 16)}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setJourneyDateTime(
                    journeyTimeMode === JOURNEY_TIME_LAST ? `${nextValue}T23:59` : nextValue
                  );
                }}
                type={getJourneyTimeInputType(journeyTimeMode)}
                value={
                  journeyTimeMode === JOURNEY_TIME_LAST
                    ? normalizeJourneyDateTimeValue(journeyDateTime, journeyTimeMode).slice(0, 10)
                    : normalizeJourneyDateTimeValue(journeyDateTime, journeyTimeMode)
                }
              />
            </label>
          ) : null}
        </div>
        {gtfsServiceWindow?.maxDate && !compact ? (
          <small className="journey-planner__coverage">
            {t('gtfsCoverageUntil', { date: formatStopPageDate(gtfsServiceWindow.maxDate) })}
          </small>
        ) : null}
      </>
    );
  }

  async function retryJourneyWithQuickTime(nextMode, minutesFromNow = 0) {
    if (!journeyFromPlace || !journeyToPlace) {
      setJourneyErrorMessage(t('journeyChooseBothPlaces'));
      return;
    }

    const nextDateTime = nextMode === JOURNEY_TIME_LAST
      ? `${getDateInputValue()}T23:59`
      : getDateTimeValueAfterMinutes(minutesFromNow);
    const requestedUnix = Math.floor(new Date(nextDateTime).getTime() / 1000);
    const nextTimeParams = nextMode === JOURNEY_TIME_DEPART_NOW
      ? { timeMode: JOURNEY_TIME_DEPART_NOW }
      : { timeMode: nextMode, time: requestedUnix };

    setJourneyTimeMode(nextMode);
    setJourneyDateTime(nextDateTime);
    await requestJourneyPath(journeyFromPlace, journeyToPlace, {
      timeParamsOverride: nextTimeParams,
    });
  }

  async function requestJourneyPath(
    fromPlace,
    toPlace,
    { background = false, preferredOption = null, showBackgroundErrors = false, timeParamsOverride = null } = {}
  ) {
    if (!fromPlace || !toPlace) {
      setJourneyErrorMessage(t('journeyChooseBothPlaces'));
      return;
    }

    const journeyTimeParams = timeParamsOverride || getJourneyRequestTimeParams();

    if (!journeyTimeParams) {
      setJourneyErrorMessage(
        isDateInputAfter(normalizeJourneyDateTimeValue(journeyDateTime, journeyTimeMode).slice(0, 10), gtfsServiceWindow?.maxDate)
          ? t('journeyDateOutOfRange')
          : t('journeyInvalidTime')
      );
      return;
    }

    lastJourneyRequestSignatureRef.current = getJourneyRequestSignature(fromPlace, toPlace, journeyTimeParams);

    if (background) {
      setIsJourneyRefreshing(true);
      if (showBackgroundErrors) {
        setJourneyErrorMessage('');
      }
    } else {
      setJourneyNavigation(null);
      setIsJourneyLoading(true);
      setJourneyLoadingStep(JOURNEY_LOADING_STEPS[0]);
      setJourneyErrorMessage('');
    }
    const requestId = journeyPathRequestIdRef.current + 1;
    journeyPathRequestIdRef.current = requestId;
    if (journeyPathAbortControllerRef.current) {
      journeyPathAbortControllerRef.current.abort();
    }
    const abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;
    journeyPathAbortControllerRef.current = abortController;
    const loadingTimers = background
      ? []
      : JOURNEY_LOADING_STEPS.slice(1).map((step, index) =>
          window.setTimeout(() => {
            if (journeyPathRequestIdRef.current === requestId) {
              setJourneyLoadingStep(step);
            }
          }, (index + 1) * 900)
        );

    try {
      const response = await axios.get(`${API_BASE_URL}/api/journey-path`, {
        params: {
          fromLat: fromPlace.latitude,
          fromLng: fromPlace.longitude,
          toLat: toPlace.latitude,
          toLng: toPlace.longitude,
          ...journeyTimeParams,
        },
        signal: abortController?.signal,
        timeout: JOURNEY_PATH_REQUEST_TIMEOUT_MS,
      });

      if (journeyPathRequestIdRef.current !== requestId) {
        return;
      }

      if (response.data?.error) {
        if (!background || showBackgroundErrors) {
          setJourneyOverlay(null);
          setJourneyOptions([]);
          setJourneyErrorMessage(response.data.error.message || t('journeyUnavailable'));
        }
        return;
      }

      const rawOptions = response.data?.itineraries?.length
        ? response.data.itineraries
        : [response.data].filter((option) => option?.summary);
      const options = rawOptions.map((option) => {
        const legs = (option.legs || []).map((leg) => ({
          ...leg,
          color: leg.mode === 'WALK' ? '#18212b' : getRouteColor(leg.routeId, leg.route),
        }));
        return { ...option, legs, features: buildJourneyFeatures(legs) };
      }).sort(compareJourneyOptions);
      const preferredSignature = getJourneyOptionSignature(preferredOption);
      const firstOption = options.find((option) => getJourneyOptionSignature(option) === preferredSignature) || options[0] || null;
      setJourneyOptions(options);
      setJourneyOverlay(firstOption);
      setJourneyUpdatedAt(Math.floor(Date.now() / 1000));
      if (!background) {
        setIsJourneyPlannerOpen(true);
        setJourneySuggestions([]);
        setJourneyActiveField('');
        fitMapToJourney(mapRef.current, firstOption?.features || []);
        persistRecentJourney(fromPlace, toPlace, journeyTimeParams, firstOption);
      }

      Promise.all(options.map(async (option) => {
        const legs = await Promise.all(option.legs.map(getWalkingDirections));
        return { ...option, legs, features: buildJourneyFeatures(legs) };
      })).then((enrichedOptions) => {
        if (journeyPathRequestIdRef.current !== requestId) {
          return;
        }

        setJourneyOptions(enrichedOptions);
        setJourneyOverlay((currentOption) => {
          const currentSignature = getJourneyOptionSignature(currentOption);
          return enrichedOptions.find((option) => getJourneyOptionSignature(option) === currentSignature)
            || enrichedOptions[0]
            || null;
        });
      });
    } catch (error) {
      if (journeyPathRequestIdRef.current !== requestId) {
        return;
      }

      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        return;
      }

      if (!background || showBackgroundErrors) {
        setJourneyOverlay(null);
        setJourneyOptions([]);
        setJourneyErrorMessage(
          error?.code === 'ECONNABORTED'
            ? t('journeyDrawFailed')
            : error?.response?.data?.error ?? t('journeyDrawFailed')
        );
      }
    } finally {
      loadingTimers.forEach((timerId) => window.clearTimeout(timerId));
      if (journeyPathAbortControllerRef.current === abortController) {
        journeyPathAbortControllerRef.current = null;
      }
      if (journeyPathRequestIdRef.current === requestId) {
        if (background) {
          setIsJourneyRefreshing(false);
        } else {
          setIsJourneyLoading(false);
          setJourneyLoadingStep(JOURNEY_LOADING_STEPS[0]);
        }
      }
    }
  }

  journeyRefreshRef.current = async () => {
    if (journeyFromPlace && journeyToPlace && journeyOverlay) {
      await requestJourneyPath(journeyFromPlace, journeyToPlace, {
        background: true,
        preferredOption: journeyOverlay,
      });
    }
  };

  async function refreshJourneyManually() {
    if (isJourneyNavigationActive) {
      return;
    }

    if (!journeyFromPlace || !journeyToPlace || !journeyOverlay) {
      return;
    }

    window.clearTimeout(journeyAutoUpdateDebounceRef.current);
    await requestJourneyPath(journeyFromPlace, journeyToPlace, {
      background: true,
      preferredOption: journeyOverlay,
      showBackgroundErrors: true,
    });
  }

  useEffect(() => {
    if (
      journeyTimeMode !== JOURNEY_TIME_DEPART_NOW ||
      isJourneyNavigationActive ||
      !journeyOverlay ||
      !journeyFromPlace ||
      !journeyToPlace
    ) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      journeyRefreshRef.current();
    }, JOURNEY_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [isJourneyNavigationActive, journeyFromPlace, journeyOverlay, journeyTimeMode, journeyToPlace]);

  useEffect(() => {
    if (isJourneyNavigationActive || !journeyOverlay?.summary || !journeyFromPlace || !journeyToPlace) {
      return undefined;
    }

    const timeParams = getJourneyRequestTimeParams();

    if (!timeParams) {
      return undefined;
    }

    const requestSignature = getJourneyRequestSignature(journeyFromPlace, journeyToPlace, timeParams);

    if (!requestSignature || requestSignature === lastJourneyRequestSignatureRef.current) {
      return undefined;
    }

    window.clearTimeout(journeyAutoUpdateDebounceRef.current);
    journeyAutoUpdateDebounceRef.current = window.setTimeout(() => {
      requestJourneyPath(journeyFromPlace, journeyToPlace, {
        background: true,
        preferredOption: journeyOverlay,
        showBackgroundErrors: true,
        timeParamsOverride: timeParams,
      });
    }, 520);

    return () => window.clearTimeout(journeyAutoUpdateDebounceRef.current);
    // Automatic journey updates intentionally track the rendered time/place state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gtfsServiceWindow?.maxDate, isJourneyNavigationActive, journeyDateTime, journeyFromPlace, journeyOverlay, journeyTimeMode, journeyToPlace]);

  async function handleJourneySubmit(event) {
    event?.preventDefault();
    clearTimeout(journeySearchDebounceRef.current);

    if (!journeyFromPlace) {
      setJourneyErrorMessage(t('journeySelectSuggestion'));
      await fetchJourneySuggestions(journeyFromQuery, 'from');
      return;
    }

    if (!journeyToPlace) {
      setJourneyErrorMessage(t('journeySelectSuggestion'));
      await fetchJourneySuggestions(journeyToQuery, 'to');
      return;
    }

    await requestJourneyPath(journeyFromPlace, journeyToPlace);
  }

  function selectJourneyOption(option) {
    setJourneyNavigation(null);
    setJourneyOverlay(option);
    setJourneyErrorMessage('');
    fitMapToJourney(mapRef.current, option?.features || []);
  }

  function startJourneyNavigation() {
    if (!journeyOverlay?.summary || !journeyFirstTransitLeg) {
      return;
    }

    const optionSnapshot = {
      ...journeyOverlay,
      legs: (journeyOverlay.legs || []).map((leg) => ({ ...leg })),
      features: (journeyOverlay.features || []).map((feature) => ({ ...feature })),
    };

    setJourneyNavigation({
      activeLegId: journeyFirstTransitLeg.id,
      option: optionSnapshot,
      startedAt: Math.floor(Date.now() / 1000),
    });
    setJourneyErrorMessage('');
    setSelectedBusId('');
    setRouteContext(null);
    setRouteContextError('');
    setAreLiveBusesVisible(true);
    loadBuses('background');
    locateUser({ focusMap: false });
    fitMapToJourney(mapRef.current, optionSnapshot.features || []);
  }

  function endJourneyNavigation() {
    setIsJourneyPlannerOpen(false);
    setJourneyOverlay(null);
    setJourneyOptions([]);
    setJourneyErrorMessage('');
    setJourneyShareMessage('');
    setSelectedBusId('');
    setRouteContext(null);
    setRouteContextError('');
    setJourneyNavigation(null);
  }

  function toggleFavoriteRoute(routeOptionOrLabel) {
    const routeKey = getRouteFavoriteKey(routeOptionOrLabel);

    if (!routeKey) {
      return;
    }

    setFavoriteRoutes((currentRoutes) => {
      const isFavorite = currentRoutes.includes(routeKey);
      const nextRoutes = isFavorite
        ? currentRoutes.filter((routeLabel) => routeLabel !== routeKey)
        : [routeKey, ...currentRoutes];
      writeStoredFavoriteRoutes(nextRoutes);
      return nextRoutes;
    });
  }

  function toggleFavoriteStop(stop) {
    const favoriteStop = normalizeFavoriteStop(stop);

    if (!favoriteStop) {
      return;
    }

    setFavoriteStops((currentStops) => {
      const isFavorite = currentStops.some((currentStop) => currentStop.id === favoriteStop.id);
      const nextStops = isFavorite
        ? currentStops.filter((currentStop) => currentStop.id !== favoriteStop.id)
        : [favoriteStop, ...currentStops.filter((currentStop) => currentStop.id !== favoriteStop.id)].slice(0, FAVORITE_STOPS_LIMIT);
      writeStoredFavoriteStops(nextStops);
      return nextStops;
    });
  }

  function toggleFavoritePlace(place) {
    const favoritePlace = normalizeFavoritePlace(place);

    if (!favoritePlace) {
      return;
    }

    setFavoritePlaces((currentPlaces) => {
      const isFavorite = currentPlaces.some((currentPlace) => currentPlace.id === favoritePlace.id);
      const nextPlaces = isFavorite
        ? currentPlaces.filter((currentPlace) => currentPlace.id !== favoritePlace.id)
        : [favoritePlace, ...currentPlaces.filter((currentPlace) => currentPlace.id !== favoritePlace.id)].slice(0, FAVORITE_PLACES_LIMIT);
      writeStoredFavoritePlaces(nextPlaces);
      return nextPlaces;
    });
  }

  function selectFavoritePlaceForJourney(field, place) {
    const favoritePlace = normalizeFavoritePlace(place);

    if (!favoritePlace) {
      return;
    }

    const nextFromPlace = field === 'from' ? favoritePlace : journeyFromPlace;
    const nextToPlace = field === 'to' ? favoritePlace : journeyToPlace;
    const shouldAutoCalculate = Boolean(journeyOverlay?.summary && nextFromPlace && nextToPlace);

    setIsJourneyPlannerOpen(true);
    setIsFavoritesOpen(false);
    setJourneyActiveField('');
    setJourneySuggestions([]);
    setJourneyNavigation(null);
    setJourneyErrorMessage('');
    setJourneyOverlay(null);
    setJourneyOptions([]);

    if (field === 'from') {
      setJourneyFromPlace(favoritePlace);
      setJourneyFromQuery(getJourneyPlaceName(favoritePlace, 'origin'));
    } else {
      setJourneyToPlace(favoritePlace);
      setJourneyToQuery(getJourneyPlaceName(favoritePlace, 'destination'));
    }

    if (shouldAutoCalculate) {
      requestJourneyPath(nextFromPlace, nextToPlace);
    }
  }

  function focusFavoritePlace(place) {
    const favoritePlace = normalizeFavoritePlace(place);

    if (!favoritePlace) {
      return;
    }

    focusAddressResult({
      ...favoritePlace,
      featureType: 'poi',
    });
    setIsFavoritesOpen(false);
  }

  function focusFavoriteStop(stop) {
    const favoriteStop = normalizeFavoriteStop(stop);

    if (!favoriteStop) {
      return;
    }

    const catalogStop = transitCatalog.stops.find((candidateStop) => String(candidateStop.id) === favoriteStop.id);
    const nextStop = catalogStop || favoriteStop;
    selectStop(nextStop);
    setIsFavoritesOpen(false);
    mapRef.current?.easeTo({
      center: [nextStop.longitude, nextStop.latitude],
      zoom: Math.max(mapRef.current.getZoom(), 15),
      duration: 700,
      essential: true,
    });
  }

  loadJourneyPathRef.current = async (stop) => {
    if (!userLocation) {
      setUserLocationMessage(t('enableLocationForJourney'));
      return;
    }

    const fromPlace = {
      id: 'current-location',
      label: t('myLocation'),
      longitude: userLocation.longitude,
      latitude: userLocation.latitude,
    };
    const toPlace = {
      id: `stop-${stop.id}`,
      label: stop.name,
      longitude: stop.longitude,
      latitude: stop.latitude,
    };
    setJourneyFromPlace(fromPlace);
    setJourneyFromQuery(fromPlace.label);
    setJourneyToPlace(toPlace);
    setJourneyToQuery(toPlace.label);
    await requestJourneyPath(fromPlace, toPlace);
  };

  function handleRouteSelection(routeId, { focusStop = null } = {}) {
    if (routeId && typeof window !== 'undefined' && window.history?.pushState) {
      window.history.pushState({ tepMapFocus: 'route' }, '', window.location.href);
    }

    const routeOption = getRouteOptionByRouteId(routeOptions, routeId);
    const nextRouteIds = routeOption?.routeIds ?? (routeId ? [routeId] : []);
    const nextRouteIdSet = new Set(nextRouteIds);

    startTransition(() => {
      setSelectedRouteId(routeId);
      setSelectedBusId('');
    });

    setSelectedStop(null);
    setJourneyOverlay(null);
    setJourneyErrorMessage('');
    setShowAllRoutePaths(false);
    setIsRoutePathPickerOpen(false);
    setIsFavoritesOpen(false);
    setIsServiceAlertsOpen(false);
    setAllRoutePathsError('');
    setAreLiveBusesVisible(true);

    if (!routeId) {
      setAreMapStopsVisible(true);
      clearMapTraceData(mapRef.current);
    }

    const busesForRoute = busesRef.current.filter(
      (bus) => !nextRouteIdSet.size || nextRouteIdSet.has(bus.routeId)
    );
    const stopsForRoute = routeId
      ? transitCatalog.stops.filter(
          (stop) =>
            stop.routeIds.length > 0 && stop.routeIds.some((stopRouteId) => nextRouteIdSet.has(stopRouteId))
        )
      : [];

    if (focusStop && fitMapToRouteFromStop(mapRef.current, focusStop, stopsForRoute)) {
      return;
    }

    if (stopsForRoute.length > 0) {
      fitMapToStops(mapRef.current, stopsForRoute);
      return;
    }

    fitMapToBuses(mapRef.current, busesForRoute);
  }

  handleRouteSelectionRef.current = handleRouteSelection;

  useEffect(() => {
    loadAppConfig();
    // Bootstraps the configured agency once; area-scoped data is loaded by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAppConfigReady) {
      return undefined;
    }

    hasAutoFittedRef.current = false;
    historyRef.current = new Map();
    setBuses([]);
    setServiceAlerts([]);
    setSelectedBusId('');
    setSelectedStop(null);
    setTransitCatalog({ meta: {}, routes: [], stops: [] });
    setAllRoutePaths([]);
    setSelectedRoutePathIds([]);
    hasInitializedRoutePathSelectionRef.current = false;
    isRequestRunningRef.current = false;
    clearMapTraceData(mapRef.current);
    loadTransitCatalog();
    loadBuses('initial');

    return undefined;
    // Reload static and realtime data whenever the selected service area changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeServiceAreaId, isAppConfigReady, transitAppConfig.id]);

  useEffect(() => {
    if (!isAppConfigReady) {
      return undefined;
    }

    const refreshIntervalMs = isJourneyNavigationActive
      ? JOURNEY_NAVIGATION_REALTIME_INTERVAL_MS
      : REFRESH_INTERVAL_MS;
    const refreshId = window.setInterval(() => {
      loadBuses('background');
    }, refreshIntervalMs);

    return () => window.clearInterval(refreshId);
    // Refresh uses the current area-scoped params from this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeServiceAreaId, isAppConfigReady, isJourneyNavigationActive]);

  useEffect(() => {
    if (
      !isJourneyNavigationActive ||
      typeof navigator === 'undefined' ||
      !navigator.geolocation
    ) {
      return undefined;
    }

    const handlePosition = (position) => {
      showUserLocation(position.coords.longitude, position.coords.latitude, { focusMap: false });
    };

    const handleError = () => {
      setUserLocationMessage((currentMessage) => currentMessage || t('journeyNavigationGpsWaiting'));
    };

    navigator.geolocation.getCurrentPosition(handlePosition, handleError, {
      enableHighAccuracy: true,
      timeout: 12000,
      maximumAge: 15000,
    });

    const watchId = navigator.geolocation.watchPosition(handlePosition, handleError, {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 15000,
    });

    return () => navigator.geolocation.clearWatch(watchId);
    // GPS navigation follows the active journey without moving the map camera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJourneyNavigationActive]);

  useEffect(() => {
    const handleBrowserBack = () => {
      startTransition(() => {
        setSelectedRouteId('');
        setSelectedBusId('');
      });
      setSelectedStop(null);
      setRouteContext(null);
      setRouteContextError('');
      setJourneyOverlay(null);
      setJourneyErrorMessage('');
      setShowAllRoutePaths(false);
      setIsRoutePathPickerOpen(false);
      setAllRoutePathsError('');
      setAreMapStopsVisible(true);
      setAreLiveBusesVisible(true);
      clearMapTraceData(mapRef.current);
    };

    window.addEventListener('popstate', handleBrowserBack);
    return () => window.removeEventListener('popstate', handleBrowserBack);
  }, []);

  useEffect(() => {
    if (
      hasInitializedRoutePathSelectionRef.current ||
      allRoutePaths.length === 0 ||
      selectedRoutePathIds.length > 0
    ) {
      return;
    }

    hasInitializedRoutePathSelectionRef.current = true;
    setSelectedRoutePathIds([
      ...new Set(allRoutePaths.map((feature) => String(feature.properties.routeId || '')).filter(Boolean)),
    ]);
  }, [allRoutePaths, selectedRoutePathIds.length]);

  useEffect(() => {
    if (isJourneyNavigationActive) {
      setRouteContext(null);
      setRouteContextError('');
      setIsRouteContextLoading(false);
      return undefined;
    }

    const requestParams = selectedBus
      ? {
          ...activeServiceAreaApiParams,
          tripId: selectedBus.tripId,
          shapeId: selectedBus.shapeId,
          routeId: selectedBus.routeId,
          startDate: selectedBus.startDate,
        }
      : selectedRouteId
        ? {
            ...activeServiceAreaApiParams,
            routeId: selectedRouteContextRouteId,
            startDate: buses[0]?.startDate ?? '',
          }
        : null;

    if (!requestParams || (!requestParams.tripId && !requestParams.shapeId && !requestParams.routeId)) {
      setRouteContext(null);
      setRouteContextError('');
      return undefined;
    }

    let isCancelled = false;

    setIsRouteContextLoading(true);
    setRouteContextError('');
    setSelectedStop(null);
    setJourneyOverlay(null);
    setJourneyErrorMessage('');

    const routeContextCacheKey = getRouteContextCacheKey(requestParams);

    async function loadRouteContext() {
      const cachedRouteContext = await readBrowserCache(routeContextCacheKey, getBrowserStaticCacheTtlMs());

      if (cachedRouteContext && !isCancelled) {
        const hasCachedPath =
          cachedRouteContext?.path?.geometry?.coordinates?.length > 1 ||
          cachedRouteContext?.paths?.some?.((pathFeature) => pathFeature?.geometry?.coordinates?.length > 1);

        setRouteContext(hasCachedPath ? cachedRouteContext : null);
        setRouteContextError(hasCachedPath ? '' : t('routePathUnavailable'));
        setIsRouteContextLoading(false);
        return;
      }

      axios
        .get(`${API_BASE_URL}/api/route-context`, {
          params: requestParams,
        })
        .then(async (response) => {
          if (isCancelled) {
            return;
          }

          const nextRouteContext = response.data;
          const hasPath =
            nextRouteContext?.path?.geometry?.coordinates?.length > 1 ||
            nextRouteContext?.paths?.some?.((pathFeature) => pathFeature?.geometry?.coordinates?.length > 1);

          setRouteContext(hasPath ? nextRouteContext : null);

          if (!hasPath) {
            setRouteContextError(t('routePathUnavailable'));
            return;
          }

          await writeBrowserCache(routeContextCacheKey, nextRouteContext);
        })
        .catch((error) => {
          if (isCancelled) {
            return;
          }

          setRouteContext(null);
          setRouteContextError(
            error?.response?.data?.error ?? t('routeContextFailed')
          );
        })
        .finally(() => {
          if (!isCancelled) {
            setIsRouteContextLoading(false);
          }
        });
    }

    loadRouteContext();

    return () => {
      isCancelled = true;
    };
  }, [activeServiceAreaApiParams, buses, isJourneyNavigationActive, selectedBus, selectedRouteContextRouteId, selectedRouteId]);

  useEffect(() => {
    if (!selectedStop?.id) {
      setStopUpcoming([]);
      setStopTimetable([]);
      setStopPassagesStopId('');
      setIsStopUpcomingLoading(false);
      return undefined;
    }

    let isCancelled = false;
    let hasLoadedSchedule = false;
    let hasLoadedRealtime = false;
    const requestedStopId = String(selectedStop.id);

    setStopUpcoming([]);
    setStopTimetable([]);
    setStopPassagesStopId('');
    setIsStopUpcomingLoading(true);

    function getStopRequestParams() {
      return {
        ...activeServiceAreaApiParams,
        routeId: selectedBus?.routeId || '',
        limit: 5,
        timetableLimit: 12,
      };
    }

    function loadSelectedStopSchedule({ showLoading = false } = {}) {
      if (showLoading) {
        setIsStopUpcomingLoading(true);
      }

      return axios
        .get(`${API_BASE_URL}/api/stops/${requestedStopId}/schedule`, {
          params: getStopRequestParams(),
        })
        .then((response) => {
          if (!isCancelled) {
            setStopTimetable(response.data?.scheduledPassages ?? []);
            setStopPassagesStopId(requestedStopId);
            hasLoadedSchedule = true;
          }
        })
        .catch(() => {
          if (!isCancelled && !hasLoadedSchedule) {
            setStopTimetable([]);
            setStopPassagesStopId(requestedStopId);
          }
        })
        .finally(() => {
          if (!isCancelled) {
            setIsStopUpcomingLoading(false);
          }
        });
    }

    function loadSelectedStopRealtime() {
      return axios
        .get(`${API_BASE_URL}/api/stops/${requestedStopId}/realtime`, {
          params: getStopRequestParams(),
        })
        .then((response) => {
          if (!isCancelled) {
            setStopUpcoming(response.data?.passages ?? []);
            setStopPassagesStopId(requestedStopId);
            hasLoadedRealtime = true;
          }
        })
        .catch(() => {
          if (!isCancelled && !hasLoadedRealtime) {
            setStopUpcoming([]);
          }
        });
    }

    loadSelectedStopSchedule({ showLoading: true });
    loadSelectedStopRealtime();
    const refreshId = window.setInterval(() => {
      loadSelectedStopRealtime();
    }, REFRESH_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(refreshId);
    };
  }, [activeServiceAreaApiParams, selectedStop?.id, selectedBus?.routeId]);

  useEffect(() => {
    const mapInstance = mapRef.current;

    if (!mapInstance) {
      return undefined;
    }

    const syncLineOverlays = () => {
      ensureMapOverlayLayers(mapInstance);
      const routePathFeatures = routeContext?.paths?.length
        ? routeContext.paths
        : routeContext?.path
          ? [routeContext.path]
          : [];
      setSourceData(
        mapInstance,
        ALL_ROUTE_PATHS_SOURCE_ID,
        !journeyDisplayOption && showAllRoutePaths && selectedRoutePathFeatures.length
          ? { type: 'FeatureCollection', features: selectedRoutePathFeatures }
          : EMPTY_FEATURE_COLLECTION
      );
      setSourceData(
        mapInstance,
        ROUTE_CONTEXT_SOURCE_ID,
        !journeyDisplayOption && routePathFeatures.length
          ? { type: 'FeatureCollection', features: routePathFeatures }
          : EMPTY_FEATURE_COLLECTION
      );
      setSourceData(
        mapInstance,
        TPL_STOPS_SOURCE_ID,
        areMapStopsVisible && !journeyDisplayOption
          ? buildStopFeatureCollection(
              transitCatalog.stops,
              journeyRouteIds.length
                ? journeyRouteIds
                : selectedBus?.routeId
                ? [selectedBus.routeId]
                : selectedRouteIdsKey.split('|').filter(Boolean),
              selectedStop?.id ?? ''
            )
          : EMPTY_FEATURE_COLLECTION
      );
      setSourceData(
        mapInstance,
        JOURNEY_SOURCE_ID,
        journeyDisplayOption?.features?.length
          ? { type: 'FeatureCollection', features: journeyDisplayOption.features }
          : EMPTY_FEATURE_COLLECTION
      );

      if (mapInstance.getLayer(ROUTE_CONTEXT_LINE_LAYER_ID)) {
        const routeColor = getRouteColor(
          selectedBus?.routeShortName || selectedBus?.routeId || selectedRoute?.shortName || selectedRouteId || ''
        );
        mapInstance.setPaintProperty(ROUTE_CONTEXT_LINE_LAYER_ID, 'line-color', routeColor);
      }

      if (mapInstance.getLayer(TPL_STOPS_DOT_LAYER_ID)) {
        const routeColor = getRouteColor(
          selectedRoute?.shortName || selectedRouteId || selectedBus?.routeShortName || selectedBus?.routeId || ''
        );
        mapInstance.setPaintProperty(TPL_STOPS_DOT_LAYER_ID, 'circle-color', [
          'case',
          ['==', ['get', 'isSelected'], true],
          '#18212b',
          ['==', ['get', 'isRouteStop'], true],
          routeColor,
          '#ffffff',
        ]);
        mapInstance.setPaintProperty(TPL_STOPS_DOT_LAYER_ID, 'circle-stroke-color', routeColor);
      }

    };

    if (typeof mapInstance.isStyleLoaded === 'function' && !mapInstance.isStyleLoaded()) {
      mapInstance.on('styledata', syncLineOverlays);
      return () => mapInstance.off('styledata', syncLineOverlays);
    }

    syncLineOverlays();
    return undefined;
  }, [
    areMapStopsVisible,
    journeyDisplayOption,
    journeyRouteIds,
    mapStyleRevision,
    routeContext,
    selectedBus,
    selectedRoute,
    selectedRouteId,
    selectedRouteIdsKey,
    selectedRoutePathFeatures,
    selectedStop?.id,
    showAllRoutePaths,
    transitCatalog.stops,
  ]);

  useEffect(() => {
    journeyStopMarkersRef.current.forEach((marker) => marker.remove());
    journeyStopMarkersRef.current = [];

    const mapInstance = mapRef.current;
    const markerStops = [
      { place: journeyBoardingStop, variant: 'boarding' },
      ...journeyTransferStops,
      { place: journeyArrivalStop, variant: 'arrival' },
    ].filter(({ place }) => Number.isFinite(place?.longitude) && Number.isFinite(place?.latitude));

    if (!mapInstance || markerStops.length === 0) {
      return undefined;
    }

    journeyStopMarkersRef.current = markerStops.map(({ place, variant }) =>
      new mapboxgl.Marker({
        element: buildJourneyStopMarker(place, variant),
        anchor: 'center',
      })
        .setLngLat([place.longitude, place.latitude])
        .addTo(mapInstance)
    );

    return () => {
      journeyStopMarkersRef.current.forEach((marker) => marker.remove());
      journeyStopMarkersRef.current = [];
    };
  }, [journeyArrivalStop, journeyBoardingStop, journeyTransferStops, language]);

  useEffect(() => {
    const mapInstance = mapRef.current;

    if (!mapInstance) {
      return;
    }

    const routeColor = getRouteColor(selectedBus?.routeId ?? '', selectedBus?.routeShortName ?? '');
    const nextStops = selectedBus && areMapStopsVisible
      ? routeContext?.stops?.map((stop, stopIndex, stops) => ({
          ...stop,
          isTerminal: stopIndex === 0 || stopIndex === stops.length - 1,
        })) ?? []
      : [];
    const nextVisibleStopIds = new Set(nextStops.map((stop) => stop.id));

    stopMarkersRef.current.forEach(({ marker }, stopId) => {
      if (!nextVisibleStopIds.has(stopId)) {
        marker.remove();
        stopMarkersRef.current.delete(stopId);
      }
    });

    nextStops.forEach((stop) => {
      const existingEntry = stopMarkersRef.current.get(stop.id);

      if (existingEntry) {
        existingEntry.marker.setLngLat([stop.longitude, stop.latitude]);
        updateStopMarkerElement(
          existingEntry.element,
          stop,
          stop.id === selectedStop?.id,
          routeColor
        );
        return;
      }

      const element = buildStopMarkerElement(
        stop,
        stop.id === selectedStop?.id,
        routeColor,
        () => selectStop(stop)
      );
      const marker = new mapboxgl.Marker({
        element,
        anchor: 'center',
      })
        .setLngLat([stop.longitude, stop.latitude])
        .addTo(mapInstance);

      stopMarkersRef.current.set(stop.id, { marker, element });
    });
  }, [areMapStopsVisible, routeContext, selectStop, selectedBus, selectedBus?.routeId, selectedStop?.id]);

  useEffect(() => {
    const mapInstance = mapRef.current;

    if (!mapInstance) {
      return;
    }

    const visibleRouteIdSet = new Set(selectedRouteIdsKey.split('|').filter(Boolean));
    const hasJourneySelection = journeyTransitLegs.length > 0;
    const nextMapVisibleBuses = areLiveBusesVisible
      ? buses.filter((bus) =>
          hasJourneySelection
            ? journeyTransitLegs.some((leg) =>
                isJourneyNavigationActive
                  ? busMatchesActiveJourneyLeg(bus, leg)
                  : busMatchesJourneyLeg(bus, leg)
              )
            : !visibleRouteIdSet.size || visibleRouteIdSet.has(bus.routeId)
        )
      : [];
    const nextVisibleIds = new Set(nextMapVisibleBuses.map((bus) => bus.id));

    markersRef.current.forEach(({ marker }, busId) => {
      if (!nextVisibleIds.has(busId)) {
        marker.remove();
        markersRef.current.delete(busId);
      }
    });

    nextMapVisibleBuses.forEach((bus) => {
      const existingEntry = markersRef.current.get(bus.id);
      const journeyLeg = journeyTransitLegs.find((leg) =>
        isJourneyNavigationActive
          ? busMatchesActiveJourneyLeg(bus, leg)
          : busMatchesJourneyLeg(bus, leg)
      );
      const journeyStatus = journeyLeg
        ? busMatchesActiveJourneyLeg(bus, journeyLeg)
          ? 'live'
          : 'route'
        : '';

      if (existingEntry) {
        existingEntry.marker.setLngLat([bus.longitude, bus.latitude]);
        updateMarkerElement(existingEntry.element, bus, bus.id === selectedBusId, journeyStatus);
        return;
      }

      const element = buildMarkerElement(
        bus,
        bus.id === selectedBusId,
        () => selectBusRef.current(bus.id),
        journeyStatus
      );
      const marker = new mapboxgl.Marker({
        element,
        anchor: 'bottom',
      })
        .setLngLat([bus.longitude, bus.latitude])
        .addTo(mapInstance);

      markersRef.current.set(bus.id, { marker, element });
    });
  }, [areLiveBusesVisible, buses, isJourneyNavigationActive, journeyTransitLegs, selectedBusId, selectedRouteIdsKey]);

  useEffect(() => {
    popupRef.current?.remove();

    if (!mapRef.current || !selectedBus) {
      return;
    }

    const popupNode = document.createElement('div');
    popupNode.className = 'bus-popup';
    popupNode.style.setProperty('--route-color', getRouteColor(selectedBus.routeId, selectedBus.routeShortName));
    popupNode.style.setProperty(
      '--route-text-color',
      getReadableTextColor(getRouteColor(selectedBus.routeId, selectedBus.routeShortName))
    );

    const headingNode = document.createElement('div');
    headingNode.className = 'bus-popup__heading';

    const routeBadgeNode = document.createElement('strong');
    routeBadgeNode.className = 'bus-popup__route';
    routeBadgeNode.textContent = selectedBus.routeShortName || selectedBus.routeId;

    const titleWrapNode = document.createElement('div');
    titleWrapNode.className = 'bus-popup__title';

    const titleNode = document.createElement('strong');
    titleNode.textContent = `Bus ${selectedBus.label}`;

    const subtitleNode = document.createElement('span');
    subtitleNode.textContent = getBusSubtitle(selectedBus);

    titleWrapNode.append(titleNode, subtitleNode);
    headingNode.append(routeBadgeNode, titleWrapNode);

    const stopNode = document.createElement('div');
    stopNode.className = 'bus-popup__next';
    if (selectedBus.nextStopName) {
      const stopLabelNode = document.createElement('span');
      stopLabelNode.textContent = t('nextStopPopup');

      const stopNameNode = document.createElement('strong');
      stopNameNode.textContent = selectedBus.nextStopName;

      stopNode.append(stopLabelNode, stopNameNode);

      if (hasRealtimeArrivalPredictions) {
        const stopTimeNode = document.createElement('em');
        stopTimeNode.textContent = formatPredictedTime(
          selectedBus.nextStopPredictedTime,
          selectedBus.nextStopScheduledTime
        );
        stopNode.append(stopTimeNode);
      }
    } else {
      stopNode.textContent = selectedBus.movement.status;
    }

    const metaNode = document.createElement('div');
    metaNode.className = 'bus-popup__meta';

    const progressNode = document.createElement('span');
    progressNode.textContent = getGtfsProgressLabel(selectedBus);

    const statusNode = document.createElement('span');
    statusNode.textContent = hasScheduleAdherence && Number.isFinite(selectedBus.delaySeconds)
      ? formatDelay(selectedBus.delaySeconds)
      : hasVehicleSpeed
        ? t('speed', { speed: selectedBus.speedKmh })
        : '';
    statusNode.className = hasScheduleAdherence ? getDelayToneClassName(selectedBus.delaySeconds) : '';

    const detectionNode = document.createElement('span');
    detectionNode.textContent = selectedBus.timestamp
      ? t('detectedAt', { time: formatTimestamp(selectedBus.timestamp) })
      : '';

    if (progressNode.textContent) {
      metaNode.append(progressNode);
    }
    if (statusNode.textContent) {
      metaNode.append(statusNode);
    }
    if (detectionNode.textContent) {
      metaNode.append(detectionNode);
    }

    popupNode.append(headingNode, stopNode);
    if (metaNode.childElementCount) {
      popupNode.append(metaNode);
    }

    popupRef.current = new mapboxgl.Popup({
      anchor: 'bottom',
      offset: POPUP_OFFSET,
      closeButton: false,
      maxWidth: 'none',
    })
      .setLngLat([selectedBus.longitude, selectedBus.latitude])
      .setDOMContent(popupNode)
      .addTo(mapRef.current);
  }, [hasRealtimeArrivalPredictions, hasScheduleAdherence, hasVehicleSpeed, selectedBus]);

  useEffect(() => {
    stopPopupRef.current?.remove();

    if (stopPageStopId || !mapRef.current || !selectedStop) {
      return;
    }

    const popupNode = document.createElement('div');
    popupNode.className = 'stop-popup';

    const headerNode = document.createElement('div');
    headerNode.className = 'stop-popup__header';

    const titleWrapNode = document.createElement('span');
    titleWrapNode.className = 'stop-popup__title-wrap';

    const titleNode = document.createElement('strong');
    titleNode.className = 'stop-popup__title';
    titleNode.textContent = selectedStop.name;

    const codeNode = document.createElement('span');
    codeNode.className = 'stop-popup__meta';
    codeNode.textContent = `Fermata ${selectedStop.id}`;

    const stopRouteLabels = sortRouteLabels(selectedStop.routeShortNames);
    const routeListNode = document.createElement('span');
    routeListNode.className = 'stop-popup__routes';

    if (stopRouteLabels.length > 0) {
      const routeListLabelNode = document.createElement('span');
      routeListLabelNode.className = 'stop-popup__routes-label';
      routeListLabelNode.textContent = 'Linee';
      routeListNode.append(routeListLabelNode);

      stopRouteLabels.forEach((routeLabel) => {
        const routeOption = getRouteOptionByRouteLabel(routeOptionsRef.current, routeLabel);
        const routeId = routeOption?.id || routeOption?.routeIds?.[0] || routeLabel;
        const routeNode = document.createElement('button');
        routeNode.className = 'stop-popup__route-chip';
        routeNode.type = 'button';
        routeNode.setAttribute('aria-label', t('openRoute', { route: routeLabel, destination: selectedStop.name }));
        applyRouteBadgeStyle(routeNode, routeLabel);
        routeNode.textContent = routeLabel;
        routeNode.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          handleRouteSelectionRef.current(routeId, { focusStop: selectedStop });
        });
        routeListNode.append(routeNode);
      });
    }

    const closeButton = document.createElement('button');
    closeButton.className = 'stop-popup__close';
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', t('closeStop'));
    closeButton.textContent = '×';
    closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      clearStopFocus();
    });

    titleWrapNode.append(titleNode, codeNode);
    if (stopRouteLabels.length > 0) {
      titleWrapNode.append(routeListNode);
    }
    headerNode.append(titleWrapNode, closeButton);

    const passageTitleNode = document.createElement('span');
    passageTitleNode.className = 'stop-popup__label';
    const hasCurrentStopPassages = stopPassagesStopId === String(selectedStop.id);
    passageTitleNode.textContent = isStopUpcomingLoading || !hasCurrentStopPassages
      ? t('loadingStopSchedule')
      : '';

    popupNode.append(headerNode, passageTitleNode);

    const popupPassageRows = hasCurrentStopPassages ? buildStopPassageRows(stopUpcoming, stopTimetable, 5) : [];
    const bodyNode = document.createElement('div');
    bodyNode.className = 'stop-popup__body';

    if (popupPassageRows.length > 0) {
      const passagesNode = document.createElement('div');
      passagesNode.className = 'stop-popup__list';

      popupPassageRows.forEach((passage) => {
        const passageNode = document.createElement('button');
        passageNode.className = `stop-popup__row ${
          passage.isRealtime ? 'stop-popup__row--realtime' : 'stop-popup__row--scheduled'
        }`;
        passageNode.type = 'button';
        passageNode.setAttribute(
          'aria-label',
          passage.isRealtime
            ? t('openRealtimeBus', {
                route: getPassageRouteLabel(passage),
                destination: getPassageDestinationText(passage),
              })
            : t('openRoute', {
                route: getPassageRouteLabel(passage),
                destination: getPassageDestinationText(passage),
              })
        );
        passageNode.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (passage.isRealtime) {
            openRealtimePassageRef.current(passage);
          } else {
            openRoutePassageRef.current(passage);
          }
        });

        const lineNode = document.createElement('strong');
        lineNode.className = 'route-badge';
        applyRouteBadgeStyle(lineNode, getPassageRouteLabel(passage));
        lineNode.textContent = getPassageRouteLabel(passage);

        const arrivalNode = document.createElement('span');
        arrivalNode.className = 'stop-popup__arrival';
        arrivalNode.textContent = formatPredictedTime(
          passage.isRealtime && hasRealtimeArrivalPredictions ? passage.predictedTime : 0,
          passage.scheduledTime
        );

        const contentNode = document.createElement('span');
        const destinationText = getPassageDestinationText(passage);
        contentNode.className = getPassageDestinationClassName('stop-popup__destination', destinationText);
        contentNode.textContent = destinationText;

        const delayNode = document.createElement('span');
        delayNode.className = 'stop-popup__status';
        delayNode.textContent = passage.isRealtime
          ? hasScheduleAdherence
            ? hasDelaySeconds(passage.delaySeconds)
              ? formatDelay(passage.delaySeconds)
              : 'Realtime'
            : 'Realtime'
          : t('scheduledTime');
        const delayToneClassName = passage.isRealtime && hasScheduleAdherence && hasDelaySeconds(passage.delaySeconds)
          ? getDelayToneClassName(passage.delaySeconds)
          : '';

        if (delayToneClassName) {
          delayNode.className = `stop-popup__status ${delayToneClassName}`;
        }

        const actionNode = document.createElement('span');
        actionNode.className = 'stop-popup__action';
        actionNode.textContent = passage.isRealtime
          ? hasArrivalCountdown
            ? `${formatArrivalCountdown(getPassageArrivalTime(passage))}`
            : t('followBus')
          : t('openLine');

        const detailNode = document.createElement('span');
        detailNode.className = 'stop-popup__details';
        detailNode.textContent = passage.isRealtime && !hasRealtimeArrivalPredictions
          ? t('scheduleTimeShort')
          : passage.isRealtime
            ? t('realtimeBus')
            : t('scheduled');

        const mainNode = document.createElement('span');
        mainNode.className = 'stop-popup__main';
        mainNode.append(contentNode, delayNode);

        const timeNode = document.createElement('span');
        timeNode.className = 'stop-popup__time';
        timeNode.append(arrivalNode, detailNode);

        passageNode.append(lineNode, mainNode, timeNode, actionNode);
        passagesNode.append(passageNode);
      });

      bodyNode.append(passagesNode);
    }

    if (hasCurrentStopPassages && popupPassageRows.length === 0 && !isStopUpcomingLoading) {
      const emptyNode = document.createElement('span');
      emptyNode.textContent = t('noUpcomingPassages');
      bodyNode.append(emptyNode);
    }

    popupNode.append(bodyNode);

    const actionsNode = document.createElement('div');
    actionsNode.className = 'stop-popup__actions';

    const favoriteButton = document.createElement('button');
    const isFavoriteStop = favoriteStopIdsKey.split('|').includes(String(selectedStop.id));
    favoriteButton.className = `popup-action-button popup-action-button--favorite ${isFavoriteStop ? 'is-active' : ''}`;
    favoriteButton.textContent = isFavoriteStop ? t('favoriteStopSaved') : t('favoriteStopSave');
    favoriteButton.type = 'button';
    favoriteButton.setAttribute('aria-pressed', isFavoriteStop ? 'true' : 'false');
    favoriteButton.setAttribute(
      'aria-label',
      isFavoriteStop ? t('removeFavoriteStop') : t('addFavoriteStop')
    );
    favoriteButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      toggleFavoriteStop(selectedStop);
    });
    actionsNode.append(favoriteButton);

    const timetableButton = document.createElement('button');
    timetableButton.className = 'popup-action-button popup-action-button--secondary';
    timetableButton.textContent = t('allTimes');
    timetableButton.type = 'button';
    timetableButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      openStopTimetablePageRef.current(selectedStop);
    });
    actionsNode.append(timetableButton);

    popupNode.append(actionsNode);

    stopPopupRef.current = new mapboxgl.Popup({
      anchor: 'bottom',
      className: 'stop-mapbox-popup',
      offset: {
        bottom: [0, -10],
      },
      closeButton: false,
      maxWidth: 'none',
    })
      .setLngLat([selectedStop.longitude, selectedStop.latitude])
      .setDOMContent(popupNode)
      .addTo(mapRef.current);
  }, [
    favoriteStopIdsKey,
    hasArrivalCountdown,
    hasJourneyPath,
    hasRealtimeArrivalPredictions,
    hasScheduleAdherence,
    isJourneyLoading,
    isStopUpcomingLoading,
    journeyOverlay,
    selectedStop,
    stopPageStopId,
    stopPassagesStopId,
    stopTimetable,
    stopUpcoming,
    userLocation,
  ]);

  if (stopPageStopId) {
    return (
      <StopTimetablePage
        appName={transitAppConfig.operator?.name || operatorShortName}
        errorMessage={stopPageErrorMessage}
        hasArrivalCountdown={hasArrivalCountdown}
        hasRealtimeArrivalPredictions={hasRealtimeArrivalPredictions}
        hasScheduleAdherence={hasScheduleAdherence}
        isFavorite={Boolean(stopPageStopId && favoriteStopsById.has(String(stopPageStopId)))}
        isLoading={isStopPageLoading}
        lastLoadedAt={stopPageLastLoadedAt}
        maxDate={gtfsServiceWindow?.maxDate || ''}
        onBack={closeStopTimetablePage}
        onDateChange={handleStopPageDateChange}
        onToggleFavorite={toggleFavoriteStop}
        onRefresh={() => setStopPageReloadKey((currentKey) => currentKey + 1)}
        onToday={() => handleStopPageDateChange(getDateInputValue())}
        realtimeCount={stopPageRealtime.length}
        rows={stopPageRows}
        scheduledCount={stopPageScheduled.length}
        selectedDate={stopPageSelectedDate}
        stop={stopPageStop}
        stopId={stopPageStopId}
      />
    );
  }

  return (
    <div className={`app-shell ${isMapFullscreen ? 'is-map-fullscreen' : ''}`}>
      <section
        className={`workspace-panel workspace-panel--map ${isMapFullscreen ? 'is-fullscreen' : ''} ${isJourneyPlannerOpen ? 'has-journey-planner' : ''} ${hasJourneyResult ? 'has-journey-result' : ''}`}
        ref={mapWorkspaceRef}
      >
        <div className="map-toolbar">
          <div>
            <span className="panel-heading__eyebrow">TPL TRACKER</span>
            <h2>
              {selectedRouteId
                ? `${selectedRoute?.shortName || selectedRouteId}`
                : t('mapTitleDefault', { operator: transitAppConfig.operator?.name || operatorShortName })}
            </h2>
          </div>

          <div className="map-toolbar__controls">
            <button className="button button--ghost button--map" onClick={() => loadBuses('manual')} type="button">
              {isRefreshing ? t('refreshing') : t('refresh')}
            </button>
            <button className="button button--ghost button--map" onClick={locateUser} type="button">
              {isLocatingUser ? t('locating') : t('myLocation')}
            </button>
            <button className="button button--ghost button--map" onClick={toggleMapFullscreen} type="button">
              {isMapFullscreen ? t('closeMap') : t('openFullscreen')}
            </button>

            <div className="map-toolbar__install-row">
              <div className="language-toggle" aria-label={t('languageToggleLabel')} role="group">
                <button
                  aria-pressed={language === 'it'}
                  className={language === 'it' ? 'is-active' : ''}
                  onClick={() => selectLanguage('it')}
                  title={t('languageItalian')}
                  type="button"
                >
                  IT
                </button>
                <button
                  aria-pressed={language === 'en'}
                  className={language === 'en' ? 'is-active' : ''}
                  onClick={() => selectLanguage('en')}
                  title={t('languageEnglish')}
                  type="button"
                >
                  EN
                </button>
              </div>
              <PwaInstallButton className="button button--install button--install-compact" />
            </div>

            <div className="map-toolbar__meta">
{/*
              <span>{areLiveBusesVisible ? `${visibleBuses.length} marker` : 'bus live nascosti'}</span>
*/}
              <span>{isRefreshing ? t('syncInProgress') : t('syncAt', { time: formatTime(lastUpdatedAt) })}</span>
              {isCatalogLoading ? <span>{t('loadingNetwork')}</span> : <span>{t('stopsCount', { count: transitCatalog.stops.length })}</span>}
              {routeContext ? (
                <span>{isRouteContextLoading ? t('updatingRoutePath') : t('routeStopsCount', { count: routeContextStopsCount })}</span>
              ) : null}
              {journeyDisplayOption?.summary ? (
                <span>
                  {t('journeyOnMap', {
                    duration: formatDurationMinutes(journeyDisplayOption.summary.durationSeconds),
                    transfers: journeyDisplayOption.summary.transfers,
                  })}
                </span>
              ) : null}
              {userLocationMessage ? <span className="map-toolbar__status">{userLocationMessage}</span> : null}
              {catalogErrorMessage ? <span className="map-toolbar__status">{catalogErrorMessage}</span> : null}
              {routeContextError ? <span className="map-toolbar__status">{routeContextError}</span> : null}
              {journeyErrorMessage ? <span className="map-toolbar__status">{journeyErrorMessage}</span> : null}
              {allRoutePathsError ? <span className="map-toolbar__status">{allRoutePathsError}</span> : null}
            </div>
          </div>
        </div>

        <div className={`map-frame ${isJourneyPlannerOpen ? 'has-journey-planner' : ''} ${hasJourneyResult ? 'has-journey-result' : ''}`}>
          {hasJourneyPath && isJourneyPlannerOpen ? (
            <section className="journey-planner" aria-label={t('journeyPlanner')}>
              <div className="journey-planner__header">
                <div>
                  <strong>{t('journeyPlanner')}</strong>
                  <span>{transitAppConfig.operator?.name || operatorShortName}</span>
                </div>
                <button
                  aria-label={t('close')}
                  onClick={() => setIsJourneyPlannerOpen(false)}
                  type="button"
                >
                  ×
                </button>
              </div>

              <form className="journey-planner__form" onSubmit={handleJourneySubmit}>
                <div className="journey-planner__fields">
                  <label>
                    <span>{t('journeyFrom')}</span>
                    <input
                      autoComplete="off"
                      onChange={(event) => handleJourneyQueryChange('from', event.target.value)}
                      onFocus={() => setJourneyActiveField('from')}
                      placeholder={t('journeyFrom')}
                      type="text"
                      value={journeyFromQuery}
                    />
                  </label>
                  <button
                    aria-label={t('journeySwap')}
                    className="journey-planner__swap"
                    onClick={swapJourneyPlaces}
                    type="button"
                  >
                    ⇅
                  </button>
                  <label>
                    <span>{t('journeyTo')}</span>
                    <input
                      autoComplete="off"
                      onChange={(event) => handleJourneyQueryChange('to', event.target.value)}
                      onFocus={() => setJourneyActiveField('to')}
                      placeholder={t('journeyTo')}
                      type="text"
                      value={journeyToQuery}
                    />
                  </label>
                </div>

                {renderJourneyTimeControls()}

                {renderJourneyFavoriteJourneys()}

                {renderJourneyFavoritePlaces()}

                {journeyActiveField && journeySuggestions.length > 0 ? (
                  <div className="journey-planner__suggestions">
                    {journeySuggestions.map((result) => (
                      <button
                        key={`${journeyActiveField}-${result.id}`}
                        onClick={() => selectJourneySuggestion(journeyActiveField, result)}
                        type="button"
                      >
                        <span>{result.label}</span>
                        {result.subtitle || result.typeLabel ? (
                          <small>
                            {[result.typeLabel, result.subtitle].filter(Boolean).join(' · ')}
                          </small>
                        ) : null}
                      </button>
                    ))}
                  </div>
                ) : null}

                {!hasJourneyResult && recentJourneys.length > 0 ? (
                  <section className="journey-recent" aria-label={t('journeyRecentTitle')}>
                    <div className="journey-recent__header">
                      <div>
                        <strong>{t('journeyRecentTitle')}</strong>
                        <span>{t('journeyRecentHint')}</span>
                      </div>
                      <button onClick={clearRecentJourneys} type="button">
                        {t('journeyRecentClear')}
                      </button>
                    </div>
                    <div className="journey-recent__list">
                      {recentJourneys.map((recentJourney) => (
                        <button
                          aria-label={t('journeyRecentUse')}
                          key={recentJourney.id}
                          onClick={() => selectRecentJourney(recentJourney)}
                          type="button"
                        >
                          <span>
                            {recentJourney.summary.routes.length ? (
                              <span className="journey-recent__routes">
                                {recentJourney.summary.routes.map((routeLabel) => (
                                  <strong key={routeLabel} style={getRouteBadgeStyle(routeLabel)}>
                                    {routeLabel}
                                  </strong>
                                ))}
                              </span>
                            ) : null}
                            <em>{getRecentJourneyTimeLabel(recentJourney)}</em>
                          </span>
                          <b>
                            {getJourneyPlaceName(recentJourney.from, 'origin')} →{' '}
                            {getJourneyPlaceName(recentJourney.to, 'destination')}
                          </b>
                          {recentJourney.summary.durationSeconds ? (
                            <small>{formatDurationMinutes(recentJourney.summary.durationSeconds)}</small>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  </section>
                ) : null}

                <div className="journey-planner__actions">
                  <button onClick={useCurrentLocationForJourney} type="button">
                    <LocationIcon />
                    {t('journeyUseLocation')}
                  </button>
                  {hasJourneyResult ? (
                    <button
                      aria-label={t('journeyRefreshManual')}
                      className={`journey-planner__refresh-button ${isJourneyRefreshing ? 'is-spinning' : ''}`}
                      disabled={isJourneyRefreshing || isJourneyLoading || isJourneyGeocoding}
                      onClick={refreshJourneyManually}
                      title={t('journeyRefreshManual')}
                      type="button"
                    >
                      <RefreshIcon />
                    </button>
                  ) : (
                    <button className="is-primary" disabled={isJourneyLoading || isJourneyGeocoding} type="submit">
                      {isJourneyLoading ? t('journeySearching') : t('journeySearch')}
                    </button>
                  )}
                </div>
              </form>

              {isJourneyLoading ? (
                <div className="journey-loading" role="status" aria-live="polite">
                  <span className="journey-loading__spinner" aria-hidden="true"></span>
                  <div>
                    <strong>{t('journeyLoadingTitle')}</strong>
                    <span>{t(journeyLoadingStep)}</span>
                  </div>
                </div>
              ) : null}

              {journeyErrorMessage ? (
                <div className="journey-planner__error" role="status">
                  <p>{journeyErrorMessage}</p>
                  {journeyFromPlace && journeyToPlace ? (
                    <div className="journey-planner__error-actions">
                      <button onClick={() => retryJourneyWithQuickTime(JOURNEY_TIME_DEPART_AT, 30)} type="button">
                        {t('journeyTryIn30')}
                      </button>
                      <button onClick={() => retryJourneyWithQuickTime(JOURNEY_TIME_DEPART_AT, 60)} type="button">
                        {t('journeyTryIn60')}
                      </button>
                      <button onClick={() => retryJourneyWithQuickTime(JOURNEY_TIME_LAST)} type="button">
                        {t('journeyTryLast')}
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {!isJourneyNavigationActive && journeyOptions.length > 1 && visibleJourneyOptions.length > 0 ? (
                <div
                  className={`journey-alternatives ${visibleJourneyOptions.length === 1 ? 'is-single' : ''}`}
                  aria-label={t('journeyAlternatives')}
                >
                  {visibleJourneyOptions.map((option) => {
                    const optionRoutes = (option.legs || []).filter((leg) => leg.mode !== 'WALK');
                    const hasLiveBus = getJourneyOptionLiveBusKeys(option).length > 0;
                    const isActiveOption =
                      journeyOverlay?.id === option.id ||
                      getJourneyOptionSignature(journeyOverlay) === getJourneyOptionSignature(option);
                    return (
                      <button
                        className={isActiveOption ? 'is-active' : ''}
                        key={option.id}
                        onClick={() => selectJourneyOption(option)}
                        type="button"
                      >
                        <div className="journey-alternatives__top">
                          {optionRoutes.length ? (
                            <div className="journey-alternatives__routes">
                              {optionRoutes.map((leg) => (
                                <span key={leg.id} style={getRouteBadgeStyle(leg.route)}>{leg.route}</span>
                              ))}
                            </div>
                          ) : null}
                          {hasLiveBus ? (
                            <span
                              aria-label={t('journeyOptionLiveBus')}
                              className="journey-alternatives__live"
                              title={t('journeyOptionLiveBus')}
                            >
                              <svg aria-hidden="true" viewBox="0 0 24 24">
                                <path d="M7 6.8C7 5.25 8.25 4 9.8 4h4.4C15.75 4 17 5.25 17 6.8v7.4c0 .72-.58 1.3-1.3 1.3h-.25l1.05 2H15l-.78-1.5H9.78L9 17.5H7.5l1.05-2H8.3c-.72 0-1.3-.58-1.3-1.3V6.8Z" />
                                <path d="M9 7h6M9 10.2h6M9.4 13.2h.1M14.5 13.2h.1" />
                              </svg>
                            </span>
                          ) : null}
                        </div>
                        <strong>{formatDurationMinutes(option.summary.durationSeconds)}</strong>
                        <small>{formatTimestamp(option.summary.startTime)}</small>
                      </button>
                    );
                  })}
                </div>
              ) : null}

              {journeyDisplayOption?.summary ? (
                <div className="journey-result">
                  <div className="journey-result__summary">
                    <strong>{formatDurationMinutes(journeyDisplayOption.summary.durationSeconds)}</strong>
                    <span>{t('journeyArriveAt', { time: formatTimestamp(journeyDisplayOption.summary.endTime) })}</span>
                    {journeyFirstLeg ? (
                      <em>
                        {journeyTimeMode === JOURNEY_TIME_DEPART_NOW
                          ? formatJourneyMoveStart(journeyFirstLeg.startTime)
                          : t('journeyMoveAt', { time: formatTimestamp(journeyFirstLeg.startTime) })}
                      </em>
                    ) : null}
                  </div>
                  <p className="journey-result__refresh">
                    {journeyDisplayOption.summary.nextAvailable
                      ? t('journeyNextAvailable')
                      : journeyTimeMode === JOURNEY_TIME_DEPART_NOW
                      ? (
                        isJourneyRefreshing
                          ? t('journeyRefreshing')
                          : t('journeyAutoRefresh', {
                              time: journeyUpdatedAt ? formatTimestamp(journeyUpdatedAt) : '',
                            })
                      )
                      : t('journeyPlannedFor', {
                          time: formatTimestamp(journeyDisplayOption.summary.searchTime || journeyDisplayOption.summary.startTime),
                        })}
                  </p>
                  {!isJourneyNavigationActive && (journeyFirstLeg || journeyFirstTransitLeg) ? (
                    <div className="journey-result__next-action">
                      {journeyFirstLeg ? (
                        <span>{t('journeyLeaveAt', { time: formatTimestamp(journeyFirstLeg.startTime) })}</span>
                      ) : null}
                      {journeyFirstTransitLeg ? (
                        <strong>
                          {t('journeyBusAt', {
                            line: journeyFirstTransitLeg.route,
                            time: formatTimestamp(journeyFirstTransitLeg.startTime),
                          })}
                        </strong>
                      ) : null}
                      {journeyArrivalStop ? (
                        <em>{t('journeyRideUntil', { stop: getJourneyPlaceName(journeyArrivalStop, 'destination') })}</em>
                      ) : null}
                    </div>
                  ) : null}
                  {journeyFirstTransitLeg ? (
                    <div className={`journey-navigation-card ${isJourneyNavigationActive ? 'is-active' : ''}`}>
                      <div className="journey-navigation-card__copy">
                        <span>{isJourneyNavigationActive ? t('journeyNavigationActive') : t('journeyNavigationTitle')}</span>
                        <strong>
                          {isJourneyNavigationActive && journeyNavigationLeg
                            ? t('journeyNavigationCurrentRide', {
                                line: journeyNavigationLeg.route,
                                destination: journeyNavigationLeg.headsign || getJourneyPlaceName(journeyNavigationLeg.to, 'destination'),
                              })
                            : journeyNavigationLeg?.liveBus
                            ? t('journeyNavigationFollowingBus', { label: journeyNavigationLeg.liveBus.label })
                            : t('journeyNavigationScheduled', { line: journeyNavigationLeg?.route || '' })}
                        </strong>
                        <em>
                          {isJourneyNavigationActive
                            ? t('journeyNavigationLiveRefresh')
                            : t('journeyNavigationHint')}
                        </em>
                      </div>
                      <div className="journey-navigation-card__status" role="status" aria-live="polite">
                        {journeyNavigationLiveState?.text ? (
                          <span>{journeyNavigationLiveState.text}</span>
                        ) : journeyNavigationLeg?.liveBus?.nextStopName ? (
                          <span>{t('journeyNavigationNextStop', { stop: journeyNavigationLeg.liveBus.nextStopName })}</span>
                        ) : null}
                        <strong>
                          {journeyNavigationRemainingStops === 1
                            ? t('journeyNavigationRemainingOneStop')
                            : t('journeyNavigationRemainingStops', { count: journeyNavigationRemainingStops })}
                        </strong>
                        {isJourneyNavigationActive && journeyNavigationGpsState?.text ? (
                          <small>{journeyNavigationGpsState.text}</small>
                        ) : null}
                        {isJourneyNavigationActive && journeyNavigationGpsState?.detail ? (
                          <small>{journeyNavigationGpsState.detail}</small>
                        ) : null}
                      </div>
                      <button
                        className={isJourneyNavigationActive ? 'is-danger' : 'is-primary'}
                        onClick={isJourneyNavigationActive ? endJourneyNavigation : startJourneyNavigation}
                        type="button"
                      >
                        {isJourneyNavigationActive ? t('journeyNavigationStop') : t('journeyNavigationStart')}
                      </button>
                    </div>
                  ) : null}
                  {!isJourneyNavigationActive ? (
                    <div className={`journey-result__time-edit ${isJourneyRefreshing ? 'is-updating' : ''}`}>
                      {renderJourneyTimeControls(true)}
                      <span className="journey-result__time-status" role="status" aria-live="polite">
                        {isJourneyRefreshing ? (
                          <>
                            <span className="journey-result__time-spinner" aria-hidden="true"></span>
                            {t('journeyRefreshing')}
                          </>
                        ) : (
                          t('journeyAutoUpdateHint')
                        )}
                      </span>
                      <button
                        aria-label={t('journeyRefreshManual')}
                        className={`journey-result__refresh-button ${isJourneyRefreshing ? 'is-spinning' : ''}`}
                        disabled={isJourneyRefreshing || isJourneyLoading || isJourneyGeocoding}
                        onClick={refreshJourneyManually}
                        title={t('journeyRefreshManual')}
                        type="button"
                      >
                        <RefreshIcon />
                      </button>
                    </div>
                  ) : null}
                  {!isJourneyNavigationActive ? (
                    <div className="journey-result__share">
                      <button onClick={copyJourneyShareLink} type="button">
                        {t('journeyCopyLink')}
                      </button>
                      {journeyShareMessage ? <span>{journeyShareMessage}</span> : null}
                    </div>
                  ) : null}
                  <div className={`journey-step-list ${isJourneyNavigationActive ? 'is-navigation-active' : ''}`}>
                    {journeyVisibleLegs.map(({ leg, legIndex }) => {
                      const liveBusStopState = getJourneyLiveBusStopState(leg);
                      const legAlerts = getJourneyLegAlerts(leg, serviceAlerts);

                      return (
                      <article className={`journey-step-card journey-step-card--${leg.mode.toLowerCase()}`} key={leg.id}>
                        <span className="journey-step-card__index">{legIndex + 1}</span>
                        <div className="journey-step-card__body">
                          <div className="journey-step-card__meta">
                            {leg.mode === 'WALK' ? (
                              <span className="journey-leg__mode">{t('journeyWalk')}</span>
                            ) : (
                              <span className="journey-leg__route" style={getRouteBadgeStyle(leg.route)}>{leg.route}</span>
                            )}
                            {formatJourneyLegTimeRange(leg) ? <small>{formatJourneyLegTimeRange(leg)}</small> : null}
                          </div>
                          <strong>
                            {leg.mode === 'WALK'
                              ? legIndex === journeyLegs.length - 1
                                ? t('journeyWalkToDestination')
                                : t('journeyWalkToStop', {
                                    stop: getJourneyPlaceName(leg.to, 'destination'),
                                  })
                              : t('journeyLineTowards', {
                                  line: leg.route,
                                  destination: leg.headsign || getJourneyPlaceName(leg.to, 'destination'),
                                })}
                          </strong>
                          <span>
                            {formatDurationMinutes(leg.durationSeconds)} ·{' '}
                            {getJourneyPlaceName(leg.from, 'origin')} →{' '}
                            {getJourneyPlaceName(leg.to, 'destination')}
                          </span>
                          {leg.mode !== 'WALK' ? (
                            <div className="journey-step-card__bus">
                              <div className="journey-leg__ride-cues">
                                <span className="journey-leg__stop-count">
                                  {leg.stopCount === 1
                                    ? t('journeyGetOffAfterOneStop')
                                    : t('journeyGetOffAfter', { count: leg.stopCount })}
                                </span>
                                <span>{t('journeyExitAt', { stop: getJourneyPlaceName(leg.to, 'destination') })}</span>
                              </div>
                              {!leg.liveBus ? (
                                <small>{leg.isRealtime ? t('realtime') : t('journeyNoLiveBus')}</small>
                              ) : null}
                            </div>
                          ) : null}
                          {legAlerts.length > 0 ? (
                            <div className="journey-leg__alert">
                              <strong>
                                {legAlerts.length === 1
                                  ? t('journeyLegAlert', { alert: legAlerts[0].header || t('serviceAlerts') })
                                  : t('journeyLegAlertMore', { count: legAlerts.length })}
                              </strong>
                              {legAlerts[0]?.description ? <span>{legAlerts[0].description}</span> : null}
                            </div>
                          ) : null}
                          {leg.mode !== 'WALK' && leg.stops?.length ? (
                            <details
                              className="journey-leg__stop-details"
                              open={isJourneyNavigationActive && leg.id === journeyNavigationLeg?.id ? true : undefined}
                            >
                              <summary>
                                <span className="journey-leg__stop-title">{t('journeyStopsTimeline')}</span>
                              </summary>
                              <ol className="journey-leg__stops" style={getRouteBadgeStyle(leg.route)}>
                                {leg.stops.map((stop, stopIndex) => {
                                  const stopTimelineState = getJourneyStopTimelineState(leg, stop, stopIndex, liveBusStopState);
                                  const busTimelineMarker = liveBusStopState ? (
                                    <li className="journey-leg__bus-marker">
                                      <span className="journey-leg__bus-icon" aria-hidden="true" />
                                      <span className="journey-leg__bus-copy">
                                        <strong>{t('journeyBusTimelinePosition', { label: leg.liveBus?.label })}</strong>
                                        {liveBusStopState.detail ? <em>{liveBusStopState.detail}</em> : null}
                                      </span>
                                    </li>
                                  ) : null;

                                  return (
                                    <React.Fragment key={`${leg.id}-${stop.id || stopIndex}`}>
                                      {liveBusStopState?.markerBeforeIndex === stopIndex ? busTimelineMarker : null}
                                      <li
                                        className={[
                                          stopIndex === 0 ? 'is-boarding' : '',
                                          stopIndex === leg.stops.length - 1 ? 'is-arrival' : '',
                                          stopTimelineState.className,
                                        ].filter(Boolean).join(' ')}
                                      >
                                        <span className="journey-leg__stop-main">
                                          <strong>{stop.name}</strong>
                                          <em>{stopTimelineState.statusLabel}</em>
                                        </span>
                                        <span className="journey-leg__stop-time">
                                          {stopTimelineState.timeLabel}
                                        </span>
                                      </li>
                                      {liveBusStopState?.markerAfterIndex === stopIndex ? busTimelineMarker : null}
                                    </React.Fragment>
                                  );
                                })}
                              </ol>
                            </details>
                          ) : null}
                        </div>
                      </article>
                      );
                    })}
                  </div>
                  {!isJourneyNavigationActive && journeyBoardingStop && journeyArrivalStop ? (
                    <div className="journey-result__stops">
                      <div className="journey-result__stop journey-result__stop--boarding">
                        <span aria-hidden="true"></span>
                        <div>
                          <small>{t('journeyBoardingStop')}</small>
                          <strong>{getJourneyPlaceName(journeyBoardingStop, 'origin')}</strong>
                        </div>
                      </div>
                      <div className="journey-result__stop journey-result__stop--arrival">
                        <span aria-hidden="true"></span>
                        <div>
                          <small>{t('journeyArrivalStop')}</small>
                          <strong>{getJourneyPlaceName(journeyArrivalStop, 'destination')}</strong>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>
          ) : null}

          {isAddressSearchOpen ? (
            <form className="map-address-search" onSubmit={searchAddress}>
              <label className="sr-only" htmlFor="map-address-search-input">
                {t('searchAddressOnMap')}
              </label>
              <SearchIcon />
              <input
                autoComplete="off"
                autoFocus
                autoCapitalize="none"
                enterKeyHint="search"
                id="map-address-search-input"
                onChange={(event) => handleAddressQueryChange(event.target.value)}
                onFocus={() => setShowLocationHint(false)}
                placeholder={t('searchAddress')}
                spellCheck="false"
                type="search"
                value={addressQuery}
              />
              <button
                aria-label={addressQuery ? t('clearAddressSearch') : t('closeAddressSearch')}
                onClick={addressQuery ? clearAddressSearch : closeAddressSearch}
                type="button"
              >
                ×
              </button>
              <button disabled={isAddressSearching} type="submit">
                {isAddressSearching ? '...' : t('go')}
              </button>
              {addressSearchResults.length > 0 || addressSearchMessage ? (
                <div aria-live="polite" className="map-address-search__results">
                  {addressSearchMessage ? <span>{addressSearchMessage}</span> : null}
                  {addressSearchResults.map((result) => (
                    <button key={result.id} onClick={() => focusAddressResult(result)} type="button">
                      {result.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </form>
          ) : null}

          <div className="map-quick-actions">
            {!isAddressSearchOpen ? (
              <div className="map-address-search-action">
                <button
                  aria-describedby={showAddressSearchHint ? 'map-address-search-hint' : undefined}
                  className="map-address-search-toggle"
                  onClick={openAddressSearch}
                  type="button"
                >
                  <SearchIcon />
                  <span className="sr-only">{t('openAddressSearch')}</span>
                </button>
                {showAddressSearchHint ? (
                  <span className="map-address-search-hint" id="map-address-search-hint" role="note">
                    {t('addressHint')}
                  </span>
                ) : null}
              </div>
            ) : null}
            {hasServiceAreaPicker ? (
              <div className="map-service-area-action">
                <button
                  aria-expanded={isServiceAreaPickerOpen}
                  aria-label={t('chooseMapArea', { area: activeServiceArea?.label || t('fullNetwork').toLowerCase() })}
                  className={`map-icon-button ${isServiceAreaPickerOpen ? 'is-active' : ''}`}
                  onClick={toggleServiceAreaPicker}
                  type="button"
                >
                  <MapAreaIcon />
                  <span className="sr-only">{t('chooseMapArea', { area: '' }).replace(/:\s*$/, '')}</span>
                </button>
                <div
                  aria-label={t('selectedArea', { area: activeServiceArea?.label || t('fullNetwork').toLowerCase() })}
                  className="map-service-area-chip"
                >
                  <span>{t('area')}</span>
                  <strong>{activeServiceArea?.label || t('fullNetwork')}</strong>
                </div>
                {isServiceAreaPickerOpen ? (
                  <div className="map-service-area-popover" role="dialog" aria-label={t('mapAreas')}>
                    <div className="map-service-area-popover__header">
                      <span>{t('mapAreas')}</span>
                      <button
                        aria-label={t('closeMapAreas')}
                        onClick={() => setIsServiceAreaPickerOpen(false)}
                        type="button"
                      >
                        ×
                      </button>
                    </div>
                    <div className="map-service-area-popover__options">
                      {serviceAreaOptions.map((area) => (
                        <button
                          aria-pressed={activeServiceArea?.id === area.id}
                          className={activeServiceArea?.id === area.id ? 'is-selected' : ''}
                          key={area.id}
                          onClick={() => selectServiceArea(area.id)}
                          type="button"
                        >
                          {area.label}
                        </button>
                      ))}
                    </div>
                    {!isDefaultServiceArea ? (
                      <button className="map-service-area-popover__reset" onClick={resetServiceArea} type="button">
                        {t('allArea')}
                      </button>
                    ) : null}
                    {mapStyleOptions.length > 1 ? (
                      <div className="map-style-picker" aria-label={t('mapStyle')}>
                        <span>{t('mapStyle')}</span>
                        <div className="map-style-picker__options">
                          {mapStyleOptions.map((style) => (
                            <button
                              aria-pressed={activeMapStyle?.id === style.id}
                              className={activeMapStyle?.id === style.id ? 'is-selected' : ''}
                              key={style.id}
                              onClick={() => selectMapStyle(style.id)}
                              type="button"
                            >
                              {style.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            <MapIconButton
              active={isMapFullscreen}
              ariaLabel={isMapFullscreen ? t('closeFullscreen') : t('openFullscreenMap')}
              onClick={toggleMapFullscreen}
            >
              <ExpandIcon />
            </MapIconButton>
            <div className="map-location-action">
              <MapIconButton
                ariaDescribedBy={showLocationHint ? 'map-location-hint' : undefined}
                ariaLabel={t('myLocation')}
                isLoading={isLocatingUser}
                onClick={() => {
                  setShowLocationHint(false);
                  locateUser();
                }}
              >
                <LocationIcon />
              </MapIconButton>
              {showLocationHint ? (
                <span className="map-location-hint" id="map-location-hint" role="note">
                  {t('locationHint')}
                </span>
              ) : null}
            </div>
            <div className="map-tools-action">
              <MapIconButton
                active={isMapToolsOpen}
                ariaDescribedBy={showMapToolsHint ? 'map-tools-hint' : undefined}
                ariaLabel={t('mapTools')}
                onClick={() => {
                  setShowMapToolsHint(false);
                  setIsMapToolsOpen((currentValue) => !currentValue);
                  setIsServiceAreaPickerOpen(false);
                }}
              >
                <MapToolsIcon />
              </MapIconButton>
              {showMapToolsHint ? (
                <span className="map-tools-hint" id="map-tools-hint" role="note">
                  {t('mapToolsHint')}
                </span>
              ) : null}
              {isMapToolsOpen ? (
                <div className="map-tools-popover" role="dialog" aria-label={t('mapTools')}>
                  <div className="map-tools-popover__header">
                    <strong>{t('mapTools')}</strong>
                    <button aria-label={t('close')} onClick={() => setIsMapToolsOpen(false)} type="button">×</button>
                  </div>
                  <button onClick={() => { loadBuses('manual'); setIsMapToolsOpen(false); }} type="button">
                    <RefreshIcon />
                    <span>{t('refreshRealtimeBus')}</span>
                  </button>
                </div>
              ) : null}
            </div>
            {hasFocusedMapView ? (
              <MapIconButton ariaLabel={t('backToTplNetwork')} onClick={resetMapFocus}>
                <ResetViewIcon />
              </MapIconButton>
            ) : null}
          </div>

          <div className="map-layer-actions" aria-label={t('mapLayers')}>
            {hasJourneyPath ? (
              <div className="map-layer-action">
                <button
                  aria-describedby={showJourneyPlannerHint ? 'journey-planner-hint' : undefined}
                  aria-label={t('journeyPlanner')}
                  aria-pressed={isJourneyPlannerOpen}
                  className={`map-layer-actions__trigger ${isJourneyPlannerOpen ? 'is-active' : ''}`}
                  onClick={() => {
                    setShowJourneyPlannerHint(false);
                    setIsJourneyPlannerOpen((currentValue) => !currentValue);
                    setIsRoutePathPickerOpen(false);
                    setIsFavoritesOpen(false);
                    setIsServiceAlertsOpen(false);
                    setIsAddressSearchOpen(false);
                  }}
                  type="button"
                >
                  <JourneyPlannerIcon />
                  <span>{t('journeyPlanner')}</span>
                </button>
                {showJourneyPlannerHint ? (
                  <span className="journey-planner-hint" id="journey-planner-hint" role="note">
                    {t('journeyPlannerHint')}
                  </span>
                ) : null}
              </div>
            ) : null}
            <div className="map-layer-action">
              <button
                aria-label={t('openFavorites')}
                aria-pressed={isFavoritesOpen}
                className={`map-layer-actions__trigger ${isFavoritesOpen ? 'is-active' : ''}`}
                onClick={() => {
                  setIsFavoritesOpen((currentValue) => !currentValue);
                  setIsJourneyPlannerOpen(false);
                  setIsRoutePathPickerOpen(false);
                  setIsServiceAlertsOpen(false);
                  setIsAddressSearchOpen(false);
                }}
                type="button"
              >
                <FavoriteIcon />
                <span>{t('favorites')}</span>
              </button>
              {isFavoritesOpen ? (
                <div className="map-popover map-popover--favorites" role="dialog" aria-label={t('favorites')}>
                  <div className="route-path-popover__header">
                    <div>
                      <strong>{t('favorites')}</strong>
                    </div>
                    <div className="route-path-popover__header-actions">
                      <button onClick={() => setIsFavoritesOpen(false)} type="button">
                        {t('close')}
                      </button>
                    </div>
                  </div>
                  {hasFavorites ? (
                    <div className="route-path-popover__favorites">
                      {favoriteJourneyEntries.length > 0 ? (
                        <section className="route-path-popover__favorite-group" aria-label={t('favoriteJourneys')}>
                          <div className="route-path-popover__section-title">
                            <span>{t('favoriteJourneys')}</span>
                          </div>
                          <div className="favorite-journey-list">
                            {favoriteJourneyEntries.map((journey) => (
                              <div className="favorite-journey-list__item" key={`favorite-popover-journey-${journey.id}`}>
                                <button
                                  className="favorite-journey-list__use"
                                  onClick={() => selectFavoriteJourney(journey)}
                                  title={`${getJourneyPlaceName(journey.from, 'origin')} → ${getJourneyPlaceName(journey.to, 'destination')}`}
                                  type="button"
                                >
                                  <strong>
                                    {getJourneyPlaceName(journey.from, 'origin')} →{' '}
                                    {getJourneyPlaceName(journey.to, 'destination')}
                                  </strong>
                                  <span>
                                    {journey.summary.durationSeconds
                                      ? formatDurationMinutes(journey.summary.durationSeconds)
                                      : t('favoriteJourneyDetail')}
                                  </span>
                                </button>
                                <button
                                  aria-label={t('removeFavoriteJourney', {
                                    journey: `${getJourneyPlaceName(journey.from, 'origin')} ${getJourneyPlaceName(journey.to, 'destination')}`,
                                  })}
                                  onClick={() => toggleFavoriteJourney(journey)}
                                  type="button"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </section>
                      ) : null}
                      {favoritePlaceEntries.length > 0 ? (
                        <section className="route-path-popover__favorite-group" aria-label={t('favoritePlaces')}>
                          <div className="route-path-popover__section-title">
                            <span>{t('favoritePlaces')}</span>
                          </div>
                          <div className="favorite-place-list">
                            {favoritePlaceEntries.map((place) => (
                              <div className="favorite-place-list__item" key={`favorite-popover-place-${place.id}`}>
                                <button
                                  className="favorite-place-list__focus"
                                  onClick={() => focusFavoritePlace(place)}
                                  title={getJourneyPlaceName(place, 'destination')}
                                  type="button"
                                >
                                  <strong>{getJourneyPlaceName(place, 'destination')}</strong>
                                  <span>{t('favoritePlaceDetail')}</span>
                                </button>
                                <div className="favorite-place-list__actions">
                                  <button onClick={() => selectFavoritePlaceForJourney('from', place)} type="button">
                                    {t('journeyFrom')}
                                  </button>
                                  <button onClick={() => selectFavoritePlaceForJourney('to', place)} type="button">
                                    {t('journeyTo')}
                                  </button>
                                  <button
                                    aria-label={t('removeFavoritePlace', {
                                      place: getJourneyPlaceName(place, 'destination'),
                                    })}
                                    onClick={() => toggleFavoritePlace(place)}
                                    type="button"
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </section>
                      ) : null}
                      {favoriteRouteOptions.length > 0 ? (
                        <section className="route-path-popover__favorite-group" aria-label={t('favoriteLines')}>
                          <div className="route-path-popover__section-title">
                            <span>{t('favoriteLines')}</span>
                          </div>
                          <div className="favorite-route-list">
                            {favoriteRouteOptions.map((route) => (
                              <button
                                key={`favorite-popover-route-${route.key}`}
                                onClick={() => handleRouteSelection(route.id)}
                                style={getRouteBadgeStyle(route.shortName)}
                                title={route.longName || route.shortName}
                                type="button"
                              >
                                {route.shortName}
                              </button>
                            ))}
                          </div>
                        </section>
                      ) : null}
                      {favoriteStopEntries.length > 0 ? (
                        <section className="route-path-popover__favorite-group" aria-label={t('favoriteStops')}>
                          <div className="route-path-popover__section-title">
                            <span>{t('favoriteStops')}</span>
                          </div>
                          <div className="favorite-stop-list">
                            {favoriteStopEntries.map((stop) => (
                              <button
                                key={`favorite-popover-stop-${stop.id}`}
                                onClick={() => focusFavoriteStop(stop)}
                                title={stop.name}
                                type="button"
                              >
                                <strong>{stop.name}</strong>
                                <span>{t('stopLineSummary', { stopId: stop.id, routes: '' })}</span>
                              </button>
                            ))}
                          </div>
                        </section>
                      ) : null}
                    </div>
                  ) : (
                    <p className="map-popover__empty">{t('noFavoritesYet')}</p>
                  )}
                </div>
              ) : null}
            </div>
            {serviceAlertCount > 0 ? (
              <div className="map-layer-action">
                <button
                  aria-label={serviceAlertCount === 1 ? t('serviceAlertOne') : t('serviceAlertCount', { count: serviceAlertCount })}
                  aria-pressed={isServiceAlertsOpen}
                  className={`map-layer-actions__trigger map-layer-actions__trigger--alert ${isServiceAlertsOpen ? 'is-active' : ''}`}
                  onClick={() => {
                    setIsServiceAlertsOpen((currentValue) => !currentValue);
                    setIsJourneyPlannerOpen(false);
                    setIsRoutePathPickerOpen(false);
                    setIsFavoritesOpen(false);
                    setIsAddressSearchOpen(false);
                  }}
                  type="button"
                >
                  <AlertIcon />
                  <span>{serviceAlertCount === 1 ? t('serviceAlertOne') : t('serviceAlertCount', { count: serviceAlertCount })}</span>
                </button>
                {isServiceAlertsOpen ? (
                  <div className="map-popover map-popover--alerts" role="dialog" aria-label={t('serviceAlerts')}>
                    <div className="route-path-popover__header">
                      <div>
                        <strong>{t('serviceAlerts')}</strong>
                      </div>
                      <div className="route-path-popover__header-actions">
                        <button onClick={() => setIsServiceAlertsOpen(false)} type="button">
                          {t('close')}
                        </button>
                      </div>
                    </div>
                    <div className="service-alert-list">
                      {serviceAlerts.map((alert) => (
                        <article className="service-alert-card" key={alert.id}>
                          <strong>{alert.header || t('serviceAlerts')}</strong>
                          <p>{alert.description || t('serviceAlertNoDetails')}</p>
                          {alert.routeIds.length > 0 ? (
                            <div className="service-alert-card__routes">
                              {alert.routeIds.slice(0, 8).map((routeId) => (
                                <span key={`${alert.id}-${routeId}`} style={getRouteBadgeStyle(routeId)}>
                                  {routeId}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          {alert.informedEntityCount > 0 ? (
                            <small>{t('serviceAlertAffected', { count: alert.informedEntityCount })}</small>
                          ) : null}
                        </article>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
            <button
              aria-label={t('lines')}
              aria-pressed={isRoutePathPickerOpen}
              className={`map-layer-actions__trigger ${isRoutePathPickerOpen ? 'is-active' : ''}`}
              disabled={isAllRoutePathsLoading}
              onClick={() => {
                toggleAllRoutePaths();
                setIsJourneyPlannerOpen(false);
                setIsFavoritesOpen(false);
                setIsServiceAlertsOpen(false);
                setIsAddressSearchOpen(false);
              }}
              type="button"
            >
              <RoutePathIcon />
              <span>{isAllRoutePathsLoading ? t('loadingRoutes') : t('lines')}</span>
            </button>

            {isRoutePathPickerOpen ? (
              <div className="route-path-popover">
                <div className="route-path-popover__header">
                  <div>
                    <strong>{t('lines')}</strong>
                  </div>
                  <div className="route-path-popover__header-actions">
                    <button onClick={() => setIsRoutePathPickerOpen(false)} type="button">
                      {t('close')}
                    </button>
                  </div>
                </div>

                <label className="route-path-popover__search">
                  <SearchIcon />
                  <input
                    onChange={(event) => setRouteQuery(event.target.value)}
                    placeholder={t('searchRoute')}
                    type="search"
                    value={routeQuery}
                  />
                </label>

                <div className="route-path-popover__section route-path-popover__section--compact">
                  <span>{t('routeType')}</span>
                  <div className="route-path-popover__groups">
                    {[
                      ['urban-night', t('urbanRoutes')],
                      ['night', t('nightRoutes')],
                      ['interurban', t('interurbanRoutes')],
                      ['all', t('allRoutes')],
                    ].map(([filterId, label]) => (
                      <button
                        className={routeListFilter === filterId ? 'is-active' : ''}
                        key={filterId}
                        onClick={() => setRouteListFilter(filterId)}
                        type="button"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="route-path-popover__section">
                  <span>{t('mapView')}</span>
                  <div className="route-path-popover__groups route-path-popover__groups--compact">
                    <button className={areLiveBusesVisible ? 'is-active' : ''} onClick={toggleLiveBusesVisibility} type="button">
                      {t('liveBuses')}
                    </button>
                    <button className={areMapStopsVisible ? 'is-active' : ''} onClick={toggleMapStopsVisibility} type="button">
                      {t('stops')}
                    </button>
                    <button className={showAllRoutePaths ? 'is-active' : ''} onClick={toggleRoutePathsVisibility} type="button">
                      {t('routePaths')}
                    </button>
                  </div>
                </div>

                <div className="route-path-popover__section route-path-popover__section--routes">
                  <span>{t('allLines')}</span>
                  <div className="route-filter-map-list">
                  <button
                    className={!selectedRouteId ? 'is-active' : ''}
                    onClick={() => handleRouteSelection('')}
                    type="button"
                  >
                    {t('allLines')}
                  </button>
                  {availableRouteOptions.map((route) => (
                    <span className="route-filter-map-item" key={route.key}>
                      <button
                        className={selectedRouteOption?.key === route.key ? 'is-active' : ''}
                        onClick={() => handleRouteSelection(route.id)}
                        style={getRouteBadgeStyle(route.shortName)}
                        title={route.longName || route.shortName}
                        type="button"
                      >
                        {route.shortName}
                      </button>
                      <button
                        aria-label={
                          favoriteRouteSet.has(getRouteFavoriteKey(route))
                            ? t('removeFavoriteLine', { route: route.shortName })
                            : t('addFavoriteLine', { route: route.shortName })
                        }
                        aria-pressed={favoriteRouteSet.has(getRouteFavoriteKey(route))}
                        className={`route-filter-map-item__favorite ${
                          favoriteRouteSet.has(getRouteFavoriteKey(route)) ? 'is-active' : ''
                        }`}
                        onClick={() => toggleFavoriteRoute(route)}
                        title={
                          favoriteRouteSet.has(getRouteFavoriteKey(route))
                            ? t('removeFavoriteLine', { route: route.shortName })
                            : t('addFavoriteLine', { route: route.shortName })
                        }
                        type="button"
                      >
                        {favoriteRouteSet.has(getRouteFavoriteKey(route)) ? '★' : '☆'}
                      </button>
                    </span>
                  ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {hasFocusedMapView ? (
            <div className="map-focus-actions" aria-label={t('mapViewControls')}>
              <button onClick={resetMapFocus} type="button">
                {t('allLines')}
              </button>
              {selectedBus ? (
                <button onClick={clearBusFocus} type="button">
                  {t('lineOnly')}
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="map-container" ref={mapContainerRef} />
          {mapInitErrorMessage ? <div className="map-scrim map-scrim--error">{mapInitErrorMessage}</div> : null}
          {isInitialLoading ? <div className="map-scrim">{t('loadingMapFleet')}</div> : null}
        </div>
      </section>

      <header className="app-hero">
        <div className="app-hero__copy">
          <span className="eyebrow"></span>
          <h1>{transitAppConfig.app?.name || 'TPL  Tracker'}</h1>

          <div className="hero-actions">
            <button className="button button--primary" onClick={() => loadBuses('manual')} type="button">
              {isInitialLoading || isRefreshing ? t('refreshInProgress') : t('refreshNow')}
            </button>
            <button
              className="button button--ghost"
              onClick={() => handleRouteSelection('')}
              type="button"
            >
              {t('showAllLines')}
            </button>
            <PwaInstallButton />
          </div>

          <div className={`status-banner ${errorMessage ? 'is-error' : ''} ${isOffline ? 'is-offline' : ''}`}>
            {errorMessage
              ? errorMessage
              : isOffline
                ? t('offlineBanner')
                : t('lastSyncBanner', {
                  time: formatTime(lastUpdatedAt),
                  seconds: Math.round(REFRESH_INTERVAL_MS / 1000),
                })}
          </div>
          <div className={dataQualityClassName}>
            <span aria-hidden="true" />
            <strong>{dataQualityLabel}</strong>
          </div>
        </div>

        <div className="app-hero__stats">
          <StatBlock
            label={t('visibleVehicles')}
            value={String(visibleBuses.length).padStart(2, '0')}
            detail={t('activeInFeed', { count: buses.length })}
          />
          <StatBlock
            label={t('routesOnMapStat')}
            value={String(visibleRoutesCount).padStart(2, '0')}
            detail={selectedRouteId ? t('routeFilter', { route: selectedRouteId }) : t('fullCoverage')}
          />
          {hasVehicleSpeed ? (
            <StatBlock
              label={t('averageSpeed')}
              value={`${averageSpeed} km/h`}
              detail={t('averageSpeedDetail')}
            />
          ) : (
            <StatBlock
              label={t('gtfsStops')}
              value={String(transitCatalog.meta?.stopCount ?? 0)}
              detail={t('staticCatalogAgency')}
            />
          )}
        </div>
      </header>


      <main className="workspace-grid">
{/*
        <aside className="workspace-panel workspace-panel--details">
          <div className="panel-heading">
            <span className="panel-heading__eyebrow">Dettaglio</span>
            <h2>{selectedBus ? `Bus ${selectedBus.label}` : selectedStop ? 'Fermata selezionata' : 'Rete TPL'}</h2>
          </div>

          {selectedBus ? (
            <div className="details-card">
              <div className="details-highlight">
                <span
                  className="details-highlight__route"
                  style={{
                    '--route-color': getRouteColor(selectedBus.routeId, selectedBus.routeShortName),
                    '--route-text-color': getReadableTextColor(getRouteColor(selectedBus.routeId, selectedBus.routeShortName)),
                  }}
                >
                  Linea {selectedBus.routeShortName || selectedBus.routeId}
                </span>
                <strong>{selectedBus.tripHeadsign || getDirectionLabel(selectedBus.directionId)}</strong>
                {selectedBus.routeLongName ? <span>{selectedBus.routeLongName}</span> : null}
                <span>Ultimo rilevamento alle {formatTimestamp(selectedBus.timestamp)}</span>
                <span>
                  {selectedBus.nextStopName
                    ? `Prossima fermata ${selectedBus.nextStopName} alle ${formatPredictedTime(
                        selectedBus.nextStopPredictedTime,
                        selectedBus.nextStopScheduledTime
                      )}`
                    : selectedBus.movement.status}
                </span>
              </div>

              <dl className="details-grid">
                <div>
                  <dt>Veicolo</dt>
                  <dd>{selectedBus.label}</dd>
                </div>
                <div>
                  <dt>Velocita</dt>
                  <dd>{selectedBus.speedKmh} km/h</dd>
                </div>
                <div>
                  <dt>Capolinea</dt>
                  <dd>{selectedBus.tripHeadsign || 'n/d'}</dd>
                </div>
                <div>
                  <dt>Linea</dt>
                  <dd>{selectedBus.routeLongName || `Linea ${selectedBus.routeShortName || selectedBus.routeId}`}</dd>
                </div>
                <div>
                  <dt>Movimento stimato</dt>
                  <dd>{selectedBus.movement.status}</dd>
                </div>
                <div>
                  <dt>Spostamento</dt>
                  <dd>{selectedBus.movement.detail}</dd>
                </div>
                <div>
                  <dt>Partenza corsa</dt>
                  <dd>{selectedBus.startTime}</dd>
                </div>
                <div>
                  <dt>Data servizio</dt>
                  <dd>{formatServiceDate(selectedBus.startDate)}</dd>
                </div>
                <div>
                  <dt>Fermata attuale</dt>
                  <dd>{selectedBus.stopName || selectedBus.stopId}</dd>
                </div>
                <div>
                  <dt>Prossima fermata</dt>
                  <dd>{selectedBus.nextStopName || 'n/d'}</dd>
                </div>
                <div>
                  <dt>Orario previsto</dt>
                  <dd>{formatPredictedTime(selectedBus.nextStopPredictedTime, selectedBus.nextStopScheduledTime)}</dd>
                </div>
                <div>
                  <dt>Scostamento</dt>
                  <dd>
                    <span className={getDelayToneClassName(selectedBus.delaySeconds)}>
                      {formatDelay(selectedBus.delaySeconds)}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Origine corsa</dt>
                  <dd>{selectedBus.startStopName || 'n/d'}</dd>
                </div>
                <div>
                  <dt>Destinazione corsa</dt>
                  <dd>{selectedBus.endStopName || 'n/d'}</dd>
                </div>
                <div>
                  <dt>Direzione geografica</dt>
                  <dd>{selectedBus.movement.direction}</dd>
                </div>
                <div>
                  <dt>Coordinate</dt>
                  <dd>
                    {selectedBus.latitude.toFixed(5)}, {selectedBus.longitude.toFixed(5)}
                  </dd>
                </div>
              </dl>

              {selectedStop ? (
                <div className="details-stop-card">
                  <span className="panel-heading__eyebrow">Fermata selezionata</span>
                  <strong>{selectedStop.name}</strong>
                  <span>
                    Fermata {selectedStop.id}
                    {selectedStop.routeShortNames?.length
                      ? ` · linee ${selectedStop.routeShortNames.slice(0, 8).join(', ')}`
                      : ''}
                  </span>
                  {renderStopPassageRows(6)}

                  {userLocation ? (
                    <button
                      className="button button--ghost"
                      onClick={() => loadJourneyPathRef.current(selectedStop)}
                      type="button"
                    >
                      {isJourneyLoading ? 'Traccio percorso...' : 'Percorso da qui'}
                    </button>
                  ) : (
                    <span>Attiva la tua posizione per calcolare il percorso con i dati GTFS.</span>
                  )}

                  {journeyDisplayOption?.summary ? (
                    <span>
                      Percorso suggerito: {formatDurationMinutes(journeyDisplayOption.summary.durationSeconds)} ·{' '}
                      {journeyDisplayOption.summary.transfers} cambi
                    </span>
                  ) : null}
                </div>
              ) : null}

              <button className="button button--secondary" onClick={() => centerBus(selectedBus.id)} type="button">
                Centra sulla mappa
              </button>
            </div>
          ) : selectedStop ? (
            <div className="details-card">
              <div className="details-stop-card">
                <span className="panel-heading__eyebrow">Fermata TPL</span>
                <strong>{selectedStop.name}</strong>
                <span>
                  Fermata {selectedStop.id}
                  {selectedStop.routeShortNames?.length
                    ? ` · linee ${selectedStop.routeShortNames.slice(0, 8).join(', ')}`
                    : ''}
                </span>
                {renderStopPassageRows(8)}

                {userLocation ? (
                  <button
                    className="button button--ghost"
                    onClick={() => loadJourneyPathRef.current(selectedStop)}
                    type="button"
                  >
                    {isJourneyLoading ? 'Traccio percorso...' : 'Percorso da qui'}
                  </button>
                ) : (
                  <span>Attiva la tua posizione per calcolare il percorso con i dati GTFS.</span>
                )}
              </div>
            </div>
          ) : (
            <div className="empty-state empty-state--details">
              <strong>Nessun bus selezionato</strong>
              <span>Clicca una fermata o un marker bus per aprire orari e dettagli.</span>
            </div>
          )}
        </aside>
*/}

{/*
        <aside className="workspace-panel workspace-panel--filters">
          <div className="panel-heading">
            <span className="panel-heading__eyebrow">Filtri</span>
            <h2>{operatorRoutePanelTitle}</h2>
          </div>

          <label className="search-field">
            <span>Cerca una linea</span>
            <input
              onChange={(event) => setRouteQuery(event.target.value)}
              placeholder="Es. 5, 11, 23"
              type="text"
              value={routeQuery}
            />
          </label>

          <div className="catalog-summary">
            <span>{availableRouteOptions.length} linee visualizzate</span>
            <span>{transitCatalog.routes.length || routeOptions.length} linee GTFS</span>
            <span>{transitCatalog.stops.length} fermate</span>
          </div>

          <div className="route-filter-tabs" aria-label="Categoria linee">
            {[
              ['urban-night', 'Urbane + notturne'],
              ['urban', 'Urbane'],
              ['night', 'Notturne'],
              ['interurban', 'Extraurbane'],
              ['all', 'Tutte'],
            ].map(([filterId, label]) => (
              <button
                className={routeListFilter === filterId ? 'is-active' : ''}
                key={filterId}
                onClick={() => setRouteListFilter(filterId)}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>

          <div className="route-chip-grid">
            <button
              className={`route-chip ${selectedRouteId === '' ? 'is-active' : ''}`}
              onClick={() => handleRouteSelection('')}
              type="button"
            >
              Tutte
            </button>

            {availableRouteOptions.map((routeOption) => {
              const routeColor = getRouteColor(routeOption.id, routeOption.shortName);
              const isActive = selectedRouteOption?.key === routeOption.key;
              const titleParts = [routeOption.longName || routeOption.shortName];

              if (routeOption.routeIds.length > 1) {
                titleParts.push(`${routeOption.routeIds.length} varianti GTFS aggregate`);
              }

              return (
                <button
                  className={`route-chip ${isActive ? 'is-active' : ''}`}
                  key={routeOption.key}
                  onClick={() => handleRouteSelection(routeOption.id)}
                  style={{
                    '--route-color': routeColor,
                    '--route-text-color': getReadableTextColor(routeColor),
                  }}
                  title={titleParts.join(' · ')}
                  type="button"
                >
                  {routeOption.shortName}
                </button>
              );
            })}
          </div>

          <div className="panel-heading panel-heading--compact">
            <span className="panel-heading__eyebrow"></span>
            <h3>Bus in evidenza</h3>
          </div>

          <div className="bus-list" role="list">
            {visibleBuses.length > 0 ? (
              visibleBuses.map((bus) => (
                <BusListItem
                  bus={bus}
                  isActive={selectedBusId === bus.id}
                  key={bus.id}
                  onSelect={() => selectBus(bus.id)}
                />
              ))
            ) : (
              <div className="empty-state">
                <strong>Nessun bus live</strong>
                <span>Prova a rimuovere il filtro o aggiorna il feed.</span>
              </div>
            )}
          </div>
        </aside>
*/}
      </main>

      <footer className="app-footer">
        <div>
          <strong>© {copyrightYear} TPL Tracker</strong>
          <span>
            {t('developedBy', { name: '' }).replace('.', '')}{' '}
            <a href="https://me.gabsam.it/" rel="noreferrer" target="_blank">
              Gabriele Sammartino
            </a>
            .
          </span>
          <span>
            {activeLanguage === 'en'
              ? 'Data source: routes, stops, schedules and realtime data provided by '
              : 'Origine dati: linee, fermate, orari e realtime forniti da '}
            {dataSource.url ? (
              <a href={dataSource.url} rel="noreferrer" target="_blank">
                {dataSource.name}
              </a>
            ) : (
              dataSource.name
            )}
            .
          </span>
          <DataPolicyDisclosure policy={dataSource.policy} />
        </div>
        <span>{t('privacyNotice')}</span>
      </footer>

    </div>
  );
}

const LANDING_OPERATORS = [
  {
    id: 'tep',
    name: 'TEP Parma',
    shortName: 'TEP',
    description: 'Mappa realtime del trasporto pubblico locale di Parma e provincia.',
    href: 'https://tep.tpl-tracker.app/',
    logo: '/operator-logos/tep.svg',
    color: '#ff5a1f',
    city: 'Parma',
  },
  {
    id: 'gtt',
    name: 'GTT Torino',
    shortName: 'GTT',
    description: 'Bus, tram e metropolitana nell’area di Torino e Piemonte nord-ovest.',
    href: 'https://gtt.tpl-tracker.app/',
    logo: '/operator-logos/gtt.png',
    color: '#0057b8',
    city: 'Torino',
  },
  {
    id: 'tper',
    name: 'TPER Bologna e Ferrara',
    shortName: 'TPER',
    description: 'Rete urbana ed extraurbana per Bologna, Ferrara e servizi collegati.',
    href: 'https://tper.tpl-tracker.app/',
    logo: '/operator-logos/tper.png',
    color: '#e92b2d',
    city: 'Bologna',
  },
  {
    id: 'atac',
    name: 'ATAC Roma',
    shortName: 'ATAC',
    description: 'Fermate, orari e passaggi realtime della rete di Roma.',
    href: 'https://atac.tpl-tracker.app/',
    logo: '/operator-logos/atac.svg',
    color: '#b40018',
    city: 'Roma',
  },
  {
    id: 'att',
    name: 'Autolinee Toscane',
    shortName: 'AT',
    description: 'Trasporto pubblico su gomma in Toscana, città e area regionale.',
    href: 'https://att.tpl-tracker.app/',
    logo: '/operator-logos/att.png',
    color: '#07946c',
    city: 'Firenze',
  },
  {
    id: 'amt-genova',
    name: 'AMT Genova',
    shortName: 'AMT',
    description: 'Rete del trasporto pubblico locale di Genova e area metropolitana.',
    href: 'https://amt-genova.tpl-tracker.app/',
    logo: '/operator-logos/amt.png',
    color: '#0d5db8',
    city: 'Genova',
  },
  {
    id: 'nl',
    name: 'OVapi Nederland',
    shortName: 'NL',
    description: 'Mezzi, fermate e orari realtime della rete nazionale olandese.',
    href: 'https://nl.tpl-tracker.app/',
    logo: '/app-icons/icon-192.png',
    color: '#005eb8',
    city: 'Nederland',
  },
];

const PWA_OPERATOR_METADATA = {
  'tep.tpl-tracker.app': {
    manifest: '/manifests/tep.webmanifest',
    icon: '/app-icons/apple-touch-icon.png',
    name: 'TEP TPL Tracker',
    title: 'TEP TPL Tracker | Bus e mezzi pubblici in tempo reale',
  },
  'gtt.tpl-tracker.app': {
    manifest: '/manifests/gtt.webmanifest',
    icon: '/app-icons/apple-touch-icon.png',
    name: 'GTT TPL Tracker',
    title: 'GTT TPL Tracker | Bus e mezzi pubblici in tempo reale',
  },
  'tper.tpl-tracker.app': {
    manifest: '/manifests/tper.webmanifest',
    icon: '/app-icons/apple-touch-icon.png',
    name: 'TPER TPL Tracker',
    title: 'TPER TPL Tracker | Bus e mezzi pubblici in tempo reale',
  },
  'atac.tpl-tracker.app': {
    manifest: '/manifests/atac.webmanifest',
    icon: '/app-icons/apple-touch-icon.png',
    name: 'ATAC TPL Tracker',
    title: 'ATAC TPL Tracker | Bus e mezzi pubblici in tempo reale',
  },
  'att.tpl-tracker.app': {
    manifest: '/manifests/att.webmanifest',
    icon: '/app-icons/apple-touch-icon.png',
    name: 'AT TPL Tracker',
    title: 'AT TPL Tracker | Bus e mezzi pubblici in tempo reale',
  },
  'amt-genova.tpl-tracker.app': {
    manifest: '/manifests/amt-genova.webmanifest',
    icon: '/app-icons/apple-touch-icon.png',
    name: 'AMT TPL Tracker',
    title: 'AMT TPL Tracker | Bus e mezzi pubblici in tempo reale',
  },
  'nl.tpl-tracker.app': {
    manifest: '/manifests/nl.webmanifest',
    icon: '/app-icons/apple-touch-icon.png',
    name: 'NL TPL Tracker',
    title: 'NL TPL Tracker | Mezzi pubblici in tempo reale',
  },
  'tep.gabsam.it': {
    manifest: '/manifests/tep.webmanifest',
    icon: '/app-icons/apple-touch-icon.png',
    name: 'TEP TPL Tracker',
    title: 'TEP TPL Tracker | Bus e mezzi pubblici in tempo reale',
  },
  'gtt.gabsam.it': {
    manifest: '/manifests/gtt.webmanifest',
    icon: '/app-icons/apple-touch-icon.png',
    name: 'GTT TPL Tracker',
    title: 'GTT TPL Tracker | Bus e mezzi pubblici in tempo reale',
  },
};

function setMetaContent(name, content) {
  if (typeof document === 'undefined' || !content) {
    return;
  }

  const selector = `meta[name="${name}"]`;
  let metaElement = document.querySelector(selector);

  if (!metaElement) {
    metaElement = document.createElement('meta');
    metaElement.setAttribute('name', name);
    document.head.appendChild(metaElement);
  }

  metaElement.setAttribute('content', content);
}

function setPropertyMetaContent(property, content) {
  if (typeof document === 'undefined' || !content) {
    return;
  }

  const selector = `meta[property="${property}"]`;
  const metaElement = document.querySelector(selector);

  if (metaElement) {
    metaElement.setAttribute('content', content);
  }
}

function setHeadLink(rel, href) {
  if (typeof document === 'undefined' || !href) {
    return;
  }

  let linkElement = document.querySelector(`link[rel="${rel}"]`);

  if (!linkElement) {
    linkElement = document.createElement('link');
    linkElement.setAttribute('rel', rel);
    document.head.appendChild(linkElement);
  }

  linkElement.setAttribute('href', href);
}

function applyPwaMetadata() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const metadata = PWA_OPERATOR_METADATA[window.location.hostname];

  if (!metadata) {
    return;
  }

  document.title = metadata.title;
  setMetaContent('application-name', metadata.name);
  setMetaContent('apple-mobile-web-app-title', metadata.name);
  setPropertyMetaContent('og:title', metadata.title);
  setMetaContent('twitter:title', metadata.title);
  setHeadLink('apple-touch-icon', `${metadata.icon}?v=20260602`);
  setHeadLink('manifest', metadata.manifest);
}

function PwaInstallButton({ className = 'button button--install' }) {
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [installMessage, setInstallMessage] = useState('');
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsPwaInstalled(isStandalone);

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPromptEvent(event);
      setInstallMessage('');
    };

    const handleAppInstalled = () => {
      setInstallPromptEvent(null);
      setIsPwaInstalled(true);
      setInstallMessage('App installata sul dispositivo.');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const installPwa = async () => {
    if (isPwaInstalled) {
      setInstallMessage('Il collegamento PWA è già disponibile su questo dispositivo.');
      return;
    }

    if (!installPromptEvent) {
      setInstallMessage('Su questo browser usa il menu del dispositivo per aggiungere TPL Tracker alla schermata Home.');
      return;
    }

    installPromptEvent.prompt();
    const choice = await installPromptEvent.userChoice;
    setInstallPromptEvent(null);

    if (choice?.outcome === 'accepted') {
      setIsPwaInstalled(true);
      setInstallMessage('Collegamento PWA creato.');
    }
  };

  return (
    <span className="pwa-install-action">
      <button className={className} onClick={installPwa} type="button">
        <img alt="" src="/app-icons/icon-192.png" />
        {isPwaInstalled ? 'App installata' : 'Installa app'}
      </button>
      {installMessage ? (
        <span className="pwa-install-action__message" role="status">
          {installMessage}
        </span>
      ) : null}
    </span>
  );
}

function LandingRouteLine({ color }) {
  return (
    <svg aria-hidden="true" className="landing-route-line" focusable="false" viewBox="0 0 260 36">
      <path
        d="M2 18H62C72 18 72 27 82 27H138C148 27 148 9 158 9H258"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="4"
        style={{ color }}
      />
      <circle cx="52" cy="18" fill="#fff" r="5" stroke="currentColor" strokeWidth="3" style={{ color }} />
      <circle cx="122" cy="27" fill="#fff" r="5" stroke="currentColor" strokeWidth="3" style={{ color }} />
      <circle cx="214" cy="9" fill="#fff" r="5" stroke="currentColor" strokeWidth="3" style={{ color }} />
    </svg>
  );
}

function LandingMapGraphic() {
  return (
    <div className="landing-map" aria-hidden="true">
      <div className="landing-map__grid" />
      <svg className="landing-map__routes" focusable="false" viewBox="0 0 760 500">
        <path d="M-20 140H150C185 140 185 220 220 220H430C470 220 470 85 510 85H780" />
        <path d="M120 -20V120C120 175 215 175 215 230V520" />
        <path d="M-10 330H160C205 330 205 275 250 275H420C455 275 455 365 490 365H780" />
        <path d="M390 -20V120C390 168 330 168 330 216V520" />
        <path d="M590 -20V150C590 205 660 205 660 260V520" />
      </svg>
      <span className="landing-map__label landing-map__label--torino">Torino</span>
      <span className="landing-map__label landing-map__label--parma">Parma</span>
      <span className="landing-map__label landing-map__label--bologna">Bologna</span>
      <span className="landing-map__label landing-map__label--roma">Roma</span>
      <span className="landing-map__label landing-map__label--genova">Genova</span>
      <span className="landing-map__node landing-map__node--one" />
      <span className="landing-map__node landing-map__node--two" />
      <span className="landing-map__node landing-map__node--three" />
      <span className="landing-map__node landing-map__node--four" />
    </div>
  );
}

function LandingPage() {
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [installMessage, setInstallMessage] = useState('');
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);
  const copyrightYear = new Date().getFullYear();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsPwaInstalled(isStandalone);

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPromptEvent(event);
      setInstallMessage('');
    };

    const handleAppInstalled = () => {
      setInstallPromptEvent(null);
      setIsPwaInstalled(true);
      setInstallMessage('App installata sul dispositivo.');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const installPwa = async () => {
    if (isPwaInstalled) {
      setInstallMessage('Il collegamento PWA è già disponibile su questo dispositivo.');
      return;
    }

    if (!installPromptEvent) {
      setInstallMessage('Su questo browser usa il menu del dispositivo per aggiungere TPL Tracker alla schermata Home.');
      return;
    }

    installPromptEvent.prompt();
    const choice = await installPromptEvent.userChoice;
    setInstallPromptEvent(null);

    if (choice?.outcome === 'accepted') {
      setIsPwaInstalled(true);
      setInstallMessage('Collegamento PWA creato.');
    }
  };

  return (
    <div className="landing-shell">
      <header className="landing-header">
        <a className="landing-brand" href="/">
          TPL Tracker
        </a>
        <nav aria-label="Navigazione principale" className="landing-nav">
          <a href="#reti">Reti</a>
          <button className="landing-nav-install" onClick={installPwa} type="button">
            <img alt="" src="/app-icons/icon-192.png" />
            {isPwaInstalled ? 'Installata' : 'Installa'}
          </button>
          <a href="/admin">Admin</a>
        </nav>
      </header>

      <main>
        <section className="landing-hero">
          <div className="landing-hero__copy">
            <h1>TPL Tracker</h1>
            <p>Scegli la rete e apri la mappa realtime del trasporto pubblico locale.</p>
            <div className="landing-actions">
            {/*  <a className="landing-button landing-button--primary" href="https://tep.tpl-tracker.app/">
                <MapIconButtonVisual />
                Apri TEP Parma
              </a>*/}
              <a className="landing-button landing-button--secondary" href="#reti">
                <ListIconVisual />
                Vedi tutte le reti
              </a>
              <button className="landing-button landing-button--install" onClick={installPwa} type="button">
                <img alt="" src="/app-icons/icon-192.png" />
                {isPwaInstalled ? 'App installata' : 'Installa app'}
              </button>
            </div>
            {installMessage ? (
              <p className="landing-install-message" role="status">
                {installMessage}
              </p>
            ) : null}
          </div>
          <LandingMapGraphic />
        </section>

        <section className="landing-networks" id="reti">
          <div className="landing-section-heading">
            <h2>Reti disponibili</h2>
            <p>Ogni sottodominio apre direttamente la mappa dell’operatore, con fermate, linee e dati disponibili.</p>
          </div>
          <div className="landing-network-grid">
            {LANDING_OPERATORS.map((operator) => (
              <a className="landing-network-card" href={operator.href} key={operator.id}>
                <div className="landing-network-card__top">
                  <span className="landing-network-card__mark" style={{ '--operator-color': operator.color }}>
                    <img alt="" src={operator.logo} />
                  </span>
                  <div>
                    <h3>{operator.name}</h3>
                    <p>{operator.description}</p>
                  </div>
                  <span className="landing-network-card__arrow" aria-hidden="true">
                    <ArrowIconVisual />
                  </span>
                </div>
                <LandingRouteLine color={operator.color} />
                <span className="landing-network-card__city">{operator.city}</span>
              </a>
            ))}
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <span>© {copyrightYear} TPL Tracker</span>
        <span>
          by{' '}
          <a href="https://me.gabsam.it/" rel="noreferrer" target="_blank">
            Gabriele Sammartino
          </a>
        </span>
      </footer>
    </div>
  );
}

function ArrowIconVisual() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}

function ListIconVisual() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24">
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h13" />
      <path d="M3 6h.01" />
      <path d="M3 12h.01" />
      <path d="M3 18h.01" />
    </svg>
  );
}

function App() {
  useEffect(() => {
    applyPwaMetadata();
  }, []);

  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const isLandingPage =
    typeof window !== 'undefined' &&
    (['tpl-tracker.app', 'www.tpl-tracker.app'].includes(window.location.hostname) ||
      searchParams?.get('landing') === '1') &&
    window.location.pathname.replace(/\/+$/, '') !== '/admin' &&
    searchParams?.get('admin') !== '1';
  const isAdminPage =
    typeof window !== 'undefined' &&
    (window.location.pathname.replace(/\/+$/, '') === '/admin' ||
      searchParams?.get('admin') === '1');
  return isLandingPage ? <LandingPage /> : isAdminPage ? <AdminConfigPanel /> : <TrackerApp />;
}

export default App;
